import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, BookOpen, Box, ChevronsDown, ChevronsUp, Compass, Copy, Download, LayoutGrid, MessageCircle, Save, ScanEye, Share2, Shield, Sparkles, User, X } from 'lucide-react';
import {
  calculatePanApi,
  chatApiStream,
  fetchUserRecordCollection,
  getFeatureChatConfig,
  getFourHarmRelief,
  optimizeFeaturePromptAsAdmin,
  saveFeaturePromptAsAdmin,
  saveUserRecordCollection,
} from '../lib/api';
import { ChartAiFollowupItem, buildChartAiFollowupMessage, buildChartAiKnowledgeQuery, buildChartAiUserMessage } from '../lib/chart-ai-shared';
import { buildChartJiaValueFuHint, formatChartLeadStemLabel, formatChartOpenLeadLine } from '../lib/chart-ai-prompt-helpers';
import { stemsData } from '../data/stems';
import { doorsData } from '../data/doors';
import { starsData } from '../data/stars';
import { godsData } from '../data/gods';
import { stemCombinationsData } from '../data/stemCombinations';
import { PALACE_DISEASE_COMBO_MAP } from '../data/palaceDiseaseCombos.generated';
import {
  AI_FOLLOWUP_PROGRESS_DURATION_MS,
  AI_FOLLOWUP_ESTIMATE_DEFAULT_SECONDS,
  AI_FOLLOWUP_PROGRESS_STEPS,
  AI_INITIAL_ESTIMATE_DEFAULT_SECONDS,
  AI_PROGRESS_DURATION_MS,
  AI_PROGRESS_MAX_WAIT_PERCENT,
  AI_PROGRESS_STEPS,
  AI_PROGRESS_TICK_MS,
  PROGRESS_STEPS,
  TEACHER_WECHAT_ID,
} from './pan/pan-page-constants';
import {
  getPanRecordNoteCount,
  getPanRecordNotes,
  MAX_PAN_RECORDS,
  PAN_MODE_ONBOARDING_KEY,
  clearPanSessionSnapshot,
  consumePanSessionRestoreOnce,
  deserializePanResult,
  loadGeoLocation,
  loadPanRecords,
  loadPanSession,
  loadPanSettings,
  mergePanRecordsById,
  markPanSessionRestoreOnce,
  parseDateTimeLocal,
  saveGeoLocation,
  savePanRecords,
  savePanSession,
  savePanSettings,
  serializePanResult,
  sortPanRecordsByRecency,
  toDateTimeLocalValue,
} from './pan/pan-persistence';
import type {
  ConsultMode,
  GeoLocationInfo,
  PalaceCell,
  PanNoteItem,
  PanMode,
  PanResult,
  PanResultSource,
  PanSessionSnapshot,
  PanSettings,
  SavedPanRecord,
  SerializedPanResult,
  TrueSolarAdjustInfo,
  UiMode,
  XiangYiEntry,
} from './pan/pan-types';
import { SymbolData, UserProfile } from '../types';
import TeacherContactModal from './TeacherContactModal';

interface PanTestPageProps {
  userProfile?: UserProfile | null;
  memberActive?: boolean;
  fourXiangyiVisible?: boolean;
  dockStyle?: 'default' | 'ios';
  preferArchiveBack?: boolean;
  pendingRecordId?: string | null;
  onPendingRecordConsumed?: (recordId: string) => void;
  onBack?: () => void;
  onReturnToWence?: () => void;
  onNavigateTab?: (tab: 'pan' | 'practice' | 'study' | 'mine') => void;
  onOpenTool?: (tool: 'decoder' | 'findObject', queryParams: URLSearchParams) => void;
  onRequireAuth?: () => void;
  onRequireProAccess?: () => void;
  /** 象意分类：full=保留二级分类，simple=简化特定子分类 */
  xiangyiCategoryMode?: 'full' | 'simple';
}

const CORE_ORIGIN_REGEX = /^核心原点\s*([0-9一二三四五六七八九十①②③④⑤⑥⑦⑧⑨⑩]+)\s*[：:]\s*(.+)$/;
const ORIGIN_INDEX_MAP: Record<string, number> = {
  '①': 1,
  '②': 2,
  '③': 3,
  '④': 4,
  '⑤': 5,
  '⑥': 6,
  '⑦': 7,
  '⑧': 8,
  '⑨': 9,
  '⑩': 10,
  '一': 1,
  '二': 2,
  '三': 3,
  '四': 4,
  '五': 5,
  '六': 6,
  '七': 7,
  '八': 8,
  '九': 9,
  '十': 10,
};

const parseOriginIndex = (raw: string): number => {
  const text = raw.trim();
  if (!text) return Number.MAX_SAFE_INTEGER;
  const asNumber = Number(text);
  if (Number.isFinite(asNumber)) return asNumber;
  return ORIGIN_INDEX_MAP[text] ?? Number.MAX_SAFE_INTEGER;
};

const sortPanNoteItemsByRecent = (notes: PanNoteItem[]): PanNoteItem[] => (
  [...notes].sort((a, b) => (
    Number(b.updatedAt || 0) - Number(a.updatedAt || 0)
    || Number(b.createdAt || 0) - Number(a.createdAt || 0)
    || String(b.id || '').localeCompare(String(a.id || ''), 'zh-CN')
  ))
);

const formatPanNoteTimestamp = (raw: number): string => {
  const ts = Number(raw || 0);
  if (!Number.isFinite(ts) || ts <= 0) return '--';
  return new Date(ts).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const sortXiangYiDetails = (details: string[]): string[] => {
  const uniqueLines: string[] = [];
  const lineSeen = new Set<string>();
  details
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      if (lineSeen.has(line)) return;
      lineSeen.add(line);
      uniqueLines.push(line);
    });

  type CoreGroup = { index: number; order: number; titleLine: string; detailLines: string[] };
  const groups: CoreGroup[] = [];
  const looseLines: string[] = [];
  let currentGroup: CoreGroup | null = null;

  uniqueLines.forEach((line, order) => {
    const matched = line.match(CORE_ORIGIN_REGEX);
    if (matched) {
      currentGroup = {
        index: parseOriginIndex(matched[1]),
        order,
        titleLine: line,
        detailLines: [],
      };
      groups.push(currentGroup);
      return;
    }
    if (currentGroup) {
      currentGroup.detailLines.push(line);
      return;
    }
    looseLines.push(line);
  });

  if (groups.length === 0) return uniqueLines;

  groups.sort((a, b) => (a.index === b.index ? a.order - b.order : a.index - b.index));

  const result: string[] = [...looseLines];
  groups.forEach((group) => {
    result.push(group.titleLine);
    result.push(...group.detailLines);
  });
  return result;
};

const GRID_POSITIONS = [4, 9, 2, 3, 5, 7, 8, 1, 6];

const OUTER_POSITIONS: Array<{ position: number; side: 'top' | 'left' | 'right' | 'bottom'; style: React.CSSProperties }> = [
  { position: 9, side: 'top', style: { left: '50%', top: '-5.2%', transform: 'translate(-50%, -50%)' } },
  { position: 4, side: 'left', style: { left: '0', top: '16.67%', transform: 'translate(calc(-100% - 8px), -50%)' } },
  { position: 3, side: 'left', style: { left: '0', top: '50%', transform: 'translate(calc(-100% - 8px), -50%)' } },
  { position: 8, side: 'left', style: { left: '0', top: '83.33%', transform: 'translate(calc(-100% - 8px), -50%)' } },
  { position: 2, side: 'right', style: { right: '0', top: '16.67%', transform: 'translate(calc(100% + 8px), -50%)' } },
  { position: 7, side: 'right', style: { right: '0', top: '50%', transform: 'translate(calc(100% + 8px), -50%)' } },
  { position: 6, side: 'right', style: { right: '0', top: '83.33%', transform: 'translate(calc(100% + 8px), -50%)' } },
  { position: 1, side: 'bottom', style: { left: '50%', bottom: '-5.2%', transform: 'translate(-50%, 50%)' } },
];

const HORSE_CORNER_POSITIONS: Record<number, React.CSSProperties> = {
  4: { left: '0', top: '-3.1%', transform: 'translate(calc(-100% - 8px), -50%)' },
  2: { right: '0', top: '-3.1%', transform: 'translate(calc(100% + 8px), -50%)' },
  8: { left: '0', bottom: '-3.1%', transform: 'translate(calc(-100% - 8px), 50%)' },
  6: { right: '0', bottom: '-3.1%', transform: 'translate(calc(100% + 8px), 50%)' },
};

const PAN_EXTENSION_PRIMARY_BUTTONS = ['移星换斗', '天门地户', '长生状态'] as const;
const PAN_EXTENSION_SECONDARY_BUTTONS = ['年神将', '月神将', '日神将', '时神将'] as const;

const POSITION_TO_TRIGRAM: Record<number, string> = {
  1: '坎',
  2: '坤',
  3: '震',
  4: '巽',
  5: '中',
  6: '乾',
  7: '兑',
  8: '艮',
  9: '离',
};

const DEITY_SYMBOL_MAP: Record<string, string> = {
  值符: '符',
  腾蛇: '蛇',
  螣蛇: '蛇',
  太阴: '阴',
  六合: '六',
  白虎: '白',
  玄武: '玄',
  九地: '地',
  九天: '天',
};

const STAR_SYMBOL_MAP: Record<string, string> = {
  天蓬: '蓬',
  天任: '任',
  天冲: '冲',
  天辅: '辅',
  天英: '英',
  天芮: '芮',
  天柱: '柱',
  天心: '心',
  天禽: '禽',
};

const GATE_SEQUENCE = ['休门', '生门', '伤门', '杜门', '景门', '死门', '惊门', '开门'];
const STAR_SEQUENCE = ['天蓬', '天任', '天冲', '天辅', '天英', '天芮', '天柱', '天心'];
const DEITY_SEQUENCE = ['值符', '腾蛇', '太阴', '六合', '白虎', '玄武', '九地', '九天'];
const SANQI_LIUYI = ['戊', '己', '庚', '辛', '壬', '癸', '丁', '丙', '乙'];
const STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
const BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];

const STEM_SET = new Set(['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸']);
const BRANCH_SET = new Set(BRANCHES);
const LIUYI_SET = new Set(['戊', '己', '庚', '辛', '壬', '癸']);
const BRANCH_OPPOSITE: Record<string, string> = {
  子: '午',
  丑: '未',
  寅: '申',
  卯: '酉',
  辰: '戌',
  巳: '亥',
  午: '子',
  未: '丑',
  申: '寅',
  酉: '卯',
  戌: '辰',
  亥: '巳',
};

const PALACE_CLOCKWISE = [2, 7, 6, 1, 8, 3, 4, 9];
const PALACE_COUNTER_CLOCKWISE = [2, 9, 4, 3, 8, 1, 6, 7];

const BRANCH_NUMBER: Record<string, number> = {
  子: 1,
  丑: 2,
  寅: 3,
  卯: 4,
  辰: 5,
  巳: 6,
  午: 7,
  未: 8,
  申: 9,
  酉: 10,
  戌: 11,
  亥: 12,
};

const PALACE_BRANCHES: Record<number, string[]> = {
  1: ['子'],
  2: ['未', '申'],
  3: ['卯'],
  4: ['辰', '巳'],
  5: [],
  6: ['戌', '亥'],
  7: ['酉'],
  8: ['丑', '寅'],
  9: ['午'],
};

const XUN_SHOU_VOIDNESS: Record<string, [string, string]> = {
  甲子: ['戌', '亥'],
  甲戌: ['申', '酉'],
  甲申: ['午', '未'],
  甲午: ['辰', '巳'],
  甲辰: ['寅', '卯'],
  甲寅: ['子', '丑'],
};

const LIUJIA_XUN: Record<string, string> = {
  甲子: '戊',
  甲戌: '己',
  甲申: '庚',
  甲午: '辛',
  甲辰: '壬',
  甲寅: '癸',
};

const ORIGINAL_STAR_BY_POSITION: Record<number, string> = {
  1: '天蓬',
  2: '天芮',
  3: '天冲',
  4: '天辅',
  5: '天禽',
  6: '天心',
  7: '天柱',
  8: '天任',
  9: '天英',
};

const ORIGINAL_GATE_BY_POSITION: Record<number, string> = {
  1: '休门',
  2: '死门',
  3: '伤门',
  4: '杜门',
  5: '无门',
  6: '开门',
  7: '惊门',
  8: '生门',
  9: '景门',
};

const PALACE_FIVE_ELEMENTS: Record<string, string> = {
  坎: '水',
  坤: '土',
  震: '木',
  巽: '木',
  中: '土',
  乾: '金',
  兑: '金',
  艮: '土',
  离: '火',
};

const GATE_FIVE_ELEMENTS: Record<string, string> = {
  休门: '水',
  生门: '土',
  伤门: '木',
  杜门: '木',
  景门: '火',
  死门: '土',
  惊门: '金',
  开门: '金',
};

const OVERCOME: Record<string, string> = {
  木: '土',
  土: '水',
  水: '火',
  火: '金',
  金: '木',
};

const STEM_TOMB_BRANCHES: Record<string, string[]> = {
  甲: ['未'],
  乙: ['戌'],
  丙: ['戌'],
  丁: ['丑'],
  戊: ['戌'],
  己: ['丑'],
  庚: ['丑'],
  辛: ['辰'],
  壬: ['辰'],
  癸: ['未'],
};

const LIUYI_JIXING: Array<{ stem: string; palace: number }> = [
  { stem: '戊', palace: 3 },
  { stem: '己', palace: 2 },
  { stem: '庚', palace: 8 },
  { stem: '辛', palace: 9 },
  { stem: '壬', palace: 4 },
  { stem: '癸', palace: 4 },
];

const YANG_DUN_SOLAR_TERMS = new Set([
  '冬至',
  '小寒',
  '大寒',
  '立春',
  '雨水',
  '惊蛰',
  '春分',
  '清明',
  '谷雨',
  '立夏',
  '小满',
  '芒种',
]);

const JIAZI = [
  '甲子', '乙丑', '丙寅', '丁卯', '戊辰', '己巳', '庚午', '辛未', '壬申', '癸酉',
  '甲戌', '乙亥', '丙子', '丁丑', '戊寅', '己卯', '庚辰', '辛巳', '壬午', '癸未',
  '甲申', '乙酉', '丙戌', '丁亥', '戊子', '己丑', '庚寅', '辛卯', '壬辰', '癸巳',
  '甲午', '乙未', '丙申', '丁酉', '戊戌', '己亥', '庚子', '辛丑', '壬寅', '癸卯',
  '甲辰', '乙巳', '丙午', '丁未', '戊申', '己酉', '庚戌', '辛亥', '壬子', '癸丑',
  '甲寅', '乙卯', '丙辰', '丁巳', '戊午', '己未', '庚申', '辛酉', '壬戌', '癸亥',
];

const TIME_BRANCH_START_HOUR: Record<string, number> = {
  子: 23,
  丑: 1,
  寅: 3,
  卯: 5,
  辰: 7,
  巳: 9,
  午: 11,
  未: 13,
  申: 15,
  酉: 17,
  戌: 19,
  亥: 21,
};

const FIVE_ZI_START_STEM_BY_HOUR_STEM: Record<string, string> = {
  甲: '甲',
  己: '甲',
  乙: '丙',
  庚: '丙',
  丙: '戊',
  辛: '戊',
  丁: '庚',
  壬: '庚',
  戊: '壬',
  癸: '壬',
};

const KE_OPTIONS = [
  { value: 0, label: '第1刻 (00分)' },
  { value: 1, label: '第2刻 (10分)' },
  { value: 2, label: '第3刻 (20分)' },
  { value: 3, label: '第4刻 (30分)' },
  { value: 4, label: '第5刻 (40分)' },
  { value: 5, label: '第6刻 (50分)' },
];

const DOOR_SYMBOL_TO_NAME: Record<string, string> = {
  休: '休门',
  生: '生门',
  伤: '伤门',
  杜: '杜门',
  景: '景门',
  死: '死门',
  惊: '惊门',
  开: '开门',
};

const STAR_SYMBOL_TO_NAME = Object.entries(STAR_SYMBOL_MAP).reduce((acc, [name, symbol]) => {
  acc[symbol] = name;
  return acc;
}, {} as Record<string, string>);

const DEITY_SYMBOL_TO_NAME: Record<string, string> = {
  符: '值符',
  蛇: '腾蛇',
  阴: '太阴',
  六: '六合',
  白: '白虎',
  玄: '玄武',
  地: '九地',
  天: '九天',
};

const DEITY_SYMBOL_TO_DATA_CHAR: Record<string, string> = {
  符: '符',
  蛇: '蛇',
  阴: '阴',
  六: '合',
  白: '虎',
  玄: '武',
  地: '地',
  天: '天',
};

const FLATTEN_INTERMEDIATE_LABELS = new Set([
  '金属与矿石',
  '身体形态',
  '身体与形态',
  '植物',
  '阻碍',
  '执行者',
  '冲突场所',
  '能与品质',
  '能力与品质',
  '拥有者',
]);

const shouldFlattenIntermediateLabel = (label: string): boolean => {
  const normalized = label.replace(/\s+/g, '').trim();
  return Boolean(normalized) && FLATTEN_INTERMEDIATE_LABELS.has(normalized);
};

const splitNestedCategoryContent = (content: string): string[] => {
  const text = content.trim();
  if (!text) return [];

  const matched: string[] = [];
  const pairRegex = /([^：:；。]{1,14})[：:]\s*([^：:]+?)(?=(?:[。；;]|$))/g;
  let match: RegExpExecArray | null = pairRegex.exec(text);
  while (match) {
    const label = (match[1] || '').trim();
    const value = (match[2] || '').trim();
    if (label && value) {
      matched.push(`${label}：${value.replace(/[。；;]+$/g, '').trim()}`);
    }
    match = pairRegex.exec(text);
  }

  return matched.length > 0 ? matched : [text];
};

const COMBO_DETAIL_LABEL_RE = /^([^：:]{1,14})[：:]\s*(.+)$/;

// 组合象意去重：同一标签（如联想/外景/内景）只保留信息更完整的一条。
const dedupeComboDetailLines = (lines: string[]): string[] => {
  const normalized = lines.map((line) => line.trim()).filter(Boolean);
  if (normalized.length <= 1) return normalized;

  type IndexedLine = { line: string; index: number };
  type LabeledBest = IndexedLine & { score: number };

  const unlabeled: IndexedLine[] = [];
  const seenUnlabeled = new Set<string>();
  const labeledBest = new Map<string, LabeledBest>();

  normalized.forEach((line, index) => {
    const matched = line.match(COMBO_DETAIL_LABEL_RE);
    if (!matched) {
      if (seenUnlabeled.has(line)) return;
      seenUnlabeled.add(line);
      unlabeled.push({ line, index });
      return;
    }

    const label = matched[1].trim();
    const value = matched[2].trim();
    const score = value.length;
    const current = labeledBest.get(label);
    if (!current || score > current.score || (score === current.score && index > current.index)) {
      labeledBest.set(label, { line, index, score });
    }
  });

  return [
    ...unlabeled,
    ...Array.from(labeledBest.values()).map(({ line, index }) => ({ line, index })),
  ]
    .sort((a, b) => a.index - b.index)
    .map((item) => item.line);
};

const SYMBOL_DATA_BY_KEY = (() => {
  const map = new Map<string, SymbolData>();
  stemsData.forEach((item) => map.set(`stem:${item.char}`, item));
  doorsData.forEach((item) => map.set(`door:${item.char}`, item));
  starsData.forEach((item) => map.set(`star:${item.char}`, item));
  godsData.forEach((item) => map.set(`god:${item.char}`, item));
  return map;
})();

const STEM_COMBO_DETAILS = (() => {
  const map = new Map<string, string[]>();
  for (const group of stemCombinationsData) {
    for (const origin of group.coreOrigins) {
      const key = origin.title.replace(/\s+/g, '');
      if (!/^[甲乙丙丁戊己庚辛壬癸]\+[甲乙丙丁戊己庚辛壬癸]$/.test(key)) continue;
      const detailLines: string[] = [];
      if (origin.logic?.trim()) detailLines.push(origin.logic.trim());
      for (const sub of origin.subItems) {
        const label = (sub.label || '').trim();
        const content = (sub.content || '').trim();
        if (!label && !content) continue;
        detailLines.push(label ? `${label}：${content}` : content);
      }
      if (detailLines.length > 0) {
        map.set(key, detailLines);
      }
    }
  }
  return map;
})();

const normalizeComboKey = (stemA: string, stemB: string): string => `${stemA}+${stemB}`;

/** 去掉“外景（银行、炼钢厂）”类示例括号，保留“疾病（落乾宫）”类结构性括号 */
const normalizeXiangyiBrackets = (text: string): string => {
  const structural = /[宫局门星值符使]/;
  return text
    .replace(/（([^）]+)）/g, (_, inner) => (structural.test(inner) ? `（${inner}）` : ` ${inner}`))
    .replace(/\(([^)]+)\)/g, (_, inner) => (structural.test(inner) ? `(${inner})` : ` ${inner}`));
};

const getPalaceDiseaseComboLine = (palace: string, comboKey: string): string => {
  const palaceKey = String(palace || '').trim();
  if (!palaceKey || !PALACE_DISEASE_COMBO_MAP[palaceKey]) return '';
  const meaning = PALACE_DISEASE_COMBO_MAP[palaceKey][comboKey];
  if (!meaning) return '';
  return `疾病：（落${palaceKey}宫）${meaning}`;
};

const getDayOfYear = (date: Date): number => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  return Math.floor(diff / 86400000);
};

const calcTrueSolarAdjust = (clockDate: Date, location: GeoLocationInfo): TrueSolarAdjustInfo => {
  const dayIndex = getDayOfYear(clockDate);
  const b = (2 * Math.PI * (dayIndex - 81)) / 364;
  const equationMinutes = 9.87 * Math.sin(2 * b) - 7.53 * Math.cos(b) - 1.5 * Math.sin(b);
  const timezoneOffsetHours = -clockDate.getTimezoneOffset() / 60;
  const timezoneMeridian = timezoneOffsetHours * 15;
  const longitudeCorrectionMinutes = 4 * (location.lng - timezoneMeridian);
  const deltaMinutes = longitudeCorrectionMinutes + equationMinutes;

  const adjustedDate = new Date(clockDate.getTime() + deltaMinutes * 60000);
  return {
    deltaMinutes,
    equationMinutes,
    longitudeCorrectionMinutes,
    timezoneMeridian,
    sourceDateText: formatDateCn(clockDate),
    adjustedDateText: formatDateCn(adjustedDate),
    location,
  };
};

const buildSymbolDetailLines = (symbol?: SymbolData, mode: 'full' | 'simple' = 'simple'): string[] => {
  if (!symbol) return [];
  const lines: string[] = [];
  for (const origin of symbol.coreOrigins) {
    const originTitle = (origin.title || '').trim();
    const titleMatch = originTitle.match(CORE_ORIGIN_REGEX);
    if (titleMatch) {
      lines.push(`核心原点 ${titleMatch[1]}：${titleMatch[2]}`);
    } else if (originTitle) {
      lines.push(originTitle);
    }
    if (titleMatch) {
      if (mode === 'full') {
        for (const sub of origin.subItems) {
          const label = (sub.label || '').trim();
          const content = (sub.content || '').trim();
          if (!content) continue;
          lines.push(label ? `${label}：${content}` : content);
        }
      } else {
        const mergedSubContents: string[] = [];
        for (const sub of origin.subItems) {
          const content = (sub.content || '').trim();
          if (!content) continue;
          mergedSubContents.push(content);
        }
        if (mergedSubContents.length) lines.push(mergedSubContents.join(' '));
      }
    } else {
      for (const sub of origin.subItems) {
        const label = (sub.label || '').trim();
        const content = (sub.content || '').trim();
        if (!content) continue;
        if (shouldFlattenIntermediateLabel(label)) {
          lines.push(...splitNestedCategoryContent(content));
          continue;
        }
        lines.push(label ? `${label}：${content}` : content);
      }
    }
  }
  return Array.from(new Set(lines));
};

const normalize = (value: unknown, fallback = '-'): string => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
};

const formatDateCn = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}年${m}月${d}日 ${h}时${mm}分`;
};

const normalizeGateName = (gate: unknown): string => {
  const text = normalize(gate, '').replaceAll('門', '门').trim();
  if (!text) return '';
  if (GATE_SEQUENCE.includes(text)) return text;
  const first = text.slice(0, 1);
  if ('休生伤杜景死惊开'.includes(first)) return `${first}门`;
  return '';
};

const toGateSymbol = (gate: unknown): string => {
  const text = normalizeGateName(gate);
  return text ? text.replace('门', '') : '-';
};

const toStarSymbol = (star: unknown): string => {
  const text = normalize(star, '-');
  if (text === '-') return text;
  return STAR_SYMBOL_MAP[text] || text.replace(/^天/, '').slice(0, 1) || '-';
};

const toDeitySymbol = (deity: unknown): string => {
  const text = normalize(deity, '-');
  return DEITY_SYMBOL_MAP[text] || text.replaceAll('九', '').replaceAll('值', '').slice(0, 1) || text;
};

const splitStems = (text: string): string[] => text.split('').filter((ch) => STEM_SET.has(ch));
const pickBranchFromPillar = (pillarText: string): string => {
  const chars = String(pillarText || '').trim().split('');
  const branch = chars.find((ch) => BRANCH_SET.has(ch));
  return branch || '';
};

const isStemInTomb = (stemText: string, palaceBranches: string[]): boolean => {
  const stems = splitStems(stemText);
  return stems.some((stem) => {
    const tombBranches = STEM_TOMB_BRANCHES[stem] || [];
    return palaceBranches.some((b) => tombBranches.includes(b));
  });
};

const hasJiXingByStem = (position: number, stemText: string): boolean => {
  const stems = splitStems(stemText);
  return LIUYI_JIXING.some((cond) => cond.palace === position && stems.includes(cond.stem));
};

interface FourHarmTruthState {
  trueVoidPalaces: number[];
  trueTombPalaces: number[];
  voidTriggeredPalaces: number[];
  tombTriggeredPalaces: number[];
  tombTargetCount: number;
  reverseJudgeTriggered: boolean;
}

const getFourGangPillarsByPan = (pan: PanResult): string[] => (
  pan.mode === 'ke'
    ? [pan.monthPillar, pan.dayPillar, pan.hourPillar, pan.kePillar || '-']
    : [pan.yearPillar, pan.monthPillar, pan.dayPillar, pan.hourPillar]
);

const branchHitCountWithChong = (branch: string, fourGangBranches: string[]): number => {
  const opposite = BRANCH_OPPOSITE[branch] || '';
  return fourGangBranches.filter((item) => item === branch || (opposite && item === opposite)).length;
};

const getPalacesByBranch = (branch: string): number[] => (
  normalizePalacePositions(
    Object.entries(PALACE_BRANCHES)
      .filter(([, branches]) => Array.isArray(branches) && branches.includes(branch))
      .map(([position]) => Number(position)),
  )
);

const buildFourHarmTruthState = (
  pan: PanResult,
  options?: { resolveForceTombStem?: (cell: PalaceCell) => string | null },
): FourHarmTruthState => {
  const fourGangBranches = getFourGangPillarsByPan(pan)
    .map((pillar) => pickBranchFromPillar(pillar))
    .filter(Boolean);
  const voidBranches = Array.from(new Set(String(pan.kongText || '').split('').filter((branch) => BRANCH_SET.has(branch))));
  const fakeVoidSet = new Set(
    voidBranches.filter((branch) => branchHitCountWithChong(branch, fourGangBranches) >= 2),
  );

  const voidTriggeredPalacesRaw: number[] = [];
  const tombTriggeredPalacesRaw: number[] = [];
  const tombTargets: Array<{ palace: number; branch: string; side: '天盘' | '地盘'; stem: string }> = [];
  const tombTargetSeen = new Set<string>();

  for (let position = 1; position <= 9; position += 1) {
    if (position === 5) continue;
    const cell = pan.palaces.get(position);
    if (!cell) continue;

    if (cell.hasVoid) voidTriggeredPalacesRaw.push(position);

    const palaceBranches = PALACE_BRANCHES[position] || [];
    const forceTombStem = options?.resolveForceTombStem?.(cell) || null;
    const heavenlyStems = splitStems(cell.heavenlyStem);
    const earthlyStems = splitStems(cell.earthlyStem);
    const heavenlyInTomb = cell.heavenlyInTomb === true || (Boolean(forceTombStem) && heavenlyStems.includes(String(forceTombStem)));
    const earthlyInTomb = cell.earthlyInTomb === true || (Boolean(forceTombStem) && earthlyStems.includes(String(forceTombStem)));
    if (cell.hasTomb || heavenlyInTomb || earthlyInTomb) {
      tombTriggeredPalacesRaw.push(position);
    }

    const appendTombTargets = (stems: string[], side: '天盘' | '地盘') => {
      stems.forEach((stem) => {
        const tombBranches = STEM_TOMB_BRANCHES[stem] || [];
        tombBranches
          .filter((branch) => palaceBranches.includes(branch))
          .forEach((branch) => {
            const key = `${position}:${side}:${stem}:${branch}`;
            if (tombTargetSeen.has(key)) return;
            tombTargetSeen.add(key);
            tombTargets.push({ palace: position, branch, side, stem });
          });
      });
    };

    if (heavenlyInTomb) appendTombTargets(heavenlyStems, '天盘');
    if (earthlyInTomb) appendTombTargets(earthlyStems, '地盘');
  }

  const voidTriggeredPalaces = normalizePalacePositions(voidTriggeredPalacesRaw);
  const trueVoidPalaces = normalizePalacePositions(
    voidTriggeredPalaces.filter((position) => {
      const relatedBranches = (PALACE_BRANCHES[position] || []).filter((branch) => voidBranches.includes(branch));
      if (relatedBranches.length === 0) return true;
      return relatedBranches.some((branch) => !fakeVoidSet.has(branch));
    }),
  );

  const trueTombPalaces = normalizePalacePositions(
    tombTargets
      .filter((item) => branchHitCountWithChong(item.branch, fourGangBranches) < 2)
      .map((item) => item.palace),
  );
  const tombTriggeredPalaces = normalizePalacePositions(tombTriggeredPalacesRaw);
  const fourGangPalaces = normalizePalacePositions(
    fourGangBranches.flatMap((branch) => getPalacesByBranch(branch)),
  );

  const hasTrueHarmByPalace = (position: number): boolean => {
    const cell = pan.palaces.get(position);
    if (!cell) return false;
    const hasTrueXing = Boolean(
      cell.hasXing
      || cell.heavenlyHasXing
      || cell.earthlyHasXing
      || cell.earthlyHasXingByFuYin,
    );
    const hasTrueMenPo = cell.hasMenPo === true;
    const hasTombFlag = tombTriggeredPalaces.includes(position);
    const hasTrueTomb = hasTombFlag && (tombTargets.length ? trueTombPalaces.includes(position) : true);
    const hasTrueVoid = voidTriggeredPalaces.includes(position) && trueVoidPalaces.includes(position);
    return hasTrueXing || hasTrueMenPo || hasTrueTomb || hasTrueVoid;
  };

  return {
    trueVoidPalaces,
    trueTombPalaces,
    voidTriggeredPalaces,
    tombTriggeredPalaces,
    tombTargetCount: tombTargets.length,
    reverseJudgeTriggered: Boolean(fourGangPalaces.length && fourGangPalaces.every((position) => hasTrueHarmByPalace(position))),
  };
};

const emptyPalace = (position: number): PalaceCell => ({
  position,
  trigram: POSITION_TO_TRIGRAM[position] || '-',
  deitySymbol: '',
  gateSymbol: '',
  starSymbol: '',
  heavenlyStem: '',
  earthlyStem: '',
  leadStem: '',
  hasVoid: false,
  hasTomb: false,
  hasXing: false,
  heavenlyHasXing: false,
  earthlyHasXing: false,
  earthlyHasXingByFuYin: false,
  hasMenPo: false,
  heavenlyInTomb: false,
  earthlyInTomb: false,
  isHorse: false,
  isZhiFu: false,
  isZhiShi: false,
});
// 排盘核心算法已迁移到后端 /pan/calculate，前端仅保留渲染与状态显示所需方法。

const getPillarItems = (pan: PanResult): Array<{ label: string; value: string }> => {
  if (pan.mode === 'ke') {
    return [
      { label: '月柱', value: pan.monthPillar },
      { label: '日柱', value: pan.dayPillar },
      { label: '时柱', value: pan.hourPillar },
      { label: '刻柱', value: pan.kePillar || '-' },
    ];
  }
  return [
    { label: '年柱', value: pan.yearPillar },
    { label: '月柱', value: pan.monthPillar },
    { label: '日柱', value: pan.dayPillar },
    { label: '时柱', value: pan.hourPillar },
  ];
};

interface BuildPanSummaryOptions {
  remoteMonthAsSubject?: boolean;
}

const NEAR_TURN_TARGET_MAP: Record<number, number> = {
  1: 2,
  2: 4,
  3: 9,
  4: 7,
  6: 8,
  7: 1,
  8: 3,
  9: 6,
};

const FAR_TURN_TARGET_MAP: Record<number, number> = {
  1: 7,
  2: 1,
  3: 8,
  4: 2,
  6: 9,
  7: 4,
  8: 6,
  9: 3,
};

const normalizePalacePositions = (positions: number[]): number[] => Array.from(new Set(
  (Array.isArray(positions) ? positions : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 1 && value <= 9)
    .map((value) => Math.round(value)),
)).sort((a, b) => a - b);

const formatPalaceLabel = (position: number): string => {
  const pos = Math.round(Number(position));
  const trigram = POSITION_TO_TRIGRAM[pos] || '';
  return trigram ? `${trigram}${pos}宫` : `${pos}宫`;
};

const getTurnTargetPosition = (position: number, consultMode: ConsultMode): number => {
  const pos = Math.round(Number(position));
  if (!Number.isFinite(pos)) return 0;
  const map = consultMode === 'face' ? NEAR_TURN_TARGET_MAP : FAR_TURN_TARGET_MAP;
  return map[pos] || 0;
};

const buildPanSummaryForAi = (
  pan: PanResult,
  consultMode: ConsultMode,
  options?: BuildPanSummaryOptions
): string => {
  const remoteMonthAsSubject = options?.remoteMonthAsSubject === true;
  const originalDayStem = splitStems(pan.dayPillar)[0] || pan.dayPillar.slice(0, 1);
  const monthStem = splitStems(pan.monthPillar)[0] || pan.monthPillar.slice(0, 1);
  const originalHourStem = splitStems(pan.hourPillar)[0] || pan.hourPillar.slice(0, 1);
  const keStem = splitStems(pan.kePillar || '')[0] || splitStems(pan.hourPillar)[0] || pan.hourPillar.slice(0, 1);
  const effectiveDayStem = pan.mode === 'ke' ? originalHourStem : originalDayStem;
  const effectiveHourStem = pan.mode === 'ke' ? keStem : originalHourStem;
  const eventStem = effectiveHourStem;
  const subjectStem = pan.mode === 'ke'
    ? (remoteMonthAsSubject ? originalDayStem : effectiveDayStem)
    : (consultMode === 'remote' ? (remoteMonthAsSubject ? monthStem : originalDayStem) : originalDayStem);
  const roleRuleText = pan.mode === 'ke'
    ? (
      remoteMonthAsSubject
        ? `刻盘规则开关=开启：时干${originalHourStem}代日干，刻干${keStem}代时干，原日干${originalDayStem}代月干为主人公`
        : `刻盘规则开关=关闭：时干${originalHourStem}代日干，刻干${keStem}代时干，主人公默认取代日干`
    )
    : (
      consultMode === 'remote'
        ? (
          remoteMonthAsSubject
            ? `规则开关=开启：策划师取日干${originalDayStem}，主人公取月干${monthStem}`
            : `规则开关=关闭：主人公取日干${originalDayStem}，策划师不入局`
        )
        : `当面测：问测人取日干${originalDayStem}`
    );
  const identityAnchorText = pan.mode === 'ke'
    ? (
      remoteMonthAsSubject
        ? `身份锚点：主人公=${subjectStem}（原日干作月干），代日干=${effectiveDayStem}（时干），事项=${eventStem}（刻干作时干）`
        : `身份锚点：主人公=${subjectStem}（时干作日干），事项=${eventStem}（刻干作时干）`
    )
    : (
      consultMode === 'remote'
        ? (
          remoteMonthAsSubject
            ? `身份锚点：策划师=${originalDayStem}（日干），主人公=${monthStem}（月干）`
            : `身份锚点：主人公=${originalDayStem}（日干），策划师=不入局`
        )
        : `身份锚点：问测人=${originalDayStem}（日干）`
    );
  const pillarLine = pan.mode === 'ke'
    ? `四柱：月${pan.monthPillar} 日${pan.dayPillar} 时${pan.hourPillar} 刻${pan.kePillar || '-'}`
    : `四柱：年${pan.yearPillar} 月${pan.monthPillar} 日${pan.dayPillar} 时${pan.hourPillar}`;
  const fourGangPillars = pan.mode === 'ke'
    ? [pan.monthPillar, pan.dayPillar, pan.hourPillar, pan.kePillar || '-']
    : [pan.yearPillar, pan.monthPillar, pan.dayPillar, pan.hourPillar];

  const tianPanStemIndex = new Map<string, number[]>();
  const fuYinRawPalaces: number[] = [];
  for (let pos = 1; pos <= 9; pos += 1) {
    const cell = pan.palaces.get(pos);
    if (!cell || pos === 5) continue;
    const heavenStems = splitStems(cell.heavenlyStem);
    const earthStems = splitStems(cell.earthlyStem);
    heavenStems.forEach((stem) => {
      const list = tianPanStemIndex.get(stem) || [];
      if (!list.includes(pos)) list.push(pos);
      tianPanStemIndex.set(stem, list);
    });
    if (heavenStems.some((stem) => earthStems.includes(stem))) {
      fuYinRawPalaces.push(pos);
    }
  }
  const fuYinPalaces = normalizePalacePositions(fuYinRawPalaces);
  const dayHourSamePalaces = normalizePalacePositions((tianPanStemIndex.get(effectiveDayStem) || [])
    .filter((position) => (tianPanStemIndex.get(effectiveHourStem) || []).includes(position)));
  const dayHourSameLabel = pan.mode === 'ke' ? '代日代时同宫' : '日时同宫';
  const transferPriorityLabel = pan.mode === 'ke' ? '代日代时同宫时优先转出代时干宫' : '日时同宫时优先转出时干宫';
  const turnModeLabel = consultMode === 'face' ? '近空断（后天转先天）' : '远空断（先天转后天）';
  const transferTriggers: string[] = [];
  if (dayHourSamePalaces.length > 0) {
    transferTriggers.push(`${dayHourSameLabel}=${dayHourSamePalaces.map((position) => formatPalaceLabel(position)).join('、')}`);
  }
  if (fuYinPalaces.length > 0) {
    transferTriggers.push(`伏吟=${fuYinPalaces.map((position) => formatPalaceLabel(position)).join('、')}`);
  }
  const dayHourTransferText = dayHourSamePalaces
    .map((position) => {
      const target = getTurnTargetPosition(position, consultMode);
      if (!target) return `${formatPalaceLabel(position)}：待核验转宫目标`;
      return `${formatPalaceLabel(position)}→${formatPalaceLabel(target)}`;
    })
    .join('、');
  const transferJudgeLine = transferTriggers.length > 0
    ? `转宫提醒：默认空亡触发；本局另触发${transferTriggers.join('，')}，按${turnModeLabel}补转宫。${dayHourTransferText ? `${transferPriorityLabel}：${dayHourTransferText}。` : ''}`
    : `转宫提醒：默认主体干宫/事项干宫逢空亡触发；若出现伏吟或${dayHourSameLabel}，也需按${turnModeLabel}转宫扩充信息。`;
  const transformedStemLine = pan.mode === 'ke'
    ? `本题代日干取${effectiveDayStem}（时干代入）；代时干取${effectiveHourStem}（刻干代入）；主人公取${subjectStem}；事项取${eventStem}`
    : `本题日干（主人公）取${subjectStem}；时干（事项）取${eventStem}`;
  const zhiFuPalaceEntry = Array.from(pan.palaces.entries()).find(([position, cell]) => position !== 5 && cell?.isZhiFu);
  const zhiFuPalaceLabel = zhiFuPalaceEntry
    ? `${zhiFuPalaceEntry[1]?.trigram || POSITION_TO_TRIGRAM[zhiFuPalaceEntry[0]]}${zhiFuPalaceEntry[0]}宫`
    : '';
  const jiaValueFuHint = buildChartJiaValueFuHint({
    xunShou: pan.xunShou,
    zhiFu: pan.zhiFu,
    zhiFuPalaceLabel,
    fourGangPillars,
  });
  const headerLines = [
    `事项：${pan.question || '未填写'}`,
    `时间节点：${pan.timeNodeText}`,
    `排盘类型：${pan.mode === 'ke' ? '刻盘' : '时盘'} · ${pan.juText}`,
    `问测方式：${consultMode === 'remote' ? '远程测' : '当面测'}`,
    `旬首：${pan.xunShou}；值符：${pan.zhiFu}；值使：${pan.zhiShi}；马星：${pan.maText}；空亡：${pan.kongText}`,
    jiaValueFuHint,
    pillarLine,
    `用神换算：${roleRuleText}；${identityAnchorText}；${transformedStemLine}${pan.mode === 'ke' ? '（刻盘默认：时干为人，刻干为事）' : ''}`,
    transferJudgeLine,
  ];

  const palaceLines: string[] = [];
  for (let pos = 1; pos <= 9; pos += 1) {
    const cell = pan.palaces.get(pos);
    if (!cell || pos === 5) continue;
    const tags: string[] = [];
    if (cell.hasVoid) tags.push('空亡');
    if (cell.hasMenPo) tags.push('门迫');
    if (cell.heavenlyHasXing || cell.earthlyHasXing) tags.push('击刑');
    if (cell.heavenlyInTomb || cell.earthlyInTomb) tags.push('入墓');
    if (cell.isZhiFu) tags.push('值符宫');
    if (cell.isHorse) tags.push('马星宫');
    palaceLines.push(
      `${cell.trigram}${pos}宫：神${cell.deitySymbol} 星${cell.starSymbol} 门${cell.gateSymbol} 天${cell.heavenlyStem} 地${cell.earthlyStem} ${formatChartLeadStemLabel(cell.leadStem)}${tags.length ? `（${tags.join('、')}）` : ''}`,
    );
  }

  const formatTianPanStemPositions = (stem: string): string => {
    const positions = tianPanStemIndex.get(stem) || [];
    if (positions.length === 0) return '未落宫';
    return positions
      .map((pos) => {
        const cell = pan.palaces.get(pos);
        return `${cell?.trigram || POSITION_TO_TRIGRAM[pos]}${pos}宫`;
      })
      .join('、');
  };
  const tianPanStemLines = Array.from(tianPanStemIndex.entries())
    .sort((a, b) => STEMS.indexOf(a[0]) - STEMS.indexOf(b[0]))
    .map(([stem]) => `${stem}：${formatTianPanStemPositions(stem)}`);
  const tianPanStemSection = [
    '天盘干落宫索引（取用神定宫必须且只能看天盘干，不得用地盘干）：',
    ...(pan.mode === 'ke'
      ? [
        `代日干 ${effectiveDayStem} -> ${formatTianPanStemPositions(effectiveDayStem)}`,
        `代时干 ${effectiveHourStem} -> ${formatTianPanStemPositions(effectiveHourStem)}`,
        `主人公 ${subjectStem} -> ${formatTianPanStemPositions(subjectStem)}`,
        `事项 ${eventStem} -> ${formatTianPanStemPositions(eventStem)}`,
      ]
      : [
        `日干 ${subjectStem} -> ${formatTianPanStemPositions(subjectStem)}`,
        `时干 ${eventStem} -> ${formatTianPanStemPositions(eventStem)}`,
      ]),
    ...(tianPanStemLines.length > 0 ? tianPanStemLines : ['本盘无可用天盘干索引']),
  ].join('\n');

  const fourGangBranches = fourGangPillars.map((pillar) => pickBranchFromPillar(pillar)).filter(Boolean);
  const voidBranches = Array.from(new Set(String(pan.kongText || '').split('').filter((branch) => BRANCH_SET.has(branch))));
  const voidCollisionLines = voidBranches.map((voidBranch) => {
    const oppositeBranch = BRANCH_OPPOSITE[voidBranch] || '';
    const hits = fourGangBranches.filter((branch) => branch === voidBranch || branch === oppositeBranch);
    const isFakeVoid = hits.length >= 2;
    return `${voidBranch}${oppositeBranch ? `↔${oppositeBranch}` : ''}：四纲命中${hits.length}次（${hits.join('、') || '无'}）=> ${isFakeVoid ? '假空亡' : '真空亡'}`;
  });
  const voidCollisionSection = [
    '真假空亡四纲碰撞（硬规则：命中次数>=2才可判假；命中集合=空亡支本字+其对冲支）：',
    `四纲柱：${fourGangPillars.join('、')}`,
    `四纲地支：${fourGangBranches.join('、') || '-'}`,
    ...(voidCollisionLines.length > 0 ? voidCollisionLines : ['本盘未识别到空亡地支，跳过碰撞判定。']),
  ].join('\n');

  // 为“打开引干法”增加结构化索引，减少模型在多宫检索时误配。
  const leadIndex = new Map<string, number[]>();
  for (let pos = 1; pos <= 9; pos += 1) {
    const cell = pan.palaces.get(pos);
    if (!cell || pos === 5) continue;
    splitStems(cell.leadStem).forEach((stem) => {
      const list = leadIndex.get(stem) || [];
      if (!list.includes(pos)) list.push(pos);
      leadIndex.set(stem, list);
    });
  }

  const openLeadLines: string[] = [];
  for (let pos = 1; pos <= 9; pos += 1) {
    const cell = pan.palaces.get(pos);
    if (!cell || pos === 5) continue;
    const palaceBranches = PALACE_BRANCHES[pos] || [];

    const harmedStemTags = new Map<string, Set<string>>();
    const appendTag = (stem: string, tag: '击刑' | '入墓') => {
      const next = harmedStemTags.get(stem) || new Set<string>();
      next.add(tag);
      harmedStemTags.set(stem, next);
    };

    splitStems(cell.heavenlyStem).forEach((stem) => {
      if (hasJiXingByStem(pos, stem)) appendTag(stem, '击刑');
      if (isStemInTomb(stem, palaceBranches)) appendTag(stem, '入墓');
    });

    splitStems(cell.earthlyStem).forEach((stem) => {
      const earthlyHasXing = cell.earthlyHasXingByFuYin || hasJiXingByStem(pos, stem);
      if (earthlyHasXing) appendTag(stem, '击刑');
      if (isStemInTomb(stem, palaceBranches)) appendTag(stem, '入墓');
    });
    if (harmedStemTags.size === 0) continue;

    const sourceLabel = `${cell.trigram}${pos}宫`;
    harmedStemTags.forEach((tagSet, stem) => {
      const harmLabel = ['击刑', '入墓'].filter((tag) => tagSet.has(tag)).join('、') || '四害';
      const targets = (leadIndex.get(stem) || []).filter((targetPos) => targetPos !== 5);
      const targetLabels = targets.map((targetPos) => {
        const target = pan.palaces.get(targetPos);
        if (!target) return `${targetPos}宫`;
        const sameStemNoOpen = splitStems(target.heavenlyStem).includes(stem);
        return `${target.trigram}${targetPos}宫${sameStemNoOpen ? '（同干不打开引干）' : ''}`;
      });
      openLeadLines.push(formatChartOpenLeadLine({
        sourceLabel,
        harmLabel,
        stem,
        targetLabels,
      }));
    });
  }

  const openLeadSection = openLeadLines.length > 0
    ? `打开引干索引（仅击刑/入墓触发）：\n${openLeadLines.join('\n')}`
    : '打开引干索引（仅击刑/入墓触发）：本盘未触发可打开引干的受害干。';

  return `${headerLines.join('\n')}\n${tianPanStemSection}\n${voidCollisionSection}\n${openLeadSection}\n九宫明细：\n${palaceLines.join('\n')}`;
};

const normalizeInlineMarkdown = (text: string): string => String(text || '')
  .replace(/\\([*_])/g, '$1')
  .replace(/＊＊/g, '**')
  .replace(/＿＿/g, '__')
  .replace(/&lt;strong&gt;/gi, '<strong>')
  .replace(/&lt;\/strong&gt;/gi, '</strong>');

const renderInline = (text: string): React.ReactNode[] => {
  const source = normalizeInlineMarkdown(text);
  const tokenRegex = /(==[^=]+==|\*\*[^*]+?\*\*|__[^_]+?__|<strong>[\s\S]+?<\/strong>)/gi;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let nodeIndex = 0;

  while ((match = tokenRegex.exec(source)) !== null) {
    const token = match[0];
    if (match.index > lastIndex) {
      nodes.push(<React.Fragment key={`plain-${nodeIndex}`}>{source.slice(lastIndex, match.index)}</React.Fragment>);
      nodeIndex += 1;
    }

    if (token.startsWith('==') && token.endsWith('==')) {
      nodes.push(<span key={`mark-${nodeIndex}`} className="rounded bg-amber-100 px-1 text-amber-900">{token.slice(2, -2)}</span>);
      nodeIndex += 1;
    } else if (
      (token.startsWith('**') && token.endsWith('**'))
      || (token.startsWith('__') && token.endsWith('__'))
      || /^<strong>[\s\S]*<\/strong>$/i.test(token)
    ) {
      const boldText = token
        .replace(/^\*\*/, '')
        .replace(/\*\*$/, '')
        .replace(/^__/, '')
        .replace(/__$/, '')
        .replace(/^<strong>/i, '')
        .replace(/<\/strong>$/i, '');
      nodes.push(<span key={`bold-${nodeIndex}`} className="font-black text-[#141414]">{boldText}</span>);
      nodeIndex += 1;
    } else {
      nodes.push(<React.Fragment key={`raw-${nodeIndex}`}>{token}</React.Fragment>);
      nodeIndex += 1;
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < source.length) {
    nodes.push(<React.Fragment key={`tail-${nodeIndex}`}>{source.slice(lastIndex)}</React.Fragment>);
  }

  if (nodes.length === 0) return [<React.Fragment key="plain-empty">{source}</React.Fragment>];
  return nodes;
};

const stripInlinePresentationMarkdown = (text: string): string => normalizeInlineMarkdown(text)
  .replace(/==/g, '')
  .replace(/\*\*/g, '')
  .replace(/__/g, '')
  .replace(/(^|[^\*])\*([^*\n]+)\*(?!\*)/g, '$1$2')
  .replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1$2')
  .replace(/<em>/gi, '')
  .replace(/<\/em>/gi, '')
  .replace(/<strong>/gi, '')
  .replace(/<\/strong>/gi, '');

const renderInlinePlain = (text: string): React.ReactNode[] => [
  <React.Fragment key="plain-inline">{stripInlinePresentationMarkdown(text)}</React.Fragment>,
];

const DECOR_TRIM_REGEX = /^[❖◆◇◈✦·•\s]+|[❖◆◇◈✦·•\s]+$/g;
const INTERNAL_SECTION_REGEX = /^(?:#{1,6}\s*)?[【\[]?\s*(?:内部推演区|内部推演|内部思考区)\s*[】\]]?\s*$/i;
const MASTER_SECTION_REGEX = /^(?:#{1,6}\s*)?[【\[]?\s*(?:大师直解|对客输出区|对客输出)\s*[】\]]?\s*$/i;
const CORE_REPORT_HEADING_REGEX = /^(?:[0-9一二三四五六七八九十]+[)）.、]\s*)?(?:结论先行|总判断|核心结论|依据分析|断局主线|象义优先|象意细化|象义细化|应期判断|行动建议|风险预警|环境调整|环境磁场调整|内部推演区|内部推演|大师直解)/;
const MASTER_HEADING_HINT_REGEX = /^(?:结论先行|总判断|核心结论|依据分析|断局主线|象义优先|象意细化|象义细化|应期判断|行动建议|风险预警|环境调整|环境磁场调整)(?:$|[（(：:\s])/u;
const MASTER_HEADING_ORDER: Readonly<Record<string, number>> = Object.freeze({
  结论先行: 1,
  总判断: 1,
  核心结论: 1,
  依据分析: 2,
  断局主线: 2,
  象义优先: 3,
  象意细化: 3,
  象义细化: 3,
  应期判断: 4,
  行动建议: 5,
  风险预警: 6,
  环境调整: 7,
  环境磁场调整: 7,
});
const INTERNAL_HEADING_HINT_REGEX = /^(?:第一段思考|第1段思考|第二段思考|显性与隐性需求分析|显性需求|隐性需求|显隐性需求|问题定性|取用神定宫|取用神|四盖溯源|四盖四害溯源与引干分析|四害溯源与引干分析|四害核验|转宫与引干|转宫|引干|应期框架|检索词|象意落地|反断|反断判定|真假空亡|真假空亡判定|真假入墓|真假入墓判定|入墓|入墓判定|真假入墓空亡判定|真假入墓空亡)/;
const INTERNAL_HEADING_LEVEL_RULES: ReadonlyArray<{ regex: RegExp; level: 2 | 3 | 4 }> = Object.freeze([
  { regex: /^(?:第一段思考|第1段思考|第二段思考|显性与隐性需求分析|取用神定宫|四盖溯源|四盖四害溯源与引干分析|四害溯源与引干分析|应期框架)(?:$|[（(：:\s])/u, level: 2 },
  { regex: /^(?:显性需求|隐性需求|问题定性|四害核验|转宫与引干|转宫|引干|反断判定|真假空亡|真假空亡判定|真假入墓|真假入墓判定|入墓|入墓判定|真假入墓空亡|真假入墓空亡判定|检索词|象意落地)(?:$|[（(：:\s])/u, level: 3 },
]);
const SUPPRESSED_ENV_IMPACT_LINE_REGEX = /^(?:#{1,6}\s*)?(?:[-*]\s*)?(?:[0-9一二三四五六七八九十]+[)）.、]\s*)?(?:环境影响|环境因素)(?:[:：].*)?$/u;
const SUPPRESSED_MICRO_HEADING_REGEX = /^(?:#{1,6}\s*)?(?:[-*]\s*)?(?:[0-9一二三四五六七八九十]+[)）.、]\s*)?(?:(?:\*\*|__)?(?:机会线|阻力线|变量线|过去演化|当前现状|核心痛点|关键矛盾|后续走向|机会|阻力|变量|现状|痛点|短期窗口|中期窗口)(?:\*\*|__)?)\s*(?:[:：].*)?$/u;
const INLINE_MICRO_HEADING_REWRITE_REGEX = /^(?:\s*(?:[-*]\s*)?(?:#{1,6}\s*)?(?:[0-9一二三四五六七八九十]+[)）.、]\s*)?)?(?:\*\*|__)?(?:核心痛点|后续走向|环境影响|环境因素|短期窗口|中期窗口)(?:\*\*|__)?\s*[：:]\s*(.+)$/u;
const STRIP_BULLET_SECTION_HEADING_REGEX = /^[-*]\s*(#{1,6}\s*(?:[0-9一二三四五六七八九十]+[)）.、]\s*)?(?:象意细化|象义细化|应期判断)\b.*)$/u;
const SUPPRESSED_INTERNAL_SUBHEADING_REGEX = /^(?:#{1,6}\s*)?(?:显性与隐性需求分析|显性需求|隐性需求|应期框架|取定用神宫|核心用神定位|真假入墓(?:空亡)?与反断判定)\s*$/u;
const INLINE_INTERNAL_SUBHEADING_REWRITE_REGEX = /^(?:\s*(?:[-*]\s*)?(?:#{1,6}\s*)?(?:[0-9一二三四五六七八九十]+[)）.、]\s*)?)?(?:取定用神宫|核心用神定位)\s*[：:]\s*(.+)$/u;
const THINKING_SECTION_SPLIT_REWRITE_RULES: ReadonlyArray<{ regex: RegExp; heading: string }> = Object.freeze([
  { regex: /^(?:\s*(?:[-*]\s*)?(?:#{1,6}\s*)?)真假入墓(?:空亡)?与反断判定\s*[：:]\s*(.+?)\s*(?:[；;]\s*反断(?:局)?判定\s*[：:]\s*(.+))?$/u, heading: '### 真假入墓' },
  { regex: /^(?:\s*(?:[-*]\s*)?(?:#{1,6}\s*)?)真假入墓空亡判定\s*[：:]\s*(.+)$/u, heading: '### 真假入墓' },
  { regex: /^(?:\s*(?:[-*]\s*)?(?:#{1,6}\s*)?)真假入墓空亡\s*[：:]\s*(.+)$/u, heading: '### 真假入墓' },
  { regex: /^(?:\s*(?:[-*]\s*)?(?:#{1,6}\s*)?)反断(?:局)?判定\s*[：:]\s*(.+)$/u, heading: '### 反断判定' },
]);

type ReportLeadHeading = {
  title: string;
  subtitle: string;
};

const parseReportLeadHeading = (line: string): ReportLeadHeading | null => {
  const raw = String(line || '').trim();
  if (!raw) return null;
  const cleaned = normalizeLooseHeadingSource(raw).replace(DECOR_TRIM_REGEX, '').trim();
  if (!/^[0-9一二三四五六七八九十]+[)）.、]/.test(cleaned)) return null;
  const match = cleaned.match(/^([0-9一二三四五六七八九十]+[)）.、]\s*)([^：:\n]{1,30}?)(?:\s*[：:]\s*(.+))?$/u);
  if (!match) return null;
  const prefix = String(match[1] || '').trim();
  const body = String(match[2] || '').replace(/[：:]\s*$/u, '').trim();
  const subtitle = String(match[3] || '').replace(/^[：:\s]+/, '').trim();
  const looksLikeMasterHeading = MASTER_HEADING_HINT_REGEX.test(body);
  const looksLikeGenericShortHeading = isLikelyShortHeadingTitle(body, 18);
  if (!looksLikeMasterHeading && !looksLikeGenericShortHeading) return null;
  const title = `${prefix} ${body}`.replace(/\s+/g, ' ').trim();
  if (!title) return null;
  return { title, subtitle };
};

type DecorSectionHeading = {
  title: string;
  rest: string;
};

type MarkdownHeadingParsed = {
  level: number;
  text: string;
};

const parseMarkdownHeadingLine = (line: string): MarkdownHeadingParsed | null => {
  const raw = String(line || '').trim();
  if (!raw) return null;
  const m = raw.match(/^(#{1,6})\s*(.+)$/);
  if (!m) return null;
  const text = String(m[2] || '').trim();
  if (!text) return null;
  return {
    level: m[1].length,
    text,
  };
};

const cleanDecorHeadingText = (text: string): string => String(text || '')
  .replace(/[❖◆◇◈✦]/g, '')
  .replace(/\*\*/g, '')
  .replace(/__/g, '')
  .replace(/<\/?strong>/gi, '')
  .replace(/[：:]+$/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeLooseHeadingSource = (text: string): string => String(text || '')
  .replace(/<\/?strong>/gi, '')
  .replace(/\*\*/g, '')
  .replace(/__/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const PALACE_TERM_REGEX = /((?:乾|坎|艮|震|巽|离|坤|兑|中)(?:一|二|三|四|五|六|七|八|九|[1-9])宫)/gu;
const INLINE_EMPHASIS_TOKEN_REGEX = /(\*\*[^*]+?\*\*|__[^_]+?__|<strong>[\s\S]+?<\/strong>)/gi;

const emphasizePalaceTerms = (input: string): string => String(input || '')
  .split(INLINE_EMPHASIS_TOKEN_REGEX)
  .map((chunk, idx) => {
    if (idx % 2 === 1) return chunk;
    return chunk.replace(PALACE_TERM_REGEX, '**$1**');
  })
  .join('');

const toCanonicalSectionHeading = (line: string): string | null => {
  const text = String(line || '').trim();
  if (!text) return null;
  if (INTERNAL_SECTION_REGEX.test(text)) return '# 内部推演';
  if (MASTER_SECTION_REGEX.test(text)) return '# 大师直解';
  return null;
};

const simplifyDisplayHeadingText = (text: string): string => {
  let t = String(text || '')
    .replace(/^\s*[-—–]{1,3}\s*/, '')
    .replace(/[“”"]/g, '')
    .trim();
  t = t.replace(/对客输出区/g, '大师直解');
  t = t.replace(/环境磁场调整/g, '环境调整');

  const coreMatch = t.match(CORE_REPORT_HEADING_REGEX);
  if (coreMatch && coreMatch[0]) {
    t = coreMatch[0].trim();
  } else {
    t = t.replace(/(?:——|--|—|-)\s*关于.+$/u, '').trim();
    if (/[:：]/.test(t)) {
      const parts = t.split(/[:：]/);
      const left = String(parts[0] || '').trim();
      const right = String(parts.slice(1).join('：') || '').trim();
      if (left && right && /(关于|深度|推演|解析|揭开|说明)/u.test(right) && left.length <= 20) {
        t = left;
      }
    }
  }

  return t.trim();
};

const toMasterOrderedHeading = (line: string): string | null => {
  const raw = String(line || '').trim();
  if (!raw) return null;
  const body = raw
    .replace(/^\s*[-*]\s*/, '')
    .replace(/^#{1,6}\s*/, '')
    .replace(/^[0-9一二三四五六七八九十]+[)）.、]?\s*/, '')
    .trim();
  const m = body.match(/^(结论先行|断局主线|象意细化|应期判断|行动建议|风险预警|环境调整|环境磁场调整)(?:[:：].*)?$/);
  if (!m) return null;
  const rawTitle = m[1];
  const title = rawTitle === '环境磁场调整' ? '环境调整' : rawTitle;
  const idx = MASTER_HEADING_ORDER[title] || MASTER_HEADING_ORDER[rawTitle];
  if (!idx) return null;
  return `## ${idx}. ${title}`;
};

type InternalSubheadingParsed = {
  heading: string;
  level: 2 | 3 | 4;
  title: string;
  rest: string;
};

const resolveInternalHeadingLevel = (title: string): 2 | 3 | 4 => {
  for (const rule of INTERNAL_HEADING_LEVEL_RULES) {
    if (rule.regex.test(title)) return rule.level;
  }
  return 3;
};

const normalizeInternalSubheading = (line: string): InternalSubheadingParsed | null => {
  const raw = String(line || '').trim();
  if (!raw) return null;
  const bodyRaw = raw
    .replace(/^\s*[-*]\s*/, '')
    .replace(/^#{1,6}\s*/, '')
    .replace(/^[0-9一二三四五六七八九十]+[)）.、]?\s*/, '')
    .trim();
  const body = bodyRaw.replace(/^[（(【\[]?[A-Za-z0-9一二三四五六七八九十]+[）)】\].、:：-]\s*/u, '').trim();
  if (!body) return null;

  if (/^第[一二三四五六七八九十0-9]+段思考$/.test(body)) {
    return {
      heading: '## 第一段思考',
      level: 2,
      title: '第一段思考',
      rest: '',
    };
  }

  const colonIndex = body.search(/[：:]/u);
  const titleRaw = (colonIndex >= 0 ? body.slice(0, colonIndex) : body).trim();
  const rest = (colonIndex >= 0 ? body.slice(colonIndex + 1) : '').trim();
  const title = titleRaw.replace(/[：:]\s*$/u, '').trim();

  if (!title) return null;
  if (!INTERNAL_HEADING_HINT_REGEX.test(title)) return null;
  if (/[，。！？；,.!?;]/.test(title)) return null;
  if (title.length > 36) return null;

  const level = resolveInternalHeadingLevel(title);
  return {
    heading: `${'#'.repeat(level)} ${title}`,
    level,
    title,
    rest,
  };
};

const isLikelyShortHeadingTitle = (text: string, maxLen = 24): boolean => {
  const t = cleanDecorHeadingText(text);
  if (!t) return false;
  if (t.length < 2 || t.length > maxLen) return false;
  if (/[，。！？；,.!?;]/.test(t)) return false;
  return true;
};

const parseDecorSectionHeading = (line: string): DecorSectionHeading | null => {
  const raw = String(line || '').trim();
  if (!raw) return null;
  if (parseReportLeadHeading(raw)) return null;

  const mBold = raw.match(/^([❖◆◇◈✦]\s*)?([0-9一二三四五六七八九十]+[)）.、]?)?\s*\*\*([^*]+?)\*\*\s*([❖◆◇◈✦])?\s*(.*)$/);
  if (mBold) {
    if (!mBold[1] && !mBold[2]) return null;
    const num = String(mBold[2] || '').trim();
    const titleCore = cleanDecorHeadingText(mBold[3] || '');
    const rest = String(mBold[5] || '').replace(/^[：:\s]+/, '').trim();
    const title = `${num ? `${num} ` : ''}${titleCore}`.trim();
    if (title) return { title, rest };
  }

  const mDecor = raw.match(/^[❖◆◇◈✦]\s*([0-9一二三四五六七八九十]+[)）.、]?)?\s*(.+?)\s*[❖◆◇◈✦]\s*(.*)$/);
  if (mDecor) {
    const num = String(mDecor[1] || '').trim();
    const titleCore = cleanDecorHeadingText(mDecor[2] || '');
    const rest = String(mDecor[3] || '').replace(/^[：:\s]+/, '').trim();
    const title = `${num ? `${num} ` : ''}${titleCore}`.trim();
    if (title) return { title, rest };
  }

  const mStandaloneBold = raw.match(/^(?:\*\*([^*]{2,40})\*\*|__([^_]{2,40})__)\s*(.*)$/);
  if (mStandaloneBold) {
    const titleCore = cleanDecorHeadingText(mStandaloneBold[1] || mStandaloneBold[2] || '');
    const rest = String(mStandaloneBold[3] || '').replace(/^[：:\s]+/, '').trim();
    if (isLikelyShortHeadingTitle(titleCore, 28)) {
      return { title: titleCore, rest };
    }
  }

  const loose = normalizeLooseHeadingSource(raw);
  const colonOnly = loose.match(/^([0-9一二三四五六七八九十]+[)）.、]?)?\s*([^：:]{2,30}?)\s*[：:]\s*$/);
  if (colonOnly) {
    const num = String(colonOnly[1] || '').trim();
    const titleCore = cleanDecorHeadingText(colonOnly[2] || '');
    const maxLen = num ? 18 : 24;
    if (isLikelyShortHeadingTitle(titleCore, maxLen)) {
      const title = `${num ? `${num} ` : ''}${titleCore}`.trim();
      if (title) return { title, rest: '' };
    }
  }

  const hasDecor = /[❖◆◇◈✦]/.test(raw);
  const hasBrokenBold = (raw.match(/\*\*/g) || []).length === 1;
  if (hasDecor || hasBrokenBold) {
    const looseDecor = loose.match(/^([0-9一二三四五六七八九十]+[)）.、]?)?\s*([^：:]{2,30}?)(?:\s*[：:]\s*(.*))?$/);
    if (looseDecor) {
      const num = String(looseDecor[1] || '').trim();
      const titleCore = cleanDecorHeadingText(looseDecor[2] || '');
      const rest = String(looseDecor[3] || '').replace(/^[：:\s]+/, '').trim();
      const maxLen = num ? 18 : 24;
      if (isLikelyShortHeadingTitle(titleCore, maxLen)) {
        const title = `${num ? `${num} ` : ''}${titleCore}`.trim();
        if (title) return { title, rest };
      }
    }
  }

  const numberedLoose = loose.match(/^([0-9一二三四五六七八九十]+[)）.、])\s*([^：:]{2,36})(?:[：:]\s*(.*))?$/);
  if (numberedLoose) {
    const num = String(numberedLoose[1] || '').trim();
    const titleCore = cleanDecorHeadingText(numberedLoose[2] || '');
    const rest = String(numberedLoose[3] || '').replace(/^[：:\s]+/, '').trim();
    if (isLikelyShortHeadingTitle(titleCore, 36)) {
      const title = `${num ? `${num} ` : ''}${titleCore}`.trim();
      if (title) return { title, rest };
    }
  }

  const cnSectionLoose = loose.match(/^([一二三四五六七八九十]+、)\s*([^：:]{2,36})(?:[：:]\s*(.*))?$/);
  if (cnSectionLoose) {
    const num = String(cnSectionLoose[1] || '').trim();
    const titleCore = cleanDecorHeadingText(cnSectionLoose[2] || '');
    const rest = String(cnSectionLoose[3] || '').replace(/^[：:\s]+/, '').trim();
    if (isLikelyShortHeadingTitle(titleCore, 36)) {
      const title = `${num ? `${num} ` : ''}${titleCore}`.trim();
      if (title) return { title, rest };
    }
  }

  return null;
};

const isMarkdownStructuralLine = (line: string): boolean => {
  const t = String(line || '').trim();
  if (!t) return false;
  if (parseMarkdownHeadingLine(t)) return true;
  if (/^>\s?/.test(t)) return true;
  if (/^[-*]\s+/.test(t)) return true;
  if (/^\d+[.)、]\s+/.test(t)) return true;
  if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) return true;
  if (/^第[一二三四五六七八九十0-9]+节/.test(t)) return true;
  if (parseReportLeadHeading(t)) return true;
  if (parseDecorSectionHeading(t)) return true;
  return false;
};

const normalizeAiReportLayoutText = (input: string): string => {
  const cleaned = String(input || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  const rewritten = cleaned
    .split('\n')
    .map((line) => {
      const raw = String(line || '');
      const trimmed = raw.trim();
      if (!trimmed) return raw;
      if (SUPPRESSED_INTERNAL_SUBHEADING_REGEX.test(trimmed)) return '';
      const stripBulletHeading = trimmed.match(STRIP_BULLET_SECTION_HEADING_REGEX);
      if (stripBulletHeading?.[1]) {
        return stripBulletHeading[1];
      }
      const inlineInternalRewrite = trimmed.match(INLINE_INTERNAL_SUBHEADING_REWRITE_REGEX);
      if (inlineInternalRewrite?.[1]) {
        return inlineInternalRewrite[1].trim();
      }
      const inlineShortWindowMatch = trimmed.match(/^(?:\s*(?:[-*]\s*)?(?:#{1,6}\s*)?)?(?:\*\*|__)?(?:短期(?:（[^）]+）|\([^)]+\))?|中期(?:（[^）]+）|\([^)]+\))?)(?:\*\*|__)?\s*[：:]\s*(.+)$/u);
      if (inlineShortWindowMatch?.[1]) {
        return inlineShortWindowMatch[1].trim();
      }
      for (const rule of THINKING_SECTION_SPLIT_REWRITE_RULES) {
        const match = trimmed.match(rule.regex);
        if (match?.[1]) {
          const primary = `${rule.heading}\n${match[1].trim()}`;
          const secondary = typeof match[2] === 'string' && match[2].trim()
            ? `\n### 反断判定\n${match[2].trim()}`
            : '';
          return `${primary}${secondary}`;
        }
      }
      const inlineRewrite = trimmed.match(INLINE_MICRO_HEADING_REWRITE_REGEX);
      if (inlineRewrite?.[1]) {
        return inlineRewrite[1].trim();
      }
      if (SUPPRESSED_ENV_IMPACT_LINE_REGEX.test(trimmed)) return '';
      if (/^(?:#{1,6}\s*)?(?:[-*]\s*)?(?:[0-9一二三四五六七八九十]+[)）.、]\s*)?(?:短期窗口|中期窗口)\s*$/u.test(trimmed)) return '';
      return raw;
    })
    .filter((line, index, arr) => {
      if (line !== '') return true;
      const prev = arr[index - 1];
      const next = arr[index + 1];
      return Boolean(prev && next);
    })
    .join('\n')
    .replace(/真击刑/g, '击刑')
    .replace(/假击刑/g, '击刑')
    .replace(/真门迫/g, '门迫')
    .replace(/假门迫/g, '门迫')
    .replace(/真空亡/g, '空亡')
    .replace(/假空亡/g, '空亡')
    .replace(/真假入墓空亡/g, '真假入墓')
    .replace(/轩辕老师看这盘[，,、：:\s]*/g, '')
    .replace(/轩辕老师[，,、：:\s]*/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return emphasizePalaceTerms(rewritten);
};

const stripBracketExplanationsForDirect = (input: string): string => {
  let text = String(input || '');
  let prev = '';
  while (text !== prev) {
    prev = text;
    text = text
      .replace(/（[^（）]*）/g, '')
      .replace(/\([^()]*\)/g, '');
  }
  return text
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+([，。！？；：])/g, '$1')
    .trim();
};

const extractChatFinishReason = (response: { full?: unknown } | null | undefined): string => {
  const payload = response?.full && typeof response.full === 'object'
    ? response.full as {
        meta?: { finishReason?: unknown };
        choices?: Array<{ finish_reason?: unknown; finishReason?: unknown }>;
      }
    : null;
  const metaFinishReason = String(payload?.meta?.finishReason || '').trim();
  if (metaFinishReason) return metaFinishReason;
  const firstChoice = Array.isArray(payload?.choices) ? payload.choices[0] : null;
  return String(firstChoice?.finish_reason || firstChoice?.finishReason || '').trim();
};

const appendLengthLimitHint = (
  text: string,
  finishReason: string,
  context: 'initial' | 'followup' = 'initial',
): string => {
  const normalized = normalizeAiReportLayoutText(text);
  if (!normalized) return normalized;
  if (String(finishReason || '').trim().toLowerCase() !== 'length') return normalized;
  if (/长度上限|可能被截断|接着上面继续讲完/.test(normalized)) return normalized;
  const hint = context === 'followup'
    ? '（本轮追问回复触发长度上限，内容可能被截断。可以直接追问“接着上面继续讲完”。）'
    : '（本轮回复触发长度上限，内容可能被截断。可以继续追问，让我接着上面讲完。）';
  return `${normalized}\n\n${hint}`.trim();
};

const STREAM_SENTENCE_PUNCTUATION = '。！？!?；;';
const STREAM_MIN_CHARS_WITHOUT_BREAK = 24;

const pickStreamVisibleText = (normalizedText: string): string => {
  const text = String(normalizedText || '');
  if (!text.trim()) return '';
  let lastBreak = -1;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (STREAM_SENTENCE_PUNCTUATION.includes(ch) || ch === '\n') {
      lastBreak = i + 1;
    }
  }
  if (lastBreak > 0) {
    return text.slice(0, lastBreak).trimEnd();
  }
  if (text.length >= STREAM_MIN_CHARS_WITHOUT_BREAK) {
    return text.trimEnd();
  }
  return '';
};

type TheoryLineSegment = {
  type: 'plain' | 'theory';
  text: string;
};

const THEORY_SEGMENT_REGEX = /（[^（）]*）|\([^()]*\)/g;

const splitTheoryLineSegments = (text: string): TheoryLineSegment[] => {
  const source = String(text || '');
  const segments: TheoryLineSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  THEORY_SEGMENT_REGEX.lastIndex = 0;

  while ((m = THEORY_SEGMENT_REGEX.exec(source)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const plain = source.slice(last, start);
    if (plain) segments.push({ type: 'plain', text: plain });
    const theory = m[0];
    if (theory) segments.push({ type: 'theory', text: theory });
    last = end;
  }

  const tail = source.slice(last);
  if (tail) segments.push({ type: 'plain', text: tail });
  return segments;
};

type TheoryInlineToken = {
  text: string;
  isTheory: boolean;
  bold: boolean;
  highlight: boolean;
};

const parseTheoryAwareInlineTokens = (text: string): TheoryInlineToken[] => {
  const source = normalizeInlineMarkdown(text);
  const segments = splitTheoryLineSegments(source);
  const tokens: TheoryInlineToken[] = [];
  let bold = false;
  let highlight = false;

  const pushToken = (chunk: string, isTheory: boolean) => {
    if (!chunk) return;
    tokens.push({
      text: chunk,
      isTheory,
      bold,
      highlight,
    });
  };

  const consumeSegment = (segmentText: string, isTheory: boolean) => {
    let i = 0;
    while (i < segmentText.length) {
      if (segmentText.startsWith('<strong>', i)) {
        bold = true;
        i += 8;
        continue;
      }
      if (segmentText.startsWith('</strong>', i)) {
        bold = false;
        i += 9;
        continue;
      }
      if (segmentText.startsWith('**', i) || segmentText.startsWith('__', i)) {
        bold = !bold;
        i += 2;
        continue;
      }
      if (segmentText.startsWith('==', i)) {
        highlight = !highlight;
        i += 2;
        continue;
      }

      let j = i;
      while (j < segmentText.length) {
        if (
          segmentText.startsWith('<strong>', j)
          || segmentText.startsWith('</strong>', j)
          || segmentText.startsWith('**', j)
          || segmentText.startsWith('__', j)
          || segmentText.startsWith('==', j)
        ) {
          break;
        }
        j += 1;
      }

      pushToken(segmentText.slice(i, j), isTheory);
      i = j;
    }
  };

  segments.forEach((segment) => {
    consumeSegment(segment.text, segment.type === 'theory');
  });

  return tokens;
};

const renderTheoryAwareLine = (
  line: string,
  key: string,
  options?: { plain?: boolean },
): React.ReactNode => {
  if (options?.plain) {
    return (
      <p key={key} className="text-[#2c2c2c] leading-[2.1]">
        {renderInlinePlain(line)}
      </p>
    );
  }
  const tokens = parseTheoryAwareInlineTokens(line);
  const hasTheory = tokens.some((token) => token.isTheory);

  if (!hasTheory) {
    return (
      <p key={key} className="text-[#2c2c2c] leading-[2.1]">
        {renderInline(line)}
      </p>
    );
  }

  return (
    <p key={key} className="text-[#2c2c2c] leading-[2.1]">
      {tokens.map((token, idx) => {
        const classes: string[] = [];
        if (token.highlight) classes.push('rounded bg-amber-100 px-1');
        classes.push(token.isTheory ? 'text-[#8B3A3A]' : 'text-[#2c2c2c]');
        if (token.bold && !token.isTheory) classes.push('font-black');
        const className = classes.join(' ');
        if (!className.trim()) return <React.Fragment key={`${key}-token-${idx}`}>{token.text}</React.Fragment>;
        return (
          <span key={`${key}-token-${idx}`} className={className}>
            {token.text}
          </span>
        );
      })}
    </p>
  );
};

const PanMarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  if (!content.trim()) return null;
  const lines = content.trim().split('\n');
  let activeMasterSection = '';

  const cleanHeadingDisplay = (title: string): string => simplifyDisplayHeadingText(String(title || '')
    .replace(/^[：:\s]+/, '')
    .replace(/[：:\s]+$/, '')
    .trim());

  const resolveMasterSectionTitle = (title: string): string => {
    const cleaned = cleanHeadingDisplay(title)
      .replace(/^[0-9一二三四五六七八九十]+[)）.、]\s*/u, '')
      .trim();
    return /^(结论先行|断局主线|象意细化|应期判断|行动建议|风险预警|环境调整)$/u.test(cleaned)
      ? cleaned
      : '';
  };

  const renderSectionHeader = (title: string, key: string, level: 1 | 2 | 3 | 4): React.ReactNode => {
    const text = cleanHeadingDisplay(title);
    if (!text) return null;
    if (level === 1) {
      return (
        <div key={key} className="mt-12 mb-8 rounded-md border-y border-[#d7c7aa] bg-gradient-to-r from-[#f7efe1] via-[#fcf8f1] to-[#f7efe1] px-2 py-2">
          <h1 className="text-[23px] sm:text-[28px] font-extrabold leading-[1.4] tracking-[0.06em] text-[#5c3a21] text-center [font-family:'FZXiaoBiaoSong-B05S','FZYaSong-B-GBK','STSong','Songti_SC','SimSun',serif]">
            {renderInline(text)}
          </h1>
        </div>
      );
    }
    if (level === 2) {
      return (
        <div key={key} className="mt-10 mb-6 rounded-lg border border-[#dbc9a9] bg-[#f8f0e0] px-3 py-2.5">
          <h2 className="text-[21px] sm:text-[25px] font-extrabold leading-[1.42] tracking-[0.04em] text-[#603818] [font-family:'FZXiaoBiaoSong-B05S','FZYaSong-B-GBK','STSong','Songti_SC','SimSun',serif]">
            {renderInline(text)}
          </h2>
        </div>
      );
    }
    if (level === 3) {
      return (
        <h3 key={key} className="mt-8 mb-4 flex items-center gap-2.5 text-[20px] sm:text-[24px] font-extrabold leading-[1.45] tracking-[0.028em] text-[#6a3f1f] [font-family:'Noto Serif SC','STSong','Songti SC','SimSun',serif]">
          <span className="inline-block h-5 w-[5px] rounded bg-[#8B4513]/80" />
          <span>{renderInline(text)}</span>
        </h3>
      );
    }
    return (
      <h4 key={key} className="mt-4 mb-2 flex items-center gap-1.5 rounded-md border-l-2 border-[#c79b69] bg-[#f8f1e5] px-2 py-1 text-[14px] sm:text-[16px] font-semibold leading-[1.55] tracking-[0.01em] text-[#87603e] [font-family:'Noto Serif SC','STSong','Songti SC','SimSun',serif]">
        <span className="inline-block h-[5px] w-[5px] rounded-full bg-[#b68454]/75" />
        <span>{renderInline(text)}</span>
      </h4>
    );
  };

  const getNumberedHeadingLevel = (title: string): 3 | 4 => {
    const t = cleanHeadingDisplay(title);
    if (/^([0-9一二三四五六七八九十]+[)）.、]|[一二三四五六七八九十]+、)/.test(t)) return 3;
    return 4;
  };

  const renderLeadHeading = (line: string, key: string, masterSection: string): React.ReactNode | null => {
    const heading = parseReportLeadHeading(line);
    if (!heading) return null;
    const isMasterHeading = Boolean(resolveMasterSectionTitle(heading.title));
    const isActionAdviceLead = masterSection === '行动建议' && !isMasterHeading;
    const headingLevel = isActionAdviceLead ? 4 : getNumberedHeadingLevel(heading.title);
    return (
      <div key={key}>
        {renderSectionHeader(heading.title, `${key}-title`, headingLevel)}
        {heading.subtitle && (
          isActionAdviceLead
            ? <div className="mt-1 mb-2">{renderTheoryAwareLine(heading.subtitle, `${key}-subtitle`)}</div>
            : <div className="mt-[-6px] mb-3">{renderTheoryAwareLine(heading.subtitle, `${key}-subtitle`)}</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3 leading-[1.95] tracking-[0.01em] text-[#2c2c2c]">
      {lines.map((line, idx) => {
        const t = line.trim();
        if (!t) return <div key={idx} className="h-2" />;
        const leadHeadingNode = renderLeadHeading(t, `lead-${idx}`, activeMasterSection);
        if (leadHeadingNode) {
          const heading = parseReportLeadHeading(t);
          const nextSection = heading ? resolveMasterSectionTitle(heading.title) : '';
          if (nextSection) activeMasterSection = nextSection;
          return leadHeadingNode;
        }
        const decorHeading = parseDecorSectionHeading(t);
        if (decorHeading) {
          const nextSection = resolveMasterSectionTitle(decorHeading.title);
          if (nextSection) activeMasterSection = nextSection;
          const decorLevel = getNumberedHeadingLevel(decorHeading.title);
          return (
            <React.Fragment key={`decor-${idx}`}>
              {renderSectionHeader(decorHeading.title, `decor-title-${idx}`, decorLevel)}
              {decorHeading.rest
                ? renderTheoryAwareLine(decorHeading.rest, `decor-rest-${idx}`, { plain: activeMasterSection === '环境调整' })
                : null}
            </React.Fragment>
          );
        }
        const plainInternalHeading = normalizeInternalSubheading(t);
        if (plainInternalHeading) {
          return (
            <React.Fragment key={`plain-internal-${idx}`}>
              {renderSectionHeader(plainInternalHeading.title, `plain-internal-title-${idx}`, plainInternalHeading.level)}
              {plainInternalHeading.rest ? renderTheoryAwareLine(plainInternalHeading.rest, `plain-internal-rest-${idx}`) : null}
            </React.Fragment>
          );
        }
        if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) {
          return (
            <div
              key={idx}
              className="my-5 h-px w-full bg-gradient-to-r from-transparent via-[#8B4513]/60 to-transparent"
            />
          );
        }
        if (/^第[一二三四五六七八九十0-9]+节/.test(t)) return renderSectionHeader(t, `sec-${idx}`, 2);
        const markdownHeading = parseMarkdownHeadingLine(t);
        if (markdownHeading) {
          const nextSection = resolveMasterSectionTitle(markdownHeading.text);
          if (nextSection) activeMasterSection = nextSection;
          if (markdownHeading.level === 1) return renderSectionHeader(markdownHeading.text, `sec1-${idx}`, 1);
          if (markdownHeading.level === 2) return renderSectionHeader(markdownHeading.text, `sec2-${idx}`, 2);
          if (markdownHeading.level === 3) return renderSectionHeader(markdownHeading.text, `sec3-${idx}`, 3);
          return renderSectionHeader(markdownHeading.text, `sec4-${idx}`, 4);
        }
        if (t.startsWith('>')) {
          const quote = t.replace(/^>\s?/, '');
          if (activeMasterSection === '环境调整') {
            return renderTheoryAwareLine(quote, `env-quote-${idx}`, { plain: true });
          }
          return (
            <blockquote
              key={idx}
              className="rounded-xl border-l-4 border-[#8B4513]/45 bg-[#f4ede1] px-3 py-2 text-[#5c3a21] text-[15px] sm:text-base shadow-sm"
            >
              {renderInline(quote)}
            </blockquote>
          );
        }
        if (t.startsWith('- ') || t.startsWith('* ')) {
          const bulletBody = t.slice(2).trim();
          const bulletHeading = parseDecorSectionHeading(bulletBody);
          if (bulletHeading) {
            const bulletLevel = getNumberedHeadingLevel(bulletHeading.title);
            return (
              <React.Fragment key={`bullet-heading-${idx}`}>
                {renderSectionHeader(bulletHeading.title, `bullet-heading-title-${idx}`, bulletLevel)}
                {bulletHeading.rest
                  ? renderTheoryAwareLine(bulletHeading.rest, `bullet-heading-rest-${idx}`, { plain: activeMasterSection === '环境调整' })
                  : null}
              </React.Fragment>
            );
          }
          return (
            <div key={idx} className="flex items-start gap-2">
              <span className="mt-[0.85em] h-1 w-1 rounded-full bg-slate-400 shrink-0" />
              <div className="flex-1">{renderTheoryAwareLine(t.slice(2), `line-bullet-${idx}`, { plain: activeMasterSection === '环境调整' })}</div>
            </div>
          );
        }
        if (/^\d+[.)、]\s/.test(t)) {
          const mNum = t.match(/^(\d+)[.)、]\s*(.*)$/);
          const num = mNum ? mNum[1] : '';
          const body = mNum ? mNum[2] : t;
          return (
            <div key={idx} className="flex items-start gap-2">
              <span className="text-slate-500 shrink-0">{num}.</span>
              <div className="flex-1">{renderTheoryAwareLine(body, `line-num-${idx}`, { plain: activeMasterSection === '环境调整' })}</div>
            </div>
          );
        }
        return renderTheoryAwareLine(line, `line-${idx}`, { plain: activeMasterSection === '环境调整' });
      })}
    </div>
  );
};

const stripInternalThinkingForDirect = (input: string): string => {
  let lines = String(input || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');
  const hiddenLineRegex = /内部思考|思考过程|推理过程|推演过程|分析链路|第1段思考|第一段思考|第1步结构化思考|结构化思考结果/i;
  const showStartRegex = /^(?:#{1,6}\s*)?(?:[【\[]?\s*)?(?:大师直解|对客输出区)(?:\s*[】\]])?\s*$/i;
  const fallbackStartRegex = /^(?:#{1,6}\s*)?(?:\d+[)）.、]\s*)?(?:结论先行|总判断|核心结论|依据分析|断局主线|象义优先|象意细化|象义细化|应期判断|行动建议|风险预警|环境调整|环境磁场调整)/;
  const hiddenSectionStartRegex = /^(?:#{1,6}\s*)?(?:[【\[]?\s*)?(?:内部推演区|内部推演|内部思考区)(?:\s*[】\]])?\s*$/i;
  const firstMasterIndex = lines.findIndex((line) => showStartRegex.test(String(line || '').trim()));
  if (firstMasterIndex > 0) {
    lines = lines.slice(firstMasterIndex);
  }

  const kept: string[] = [];
  let hidingBlock = false;

  for (const line of lines) {
    const t = String(line || '').trim();
    if (hiddenSectionStartRegex.test(t)) {
      hidingBlock = true;
      continue;
    }
    if (hidingBlock && (showStartRegex.test(t) || fallbackStartRegex.test(t))) {
      hidingBlock = false;
    }
    if (hidingBlock) continue;
    if (hiddenLineRegex.test(t)) continue;
    kept.push(line);
  }

  return kept
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const PanTestPage: React.FC<PanTestPageProps> = ({
  userProfile,
  memberActive = false,
  fourXiangyiVisible = false,
  dockStyle = 'ios',
  preferArchiveBack = false,
  pendingRecordId = null,
  onPendingRecordConsumed,
  onBack,
  onReturnToWence,
  onNavigateTab,
  onOpenTool,
  onRequireAuth,
  onRequireProAccess,
  xiangyiCategoryMode = 'full',
}) => {
  const initialSettings = useMemo(() => loadPanSettings(), []);
  const restoredSession = useMemo(() => {
    if (!consumePanSessionRestoreOnce()) return null;
    const snapshot = loadPanSession();
    if (!snapshot) return null;
    try {
      const restoredPan = deserializePanResult(snapshot.pan);
      return { snapshot, restoredPan };
    } catch {
      return null;
    }
  }, []);
  const restoredSnapshot = restoredSession?.snapshot || null;
  const launchParams = useMemo(() => {
    if (typeof window === 'undefined') {
      return { fromWence: false, seededQuestion: '' };
    }
    const params = new URLSearchParams(window.location.search);
    const seededQuestion = String(params.get('question') || params.get('q') || '').trim();
    const launchFrom = String(params.get('from') || '').trim();
    return {
      fromWence: launchFrom === 'wence' || launchFrom === 'xiaoyaoWence',
      seededQuestion,
    };
  }, []);

  const [dateTimeValue, setDateTimeValue] = useState(() => (
    restoredSnapshot?.dateTimeValue || toDateTimeLocalValue(new Date())
  ));
  const [panMode, setPanMode] = useState<PanMode>(() => (
    restoredSnapshot?.panMode === 'ke' ? 'ke' : 'hour'
  ));
  const [keIndex, setKeIndex] = useState(() => (
    restoredSnapshot ? Math.max(0, Math.min(5, Number(restoredSnapshot.keIndex) || 0)) : Math.floor(new Date().getMinutes() / 10)
  ));
  const [manualJu, setManualJu] = useState<string>(() => restoredSnapshot?.manualJu || 'auto');
  const [uiMode, setUiMode] = useState<UiMode>(() => (
    restoredSnapshot
      ? (restoredSnapshot.uiMode === 'pro' ? 'pro' : 'simple')
      : initialSettings.defaultMode
  ));
  const [showModeOnboarding, setShowModeOnboarding] = useState(() => {
    try {
      return localStorage.getItem(PAN_MODE_ONBOARDING_KEY) !== '1';
    } catch {
      return false;
    }
  });
  const [showConfig, setShowConfig] = useState(() => (
    restoredSnapshot ? Boolean(restoredSnapshot.showConfig) : true
  ));
  const [question, setQuestion] = useState(() => restoredSnapshot?.question || launchParams.seededQuestion || '');
  const [consultMode, setConsultMode] = useState<ConsultMode>(() => (
    restoredSnapshot?.consultMode === 'face' ? 'face' : 'remote'
  ));
  const [useTrueSolarTime, setUseTrueSolarTime] = useState(() => (
    restoredSnapshot ? Boolean(restoredSnapshot.useTrueSolarTime) : false
  ));
  const [geoLocation, setGeoLocation] = useState<GeoLocationInfo | null>(() => loadGeoLocation());
  const [geoLoading, setGeoLoading] = useState(false);
  const [trueSolarInfo, setTrueSolarInfo] = useState<TrueSolarAdjustInfo | null>(() => (
    restoredSnapshot?.trueSolarInfo || null
  ));
  const [records, setRecords] = useState<SavedPanRecord[]>(() => loadPanRecords());
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedPalacePos, setSelectedPalacePos] = useState<number | null>(null);
  const [expandedXiangYiKeys, setExpandedXiangYiKeys] = useState<string[]>([]);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => (
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  ));
  const [loading, setLoading] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pan, setPan] = useState<PanResult | null>(() => restoredSession?.restoredPan || null);
  const [aiExtraQuestion, setAiExtraQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingMode, setAiLoadingMode] = useState<'initial' | 'followup'>('initial');
  const [aiResponseMode, setAiResponseMode] = useState<'unknown' | 'stream' | 'non_stream'>('non_stream');
  const [aiStreamOutputEnabled, setAiStreamOutputEnabled] = useState<boolean | null>(null);
  const [showAiQuestionModal, setShowAiQuestionModal] = useState(false);
  const [aiProgressPercent, setAiProgressPercent] = useState(0);
  const [aiProgressIndex, setAiProgressIndex] = useState(0);
  const [aiElapsedSeconds, setAiElapsedSeconds] = useState(0);
  const [aiInitialEstimateSeconds, setAiInitialEstimateSeconds] = useState(AI_INITIAL_ESTIMATE_DEFAULT_SECONDS);
  const [aiFollowupEstimateSeconds, setAiFollowupEstimateSeconds] = useState(AI_FOLLOWUP_ESTIMATE_DEFAULT_SECONDS);
  const [aiThinkingOverlayVisible, setAiThinkingOverlayVisible] = useState(false);
  const [aiFirstSentenceSeconds, setAiFirstSentenceSeconds] = useState<number | null>(null);
  const [aiError, setAiError] = useState('');
  const [aiErrorModal, setAiErrorModal] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [aiReport, setAiReport] = useState(() => restoredSnapshot?.aiReport || '');
  const [aiFollowupHistory, setAiFollowupHistory] = useState<ChartAiFollowupItem[]>(() => (
    Array.isArray(restoredSnapshot?.aiFollowupHistory) ? restoredSnapshot.aiFollowupHistory : []
  ));
  const [aiFollowupStreaming, setAiFollowupStreaming] = useState('');
  const [aiExplainMode, setAiExplainMode] = useState<'direct' | 'detail'>(() => (
    restoredSnapshot?.aiExplainMode === 'direct' ? 'direct' : 'detail'
  ));
  const [showAiPanel, setShowAiPanel] = useState(() => (
    Boolean(String(restoredSnapshot?.aiReport || '').trim())
    || (Array.isArray(restoredSnapshot?.aiFollowupHistory) && restoredSnapshot.aiFollowupHistory.length > 0)
  ));
  const [showFollowupComposer, setShowFollowupComposer] = useState(false);
  const [showFourHarmResult, setShowFourHarmResult] = useState(false);
  const [fourHarmLoading, setFourHarmLoading] = useState(false);
  const [fourHarmLines, setFourHarmLines] = useState<string[]>([]);
  const [fourHarmError, setFourHarmError] = useState('');
  const [currentRecordId, setCurrentRecordId] = useState<string | null>(() => restoredSnapshot?.currentRecordId || null);
  const [resultSource, setResultSource] = useState<PanResultSource>(() => (
    restoredSnapshot?.resultSource === 'history' ? 'history' : 'live'
  ));

  // 提示词优化相关状态
  const [promptOptimization, setPromptOptimization] = useState('');
  const [optimizedPrompt, setOptimizedPrompt] = useState('');
  const [optimizationChangeNotes, setOptimizationChangeNotes] = useState<string[]>([]);
  const [optimizationLoading, setOptimizationLoading] = useState(false);
  const [optimizationSaving, setOptimizationSaving] = useState(false);
  const [optimizationError, setOptimizationError] = useState('');
  const [optimizationSaveNotice, setOptimizationSaveNotice] = useState('');
  const [showMasterConsultModal, setShowMasterConsultModal] = useState(false);
  const [showSolutionModal, setShowSolutionModal] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);
  const aiReportRef = useRef<HTMLDivElement | null>(null);
  const aiPanelContainerRef = useRef<HTMLDivElement | null>(null);
  const aiExtraQuestionRef = useRef<HTMLTextAreaElement | null>(null);
  const questionTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const questionInputRef = useRef<HTMLInputElement | null>(null);
  const aiRequestStartAtRef = useRef<number>(0);
  const aiFirstSentenceShownRef = useRef(false);
  const aiFirstSentenceSecondsRef = useRef<number | null>(null);
  const currentRecordIdRef = useRef<string | null>(restoredSnapshot?.currentRecordId || null);
  const recordsRef = useRef<SavedPanRecord[]>(loadPanRecords());
  const cloudRecordsReadyRef = useRef(false);
  const cloudRecordsSyncHashRef = useRef('');
  const isAdminMember = Boolean(userProfile?.isAdminMember || userProfile?.memberType === 'admin');
  const hasAiMemberAccess = Boolean(memberActive || isAdminMember);
  const canShowFourXiangyi = Boolean(fourXiangyiVisible);
  const canUseFourXiangyi = Boolean(canShowFourXiangyi && isAdminMember);
  const ensureAiMemberAccess = (): boolean => {
    if (!String(userProfile?.userId || '').trim()) {
      setAiError('请先登录后再使用 AI 解盘。');
      setShowAiQuestionModal(false);
      onRequireAuth?.();
      return false;
    }
    if (hasAiMemberAccess) return true;
    setAiError('当前账号积分或会员权限不足，请扫码添加若轩老师开通会员权限。');
    setShowAiQuestionModal(false);
    onRequireProAccess?.();
    return false;
  };
  const openMasterConsultModal = () => {
    setShowMasterConsultModal(true);
  };

  const resetAiStreamingPhase = () => {
    aiRequestStartAtRef.current = Date.now();
    aiFirstSentenceShownRef.current = false;
    aiFirstSentenceSecondsRef.current = null;
    setAiFirstSentenceSeconds(null);
    setAiThinkingOverlayVisible(true);
  };

  const markAiFirstSentenceVisible = (text: string) => {
    if (aiFirstSentenceShownRef.current) return;
    if (!String(text || '').trim()) return;
    aiFirstSentenceShownRef.current = true;
    const elapsedSeconds = Math.max(0.1, (Date.now() - aiRequestStartAtRef.current) / 1000);
    const rounded = Number(elapsedSeconds.toFixed(1));
    aiFirstSentenceSecondsRef.current = rounded;
    setAiFirstSentenceSeconds(rounded);
    setAiThinkingOverlayVisible(false);
  };

  const applyPanRecords = (nextRecords: SavedPanRecord[]): SavedPanRecord[] => {
    const normalized = sortPanRecordsByRecency(mergePanRecordsById(nextRecords)).slice(0, MAX_PAN_RECORDS);
    recordsRef.current = normalized;
    setRecords(normalized);
    return normalized;
  };

  const buildPanRecordSnapshot = (
    activePan: PanResult,
    recordQuestion: string,
    overrides?: {
      recordId?: string | null;
      createdAt?: number;
      updatedAt?: number;
      aiReport?: string;
      aiFollowupHistory?: ChartAiFollowupItem[];
      notes?: PanNoteItem[];
      trueSolarInfo?: TrueSolarAdjustInfo | null;
    },
  ): SavedPanRecord => {
    const now = Math.max(1, Number(overrides?.updatedAt || Date.now()));
    const existingId = String(overrides?.recordId || '').trim();
    const createdAt = Math.max(1, Number(overrides?.createdAt || now));
    const notes = sortPanNoteItemsByRecent(
      Array.isArray(overrides?.notes) ? overrides.notes : [],
    );
    return {
      id: existingId || `${createdAt}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt,
      updatedAt: now,
      question: recordQuestion,
      sourceDateTime: dateTimeValue,
      panMode,
      consultMode,
      keIndex,
      manualJu,
      useTrueSolarTime,
      trueSolar: overrides?.trueSolarInfo ?? trueSolarInfo,
      aiReport: String(overrides?.aiReport || ''),
      aiFollowupHistory: Array.isArray(overrides?.aiFollowupHistory) ? overrides.aiFollowupHistory : [],
      note: notes[0]?.content || '',
      notes,
      pan: serializePanResult({ ...activePan, question: recordQuestion }),
    };
  };

  const upsertPanRecord = (
    activePan: PanResult | null,
    options?: {
      question?: string;
      allowUntitled?: boolean;
      aiReport?: string;
      aiFollowupHistory?: ChartAiFollowupItem[];
      notes?: PanNoteItem[];
      recordId?: string | null;
      trueSolarInfo?: TrueSolarAdjustInfo | null;
    },
  ): { record: SavedPanRecord | null; usedUntitled: boolean } => {
    if (!activePan) return { record: null, usedUntitled: false };
    const rawQuestion = String(options?.question ?? activePan.question ?? question ?? '').trim();
    const allowUntitled = Boolean(options?.allowUntitled);
    const usedUntitled = !rawQuestion && allowUntitled;
    const finalQuestion = rawQuestion || (allowUntitled ? '未命名事项' : '');
    if (!finalQuestion) return { record: null, usedUntitled: false };

    const targetId = String(options?.recordId || currentRecordIdRef.current || currentRecordId || '').trim();
    const existing = targetId
      ? recordsRef.current.find((item) => item.id === targetId) || null
      : null;
    const nextRecord = buildPanRecordSnapshot(activePan, finalQuestion, {
      recordId: existing?.id || targetId || null,
      createdAt: existing?.createdAt,
      aiReport: options?.aiReport ?? existing?.aiReport ?? '',
      aiFollowupHistory: options?.aiFollowupHistory ?? existing?.aiFollowupHistory ?? [],
      notes: options?.notes ?? getPanRecordNotes(existing),
      trueSolarInfo: options?.trueSolarInfo,
    });
    applyPanRecords([nextRecord, ...recordsRef.current.filter((item) => item.id !== nextRecord.id)]);
    setCurrentRecordId(nextRecord.id);
    currentRecordIdRef.current = nextRecord.id;
    return { record: nextRecord, usedUntitled };
  };

  const persistSessionSnapshot = (activePan: PanResult) => {
    savePanSession({
      savedAt: Date.now(),
      dateTimeValue,
      panMode,
      keIndex: Math.max(0, Math.min(5, Number(keIndex) || 0)),
      manualJu,
      uiMode,
      showConfig,
      question,
      consultMode,
      useTrueSolarTime,
      trueSolarInfo,
      currentRecordId,
      resultSource,
      aiReport,
      aiFollowupHistory,
      aiExplainMode,
      pan: serializePanResult(activePan),
    });
  };

  const normalizeAiErrorMessage = (raw: string): string => {
    const text = String(raw || '').trim();
    if (!text) return 'AI 解析失败，请稍后再试。';
    if (text.includes('请先登录')) {
      return '请先登录后再使用 AI 解盘。';
    }
    const memberKeywords = ['未开通会员', '会员已过期', '积分不足', '未激活', '权限不足'];
    if (memberKeywords.some((kw) => text.includes(kw))) {
      return '当前账号积分或会员权限不足，请扫码添加若轩老师开通会员权限。';
    }
    const lower = text.toLowerCase();
    if (
      lower.includes('abort')
      || lower.includes('aborted')
      || lower.includes('timed out')
      || lower.includes('timeout')
      || text.includes('请求超时')
    ) {
      return 'AI 解析超时或连接中断，请重试；若多次失败，请切换模型后再试。';
    }
    if (
      lower.includes('network')
      || lower.includes('failed to fetch')
      || text.includes('网络连接失败')
    ) {
      return '网络连接不稳定，请稍后重试。';
    }
    return text;
  };

  const isAiConnectivityError = (raw: string): boolean => {
    const text = String(raw || '').trim().toLowerCase();
    if (!text) return false;
    return (
      text.includes('请求超时')
      || text.includes('超时')
      || text.includes('网络')
      || text.includes('failed to fetch')
      || text.includes('fetch')
      || text.includes('abort')
      || text.includes('timed out')
      || text.includes('连接')
    );
  };

  useEffect(() => {
    if (!loading) return;
    const timer = window.setInterval(() => {
      setProgressIndex((prev) => (prev < PROGRESS_STEPS.length - 1 ? prev + 1 : prev));
    }, 220);
    return () => window.clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    if (!aiLoading) {
      setAiProgressPercent(0);
      setAiProgressIndex(0);
      setAiElapsedSeconds(0);
      return;
    }
    const activeSteps = aiLoadingMode === 'followup' ? AI_FOLLOWUP_PROGRESS_STEPS : AI_PROGRESS_STEPS;
    const activeDuration = aiLoadingMode === 'followup'
      ? AI_FOLLOWUP_PROGRESS_DURATION_MS
      : AI_PROGRESS_DURATION_MS;
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const rawPercent = (elapsed / activeDuration) * 100;
      const percent = Math.max(0, Math.min(AI_PROGRESS_MAX_WAIT_PERCENT, rawPercent));
      const ratio = Math.max(0, Math.min(0.999, percent / 100));
      const nextIndex = Math.min(
        activeSteps.length - 1,
        Math.floor(ratio * activeSteps.length),
      );
      setAiProgressPercent(percent);
      setAiProgressIndex(nextIndex);
      setAiElapsedSeconds(Math.max(0, Number((elapsed / 1000).toFixed(1))));
    };
    tick();
    const timer = window.setInterval(tick, AI_PROGRESS_TICK_MS);
    return () => window.clearInterval(timer);
  }, [aiLoading, aiLoadingMode]);

  useEffect(() => {
    recordsRef.current = records;
    savePanRecords(records);
  }, [records]);

  useEffect(() => {
    let cancelled = false;
    const hydrateCloudRecords = async () => {
      const remote = await fetchUserRecordCollection<SavedPanRecord>('pan');
      if (cancelled) return;
      if (!remote) {
        cloudRecordsReadyRef.current = false;
        cloudRecordsSyncHashRef.current = JSON.stringify(recordsRef.current);
        return;
      }
      const merged = sortPanRecordsByRecency(mergePanRecordsById([
        ...recordsRef.current,
        ...(Array.isArray(remote.records) ? remote.records : []),
      ])).slice(0, MAX_PAN_RECORDS);
      recordsRef.current = merged;
      setRecords(merged);
      cloudRecordsReadyRef.current = true;
      cloudRecordsSyncHashRef.current = JSON.stringify(merged);
    };
    void hydrateCloudRecords();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!cloudRecordsReadyRef.current) return;
    const nextHash = JSON.stringify(records);
    if (nextHash === cloudRecordsSyncHashRef.current) return;
    const timer = window.setTimeout(() => {
      void saveUserRecordCollection('pan', records).then((ok) => {
        if (ok) {
          cloudRecordsSyncHashRef.current = nextHash;
        }
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [records]);

  useEffect(() => {
    const onResize = () => {
      setIsMobileViewport(window.innerWidth < 1024);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!showAiQuestionModal) return;
    const timer = window.setTimeout(() => {
      aiExtraQuestionRef.current?.focus();
    }, 40);
    return () => window.clearTimeout(timer);
  }, [showAiQuestionModal]);

  useEffect(() => {
    if (!pan) return;
    setSelectedPalacePos(null);
    setExpandedXiangYiKeys([]);
  }, [pan]);

  useEffect(() => {
    if (!pan) return;
    persistSessionSnapshot(pan);
  }, [
    pan,
    dateTimeValue,
    panMode,
    keIndex,
    manualJu,
    uiMode,
    showConfig,
    question,
    consultMode,
    useTrueSolarTime,
    trueSolarInfo,
    currentRecordId,
    resultSource,
    aiReport,
    aiFollowupHistory,
    aiExplainMode,
  ]);

  const selectModeOnboarding = (mode: UiMode) => {
    setUiMode(mode);
    savePanSettings({ ...loadPanSettings(), defaultMode: mode });
    try {
      localStorage.setItem(PAN_MODE_ONBOARDING_KEY, '1');
    } catch {
      // noop
    }
    setShowModeOnboarding(false);
  };

  const requestGeoLocation = async (): Promise<GeoLocationInfo | null> => {
    if (!navigator.geolocation) {
      setError('当前浏览器不支持定位，无法计算真太阳时。');
      return null;
    }
    setGeoLoading(true);
    setError('');
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 6000,
          maximumAge: 86400000,
        });
      });
      const next: GeoLocationInfo = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        fetchedAt: Date.now(),
      };
      setGeoLocation(next);
      saveGeoLocation(next);
      return next;
    } catch {
      setError('定位失败，请允许定位权限后重试。');
      return null;
    } finally {
      setGeoLoading(false);
    }
  };

  const toggleTrueSolar = async () => {
    const next = !useTrueSolarTime;
    setUseTrueSolarTime(next);
    if (!next) {
      setTrueSolarInfo(null);
      return;
    }
    if (geoLocation) return;
    const loc = await requestGeoLocation();
    if (!loc) {
      setUseTrueSolarTime(false);
    }
  };

  const patchRecordAiReport = (recordId: string, report: string, followupHistory?: ChartAiFollowupItem[]) => {
    const targetId = String(recordId || '').trim();
    if (!targetId) return;
    applyPanRecords(recordsRef.current.map((item) => {
      if (item.id !== targetId) return item;
      return {
        ...item,
        updatedAt: Date.now(),
        aiReport: report,
        aiFollowupHistory: followupHistory !== undefined ? followupHistory : (item.aiFollowupHistory || []),
      };
    }));
  };

  const patchRecordNotes = (recordId: string, updater: (notes: PanNoteItem[]) => PanNoteItem[]) => {
    const targetId = String(recordId || '').trim();
    if (!targetId) return;
    applyPanRecords(recordsRef.current.map((item) => {
      if (item.id !== targetId) return item;
      const nextNotes = sortPanNoteItemsByRecent(updater(getPanRecordNotes(item)));
      return {
        ...item,
        updatedAt: Date.now(),
        note: nextNotes[0]?.content || '',
        notes: nextNotes,
      };
    }));
  };

  const deleteRecord = (recordId: string) => {
    applyPanRecords(recordsRef.current.filter((item) => item.id !== recordId));
    if (currentRecordIdRef.current === recordId) {
      currentRecordIdRef.current = null;
      setCurrentRecordId(null);
      setEditingNoteId(null);
      setNoteDraft('');
    }
  };

  const applyRecord = (record: SavedPanRecord) => {
    const touchedRecord: SavedPanRecord = { ...record, updatedAt: Date.now() };
    applyPanRecords([touchedRecord, ...recordsRef.current.filter((item) => item.id !== record.id)]);
    const restored = deserializePanResult(record.pan);
    setPan(restored);
    setResultSource('history');
    setQuestion(record.question);
    setDateTimeValue(record.sourceDateTime);
    setPanMode(record.panMode);
    setConsultMode(record.consultMode === 'remote' ? 'remote' : 'face');
    setKeIndex(record.keIndex);
    setManualJu(record.manualJu);
    setUseTrueSolarTime(record.useTrueSolarTime);
    setTrueSolarInfo(record.trueSolar);
    setCurrentRecordId(record.id);
    currentRecordIdRef.current = record.id;
    setShowConfig(false);
    setShowHistoryModal(false);
    setAiError('');
    const restoredAiReport = String(record.aiReport || '');
    const restoredFollowups = Array.isArray(record.aiFollowupHistory) ? record.aiFollowupHistory : [];
    setAiReport(restoredAiReport);
    setAiFollowupHistory(restoredFollowups);
    setAiFollowupStreaming('');
    setShowAiPanel(Boolean(restoredAiReport.trim()) || restoredFollowups.length > 0);
    setShowFollowupComposer(false);
    window.setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  useLayoutEffect(() => {
    const targetId = String(pendingRecordId || '').trim();
    if (!targetId) return;
    const target = records.find((item) => String(item.id) === targetId);
    if (target) {
      applyRecord(target);
    }
    onPendingRecordConsumed?.(targetId);
  }, [pendingRecordId, records, onPendingRecordConsumed]);

  const submitPan = async (
    targetDateTime?: string,
    overrides?: { keIndex?: number },
  ): Promise<PanResult | null> => {
    const actual = targetDateTime || dateTimeValue;
    const parsed = parseDateTimeLocal(actual);
    if (!parsed) {
      setError('排盘时间格式无效。');
      return null;
    }

    const activeMode = panMode;
    const rawKe = typeof overrides?.keIndex === 'number' ? overrides.keIndex : Number(keIndex);
    const safeKe = Math.max(0, Math.min(5, Number(rawKe) || 0));
    const selectedJu = manualJu === 'auto' ? null : Number(manualJu);
    let solarInfo: TrueSolarAdjustInfo | null = null;
    let activeLocation = geoLocation;

    setLoading(true);
    setProgressIndex(0);
    setError('');

    try {
      if (useTrueSolarTime) {
        if (!activeLocation) {
          activeLocation = await requestGeoLocation();
        }
        if (!activeLocation) {
          throw new Error('未获取定位，无法计算真太阳时');
        }
      }
      const response = await calculatePanApi({
        dateTime: actual,
        panMode: activeMode,
        question: question.trim(),
        keIndex: safeKe,
        manualJu: selectedJu,
        useTrueSolarTime,
        timezoneOffsetMinutes: parsed.getTimezoneOffset(),
        location: useTrueSolarTime ? activeLocation : null,
      });
      const result = deserializePanResult(response.pan as SavedPanRecord['pan']);
      solarInfo = (response.trueSolarInfo as TrueSolarAdjustInfo | null) || null;

      setPan(result);
      setResultSource('live');
      setTrueSolarInfo(solarInfo);
      setShowConfig(false);
      setAiError('');
      setAiReport('');
      setAiFollowupHistory([]);
      setAiFollowupStreaming('');
      setShowAiPanel(false);
      setShowFollowupComposer(false);
      setSelectedPalacePos(null);
      setExpandedXiangYiKeys([]);
      const saved = upsertPanRecord(result, { question: result.question, trueSolarInfo: solarInfo });
      const recordId = saved.record?.id || null;
      setCurrentRecordId(recordId);
      currentRecordIdRef.current = recordId;
      await new Promise((resolve) => window.setTimeout(resolve, 220));
      window.setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '后端排盘失败';
      setPan(null);
      setError(`排盘失败：${msg}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const text = '轩辕国学排盘';
    try {
      if (navigator.share) {
        await navigator.share({ title: text, text, url: shareUrl });
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const input = document.createElement('input');
        input.value = shareUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setError('链接已复制，可直接分享。');
    } catch {
      setError('分享失败，请重试。');
    }
  };

  const buildPalaceToolQuery = (cell?: PalaceCell | null): URLSearchParams => {
    const params = new URLSearchParams();
    if (!cell) return params;

    const deityName = DEITY_SYMBOL_TO_NAME[cell.deitySymbol] || '';
    const starName = STAR_SYMBOL_TO_NAME[cell.starSymbol] || '';
    const doorName = DOOR_SYMBOL_TO_NAME[cell.gateSymbol] || '';
    const heavenStem = splitStems(cell.heavenlyStem)[0] || '';
    const earthStem = splitStems(cell.earthlyStem)[0] || '';
    const forceTombStem = getForcedTombStemForZhiFuKun(cell);

    if (deityName) params.set('god', deityName);
    if (starName) params.set('star', starName);
    if (doorName) params.set('door', doorName);
    if (cell.heavenlyStem) params.set('heaven', cell.heavenlyStem);
    if (cell.earthlyStem) params.set('earth', cell.earthlyStem);
    if (heavenStem) params.set('heavenStem', heavenStem);
    if (earthStem) params.set('earthStem', earthStem);
    if (cell.leadStem) params.set('leadStem', cell.leadStem);
    if (cell.hasVoid) params.set('void', '1');
    if (cell.hasMenPo) params.set('doorPo', '1');
    if (cell.heavenlyHasXing) params.set('heavenXing', '1');
    if (cell.earthlyHasXing) params.set('earthXing', '1');
    if (cell.heavenlyInTomb || (!!forceTombStem && splitStems(cell.heavenlyStem).includes(forceTombStem))) params.set('heavenMu', '1');
    if (cell.earthlyInTomb || (!!forceTombStem && splitStems(cell.earthlyStem).includes(forceTombStem))) params.set('earthMu', '1');
    return params;
  };

  const openPracticeTool = (tool: 'decoder' | 'findObject', cell?: PalaceCell | null) => {
    const extra = buildPalaceToolQuery(cell);
    if (pan) {
      persistSessionSnapshot(pan);
      markPanSessionRestoreOnce();
    }
    if (onOpenTool) {
      onOpenTool(tool, extra);
      return;
    }
    const params = new URLSearchParams(extra);
    params.set('tool', tool);
    params.set('from', 'pan');
    window.location.href = `/home?${params.toString()}`;
  };

  const runFourHarmRelief = async (cell?: PalaceCell | null) => {
    if (!canShowFourXiangyi) {
      setShowFourHarmResult(false);
      return;
    }
    if (!canUseFourXiangyi) {
      setShowFourHarmResult(true);
      setFourHarmLoading(false);
      setFourHarmLines([]);
      setFourHarmError('已显示入口，但当前账号非管理员会员，无法调用后台。');
      return;
    }
    const target = cell || null;
    if (!target) {
      setFourHarmError('请先选择一个宫位。');
      setShowFourHarmResult(true);
      return;
    }
    const forceTombStemResolver = (item: PalaceCell): string | null => getForcedTombStemForZhiFuKun(item);
    const truthState = pan
      ? buildFourHarmTruthState(pan, { resolveForceTombStem: forceTombStemResolver })
      : {
        trueVoidPalaces: [],
        trueTombPalaces: [],
        voidTriggeredPalaces: [],
        tombTriggeredPalaces: [],
        tombTargetCount: 0,
        reverseJudgeTriggered: false,
      };
    const forceTombStem = forceTombStemResolver(target);
    const heavenlyStems = splitStems(target.heavenlyStem);
    const earthlyStems = splitStems(target.earthlyStem);
    const targetHeavenlyInTomb = target.heavenlyInTomb || (Boolean(forceTombStem) && heavenlyStems.includes(String(forceTombStem)));
    const targetEarthlyInTomb = target.earthlyInTomb || (Boolean(forceTombStem) && earthlyStems.includes(String(forceTombStem)));
    const targetHasTomb = target.hasTomb || targetHeavenlyInTomb || targetEarthlyInTomb || truthState.tombTriggeredPalaces.includes(target.position);
    const targetTrueVoid = target.hasVoid
      ? truthState.trueVoidPalaces.includes(target.position)
      : false;
    const targetTrueTomb = targetHasTomb
      ? (truthState.tombTargetCount > 0 ? truthState.trueTombPalaces.includes(target.position) : true)
      : false;

    setShowFourHarmResult(true);
    setFourHarmLoading(true);
    setFourHarmError('');
    setFourHarmLines([]);
    try {
      const resp = await getFourHarmRelief({
        position: target.position,
        trigram: target.trigram,
        gateSymbol: target.gateSymbol,
        heavenlyStem: target.heavenlyStem,
        earthlyStem: target.earthlyStem,
        leadStem: target.leadStem,
        hasVoid: target.hasVoid,
        hasMenPo: target.hasMenPo,
        heavenlyHasXing: target.heavenlyHasXing,
        earthlyHasXing: target.earthlyHasXing,
        earthlyHasXingByFuYin: target.earthlyHasXingByFuYin,
        heavenlyInTomb: targetHeavenlyInTomb,
        earthlyInTomb: targetEarthlyInTomb,
        trueVoid: target.hasVoid ? targetTrueVoid : undefined,
        trueTomb: targetHasTomb ? targetTrueTomb : undefined,
        reverseJudgeTriggered: truthState.reverseJudgeTriggered,
      });
      setFourHarmLines(resp.lines.length ? resp.lines : ['暂无四害化解建议。']);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '四害化解失败';
      setFourHarmError(msg);
    } finally {
      setFourHarmLoading(false);
    }
  };

  const openHistory = () => {
    if (!records.length) {
      setError('暂无排盘记录。保存、记笔记或做过 AI 解析的局都会保留。');
      return;
    }
    setShowHistoryModal(true);
  };

  const showFeatureDevelopingNotice = () => {
    setNotice('正在研发中，即将上线');
    window.setTimeout(() => setNotice(''), 1800);
  };

  const openAiPanel = (options?: { openQuestionModal?: boolean }) => {
    if (!ensureAiMemberAccess()) return;
    setShowAiPanel(true);
    setAiError('');
    setShowFollowupComposer(false);
    if (options?.openQuestionModal) {
      setShowAiQuestionModal(true);
    }
  };

  const handleSwitchJu = async (direction: 'prev' | 'next') => {
    if (loading || aiLoading) return;
    const base = parseDateTimeLocal(dateTimeValue);
    if (!base) {
      setError('当前时间格式异常，无法切换上局下局。');
      return;
    }
    const delta = direction === 'prev' ? -1 : 1;
    if (panMode === 'ke') {
      const currentKe = Math.max(0, Math.min(5, Number(keIndex) || 0));
      let nextKe = currentKe + delta;
      const nextDate = new Date(base.getTime());
      if (nextKe < 0) {
        nextKe = 5;
        nextDate.setHours(nextDate.getHours() - 1);
      } else if (nextKe > 5) {
        nextKe = 0;
        nextDate.setHours(nextDate.getHours() + 1);
      }
      nextDate.setMinutes(nextKe * 10, 0, 0);
      const nextDateTime = toDateTimeLocalValue(nextDate);
      setDateTimeValue(nextDateTime);
      setKeIndex(nextKe);
      await submitPan(nextDateTime, { keIndex: nextKe });
      return;
    }
    const nextDate = new Date(base.getTime() + delta * 2 * 60 * 60 * 1000);
    nextDate.setSeconds(0, 0);
    const nextDateTime = toDateTimeLocalValue(nextDate);
    setDateTimeValue(nextDateTime);
    await submitPan(nextDateTime);
  };

  const handleHeaderBack = () => {
    if (showAiQuestionModal) {
      setShowAiQuestionModal(false);
      return;
    }
    if (showHistoryModal) {
      setShowHistoryModal(false);
      return;
    }
    if (showNotesPanel) {
      setShowNotesPanel(false);
      return;
    }
    if (showMasterConsultModal) {
      setShowMasterConsultModal(false);
      return;
    }
    if (showSolutionModal) {
      setShowSolutionModal(false);
      return;
    }
    if (aiErrorModal.open) {
      setAiErrorModal({ open: false, message: '' });
      return;
    }
    if (showFollowupComposer) {
      setShowFollowupComposer(false);
      return;
    }
    if (showAiPanel) {
      setShowAiPanel(false);
      return;
    }
    if (isMobileViewport && selectedPalacePos) {
      setSelectedPalacePos(null);
      setExpandedXiangYiKeys([]);
      return;
    }
    if (pan && !showConfig) {
      if (preferArchiveBack) {
        clearPanSessionSnapshot();
        if (onBack) {
          onBack();
          return;
        }
      }
      setPan(null);
      setShowConfig(true);
      setSelectedPalacePos(null);
      setExpandedXiangYiKeys([]);
      setAiReport('');
      setAiFollowupHistory([]);
      setAiFollowupStreaming('');
      setAiError('');
      setShowAiPanel(false);
      setShowFollowupComposer(false);
      return;
    }
    clearPanSessionSnapshot();
    if (onBack) {
      onBack();
      return;
    }
    window.location.href = '/home';
  };

  const handleBottomTabSwitch = (tab: 'pan' | 'practice' | 'study' | 'mine') => {
    if (tab !== 'pan' && pan) {
      persistSessionSnapshot(pan);
      markPanSessionRestoreOnce();
    }
    if (onNavigateTab) {
      onNavigateTab(tab);
      return;
    }
    const fallbackUrl = tab === 'pan'
      ? '/'
      : `/home?tab=${tab}`;
    window.location.href = fallbackUrl;
  };

  const runAiInterpretation = async (targetPan?: PanResult) => {
    if (!ensureAiMemberAccess()) return;
    const activePan = targetPan || pan;
    if (!activePan) {
      setAiError('请先排盘，再进行 AI 解析。');
      setShowAiPanel(true);
      return;
    }
    const inputText = aiExtraQuestion.trim();
    const shenYongMatch = inputText.match(/^请补充提问更多细节，或者在取用神方面有什么建议？[^补充提问：]*/);
    const shenYongSuggestion = shenYongMatch ? shenYongMatch[0].replace(/^请补充提问更多细节，或者在取用神方面有什么建议？/, '').trim() : '';
    const extraQ = inputText.includes('补充提问：')
      ? inputText.split('补充提问：')[1].trim()
      : (shenYongSuggestion ? '' : inputText);
    const baseQuestionRaw = String(activePan.question || question || '').trim();
    const priorReport = aiFollowupHistory.length > 0
      ? aiFollowupHistory[aiFollowupHistory.length - 1].a
      : aiReport.trim();
    const isFollowup = Boolean(priorReport && extraQ);
    const resolvedQuestion = String(baseQuestionRaw || (!isFollowup ? extraQ : '')).trim();

    if (!resolvedQuestion && !extraQ) {
      const message = '事项未填写，请先补充提问后再进行 AI 解读。';
      setAiError(message);
      setShowAiPanel(true);
      setShowFollowupComposer(false);
      if (aiExtraQuestionRef.current) aiExtraQuestionRef.current.focus();
      else if (questionInputRef.current) questionInputRef.current.focus();
      else if (questionTextareaRef.current) questionTextareaRef.current.focus();
      return;
    }
    setShowAiQuestionModal(false);

    const previousReport = aiReport;
    const previousFollowupHistory = [...aiFollowupHistory];
    const previousFollowupStreaming = aiFollowupStreaming;
    const workingPan = resolvedQuestion && activePan.question !== resolvedQuestion
      ? { ...activePan, question: resolvedQuestion }
      : activePan;
    if (resolvedQuestion && question !== resolvedQuestion) {
      setQuestion(resolvedQuestion);
    }
    if (resolvedQuestion && activePan.question !== resolvedQuestion) {
      setPan(workingPan);
    }
    const ensuredRecord = upsertPanRecord(workingPan, {
      question: resolvedQuestion,
      aiReport: previousReport,
      aiFollowupHistory: previousFollowupHistory,
    });
    const ensuredRecordId = ensuredRecord.record?.id || currentRecordIdRef.current || currentRecordId || null;
    const requestStartedAt = Date.now();
    let completedSuccessfully = false;

    resetAiStreamingPhase();
    setAiLoading(true);
    setAiLoadingMode(isFollowup ? 'followup' : 'initial');
    setAiError('');
    setShowAiPanel(true);
    setShowFollowupComposer(false);

    let streamModeHint = aiStreamOutputEnabled;
    try {
      const featureConfig = await Promise.race([
        getFeatureChatConfig('chartInterpreter'),
        new Promise<null>((resolve) => {
          window.setTimeout(() => resolve(null), 4500);
        }),
      ]);
      if (featureConfig) {
        streamModeHint = featureConfig.streamOutputEnabled;
        setAiStreamOutputEnabled(featureConfig.streamOutputEnabled);
      }
    } catch {
      // ignore feature-config lookup failures, fallback to existing hint
    }
    setAiResponseMode(
      streamModeHint === true
        ? 'stream'
        : (streamModeHint === false ? 'non_stream' : 'unknown')
    );

    try {
      const baseQuestion = resolvedQuestion || '请先做综合判断，再给行动建议。';
      const panSummary = buildPanSummaryForAi(workingPan, consultMode, {
        remoteMonthAsSubject: loadPanSettings().remoteMonthAsSubject === true,
      });
      const apiPriorReport = isFollowup ? priorReport : undefined;
      const apiFollowupHistory = isFollowup && aiFollowupHistory.length > 0
        ? aiFollowupHistory
        : (isFollowup ? [] : []);
      const prompt = isFollowup
        ? buildChartAiFollowupMessage({
          dataText: panSummary,
          question: baseQuestion,
          extraQuestion: extraQ || undefined,
          priorReport: apiPriorReport,
          followupHistory: apiFollowupHistory.length > 0 ? apiFollowupHistory : undefined,
        })
        : buildChartAiUserMessage({
          dataText: panSummary,
          question: baseQuestion,
          extraQuestion: extraQ || undefined,
          priorReport: apiPriorReport,
          followupHistory: apiFollowupHistory.length > 0 ? apiFollowupHistory : undefined,
          shenYongSuggestion: shenYongSuggestion || undefined,
        });
      const knowledgeQuery = buildChartAiKnowledgeQuery(
        panSummary,
        extraQ ? `${baseQuestion}\n${extraQ}` : baseQuestion,
      );
      const followupSid = (isFollowup && (ensuredRecordId || recordsRef.current[0]?.id))
        ? (ensuredRecordId || recordsRef.current[0]?.id)!
        : undefined;
      let streamBuffer = '';
      const response = await chatApiStream({
        messages: [{ role: 'user', content: prompt }],
        jsonMode: false,
        stream: streamModeHint === true,
        feature: 'chartInterpreter',
        featureMode: isFollowup ? 'followup' : 'manual',
        followupSessionId: followupSid,
        knowledgeQuery,
        usageQuestion: baseQuestionRaw || baseQuestion,
        usageExtraQuestion: extraQ || undefined,
      }, {
        onMode: (mode) => {
          setAiResponseMode(mode);
          setAiStreamOutputEnabled(mode === 'stream');
          if (mode === 'stream') {
            streamBuffer = '';
            if (isFollowup) setAiFollowupStreaming('');
            else setAiReport('');
          } else {
            setAiThinkingOverlayVisible(true);
          }
        },
        onChunk: (chunk) => {
          if (!chunk) return;
          streamBuffer += chunk;
          const normalized = normalizeAiReportLayoutText(streamBuffer);
          const visible = pickStreamVisibleText(normalized) || normalized.trimEnd();
          if (!visible) return;
          if (isFollowup) setAiFollowupStreaming(visible);
          else setAiReport(visible);
          markAiFirstSentenceVisible(visible);
        },
      });
      const finishReason = extractChatFinishReason(response);
      const reportText = appendLengthLimitHint(
        streamBuffer || response.text || '解析失败，请重试。',
        finishReason,
        isFollowup ? 'followup' : 'initial',
      );
      markAiFirstSentenceVisible(reportText);
      const targetRecordId = ensuredRecordId || recordsRef.current[0]?.id || null;
      if (isFollowup) {
        const nextHistory: ChartAiFollowupItem[] = [...aiFollowupHistory, { q: extraQ, a: reportText }];
        setAiFollowupHistory(nextHistory);
        setAiFollowupStreaming('');
        setAiExtraQuestion('');
        if (targetRecordId) {
          patchRecordAiReport(targetRecordId, aiReport, nextHistory);
          setCurrentRecordId(targetRecordId);
          currentRecordIdRef.current = targetRecordId;
        }
      } else {
        setAiReport(reportText);
        setAiFollowupHistory([]);
        if (targetRecordId) {
          patchRecordAiReport(targetRecordId, reportText, []);
          setCurrentRecordId(targetRecordId);
          currentRecordIdRef.current = targetRecordId;
        }
      }
      completedSuccessfully = true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'AI 解析失败';
      const normalizedMessage = normalizeAiErrorMessage(msg);
      setAiError(normalizedMessage);
      if (isFollowup) {
        setAiReport(previousReport);
        setAiFollowupHistory(previousFollowupHistory);
        setAiFollowupStreaming(previousFollowupStreaming);
      }
      if (isAiConnectivityError(msg)) {
        setAiReport(previousReport);
        setAiFollowupHistory(previousFollowupHistory);
        setAiFollowupStreaming(previousFollowupStreaming);
        setAiErrorModal({ open: true, message: normalizedMessage });
      }
      setShowAiPanel(true);
    } finally {
      setAiLoading(false);
      setAiThinkingOverlayVisible(false);
      if (completedSuccessfully) {
        const elapsedSeconds = Math.max(1, Math.round((Date.now() - requestStartedAt) / 1000));
        const firstSentenceSeconds = aiFirstSentenceSecondsRef.current;
        const observedSeconds = Number.isFinite(Number(firstSentenceSeconds))
          ? Math.max(0.1, Number(firstSentenceSeconds))
          : elapsedSeconds;
        if (isFollowup) {
          setAiFollowupEstimateSeconds((prev) => Math.max(2, Math.min(90, Math.round(prev * 0.6 + observedSeconds * 0.4))));
        } else {
          setAiInitialEstimateSeconds((prev) => Math.max(3, Math.min(90, Math.round(prev * 0.6 + observedSeconds * 0.4))));
        }
      }
    }
  };

  // 提示词优化相关处理函数
  const handlePromptOptimization = async () => {
    if (!isAdminMember) {
      setOptimizationError('仅管理员可提交提示词优化。');
      return;
    }
    if (!pan) {
      setOptimizationError('请先排盘后再进行提示词优化。');
      return;
    }
    if (!promptOptimization.trim()) {
      setOptimizationError('请输入优化建议。');
      return;
    }
    setOptimizationLoading(true);
    setOptimizationError('');
    setOptimizationSaveNotice('');
    setOptimizationChangeNotes([]);
    try {
      // 构建当前的提示词内容
      const baseQuestion = pan.question.trim() || question.trim() || '请先做综合判断，再给行动建议。';
      const panSummary = buildPanSummaryForAi(pan, consultMode, {
        remoteMonthAsSubject: loadPanSettings().remoteMonthAsSubject === true,
      });
      const currentPrompt = buildChartAiUserMessage({
        dataText: panSummary,
        question: baseQuestion,
      });
      const result = await optimizeFeaturePromptAsAdmin({
        featureId: 'chartInterpreter',
        suggestion: promptOptimization.trim(),
        currentPrompt,
        question: baseQuestion,
        dataText: panSummary,
        lastOutput: aiReport.trim() || undefined,
      });
      setOptimizedPrompt(result.optimizedPrompt);
      setOptimizationChangeNotes(result.changeNotes);
      if (result.unchanged) {
        setOptimizationError('本次优化未产生明显差异。');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '提示词优化失败，请重试。';
      setOptimizationError(msg);
    } finally {
      setOptimizationLoading(false);
    }
  };

  const handleSaveOptimizedPrompt = async () => {
    if (!isAdminMember) {
      setOptimizationError('仅管理员可保存提示词。');
      return;
    }
    if (!optimizedPrompt.trim()) {
      setOptimizationError('请先提交优化，生成可保存的提示词。');
      return;
    }
    setOptimizationSaving(true);
    setOptimizationError('');
    setOptimizationSaveNotice('');
    try {
      await saveFeaturePromptAsAdmin({
        featureId: 'chartInterpreter',
        prompt: optimizedPrompt.trim(),
      });
      setOptimizationSaveNotice('已保存到后端 AI 提示词（chartInterpreter）。');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '保存失败，请重试。';
      setOptimizationError(msg);
    } finally {
      setOptimizationSaving(false);
    }
  };

  const handleRepeatTestWithOptimizedPrompt = async () => {
    if (!pan || !optimizedPrompt.trim()) {
      setOptimizationError('没有可用的优化提示词，请先进行提示词优化。');
      return;
    }
    // 使用优化后的提示词重新运行 AI 解析
    resetAiStreamingPhase();
    setAiLoading(true);
    setAiLoadingMode('initial');
    setAiError('');
    setShowAiPanel(true);
    try {
      const knowledgeQuery = buildChartAiKnowledgeQuery(
        buildPanSummaryForAi(pan, consultMode, {
          remoteMonthAsSubject: loadPanSettings().remoteMonthAsSubject === true,
        }),
        pan.question.trim() || question.trim() || '请先做综合判断，再给行动建议。'
      );
      let streamBuffer = '';
      const response = await chatApiStream({
        messages: [{ role: 'user', content: optimizedPrompt.trim() }],
        jsonMode: false,
        stream: aiStreamOutputEnabled === true,
        feature: 'chartInterpreter',
        featureMode: 'manual',
        knowledgeQuery,
      }, {
        onMode: (mode) => {
          setAiResponseMode(mode);
          setAiStreamOutputEnabled(mode === 'stream');
          if (mode === 'stream') {
            streamBuffer = '';
            setAiReport('');
          } else {
            setAiThinkingOverlayVisible(true);
          }
        },
        onChunk: (chunk) => {
          if (!chunk) return;
          streamBuffer += chunk;
          const normalized = normalizeAiReportLayoutText(streamBuffer);
          const visible = pickStreamVisibleText(normalized) || normalized.trimEnd();
          if (!visible) return;
          setAiReport(visible);
          markAiFirstSentenceVisible(visible);
        },
      });
      const finishReason = extractChatFinishReason(response);
      const reportText = appendLengthLimitHint(
        streamBuffer || response.text || '解析失败，请重试。',
        finishReason,
        'initial',
      );
      markAiFirstSentenceVisible(reportText);
      setAiReport(reportText);
      setAiFollowupHistory([]);
      const saved = upsertPanRecord(pan, {
        question: pan.question.trim() || question.trim(),
        aiReport: reportText,
        aiFollowupHistory: [],
      });
      const targetRecordId = saved.record?.id || currentRecordIdRef.current || currentRecordId || recordsRef.current[0]?.id || null;
      if (targetRecordId) {
        patchRecordAiReport(targetRecordId, reportText, []);
        setCurrentRecordId(targetRecordId);
        currentRecordIdRef.current = targetRecordId;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'AI 解析失败';
      setAiError(normalizeAiErrorMessage(msg));
    } finally {
      setAiLoading(false);
      setAiThinkingOverlayVisible(false);
    }
  };

  const startSimpleAiPan = async () => {
    if (!ensureAiMemberAccess()) return;
    const result = await submitPan();
    if (!result) return;
    setShowAiPanel(false);
    await runAiInterpretation(result);
  };

  const getStemClass = (position: number, stem: string, forceXing = false, forceTomb = false): string => {
    const palaceBranches = PALACE_BRANCHES[position] || [];
    const stemHasXing = forceXing || hasJiXingByStem(position, stem);
    const stemInTomb = forceTomb || isStemInTomb(stem, palaceBranches);
    if (stemHasXing && stemInTomb) return 'text-sky-600';
    if (stemHasXing) return 'text-blue-600';
    if (stemInTomb) return 'text-amber-700';
    return 'text-slate-900';
  };

  const renderStemText = (
    position: number,
    stemText: string,
    options?: { forceXing?: boolean; forceTombStem?: string | null },
  ): React.ReactNode => {
    const stems = splitStems(stemText);
    if (stems.length === 0) return stemText;
    return stems.map((stem, index) => (
      <span
        key={`${position}-${stem}-${index}`}
        className={getStemClass(
          position,
          stem,
          options?.forceXing,
          options?.forceTombStem === stem,
        )}
      >
        {stem}
      </span>
    ));
  };

  const getForcedTombStemForZhiFuKun = (cell: PalaceCell): string | null => {
    if (!pan) return null;
    if (!(cell.isZhiFu && cell.position === 2)) return null;
    if (!isStemInTomb('甲', PALACE_BRANCHES[2] || [])) return null;
    const stems = splitStems(pan.xunShou);
    const dunStem = stems[stems.length - 1] || '';
    return LIUYI_SET.has(dunStem) ? dunStem : null;
  };

  const getGateClass = (cell: PalaceCell): string => {
    if (cell.hasMenPo) return 'text-red-600';
    return 'text-slate-900';
  };

  const topLabelClass = 'px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 bg-[#fefbec] text-[#9c5b31] text-[14px] sm:text-[15px] lg:text-[14px] font-semibold border-b border-r border-slate-200 text-center';
  const topValueClass = 'px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 bg-white text-slate-900 text-[14px] sm:text-[15px] lg:text-[14px] border-b border-slate-200 text-left';
  const summaryTextClass = 'text-[14px] sm:text-[15px] lg:text-[14px]';
  const palaceSymbolClass = 'text-[14px] sm:text-[16px] lg:text-[17px] leading-none';
  const palaceVoidClass = 'text-[12px] sm:text-sm';
  const outerLeadClass = 'text-[14px] sm:text-[16px] lg:text-[17px] leading-none';
  const pillarItems = pan ? getPillarItems(pan) : [];
  const rowLabelClass = 'w-[72px] sm:w-[88px] shrink-0 text-sm sm:text-base font-semibold text-slate-700';
  const optionCircleClass = (active: boolean): string => (
    `w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 inline-flex items-center justify-center shrink-0 leading-none font-normal ${
      active ? 'border-[#8b6b3d] bg-[#8b6b3d] text-white' : 'border-[#b5becb] bg-white text-transparent'
    }`
  );
  const reportQuestionText = (pan?.question || question || '未填写事项').trim() || '未填写事项';
  const reportDateText = pan?.timeNodeText || dateTimeValue.replace('T', ' ');
  const reportTypeText = `${pan?.mode === 'ke' ? '刻盘' : '时盘'} · ${consultMode === 'remote' ? '远程测' : '当面测'} · ${pan?.juText || '-'}`;
  const reportTermText = pan?.solarTermText || '-';
  const reportPaperStyle: React.CSSProperties = {
    backgroundImage: 'linear-gradient(#faf9f6,#faf9f6),linear-gradient(135deg,#d4c5a9,#8B4513,#d4c5a9,#8B4513)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  };
  const reportTextureStyle: React.CSSProperties = {
    backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0.42), rgba(255,255,255,0)),url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E\")",
  };

  const activePalaceCell = useMemo(() => {
    if (!pan || !selectedPalacePos) return null;
    return pan.palaces.get(selectedPalacePos) || null;
  }, [pan, selectedPalacePos]);

  useEffect(() => {
    setShowFourHarmResult(false);
    setFourHarmLoading(false);
    setFourHarmLines([]);
    setFourHarmError('');
  }, [activePalaceCell?.position]);

  const xiangYiEntries = useMemo<XiangYiEntry[]>(() => {
    if (!activePalaceCell) return [];
    const items: XiangYiEntry[] = [];
    const seen = new Set<string>();
    const indexByLabel = new Map<string, number>();
    const palaceBranches = PALACE_BRANCHES[activePalaceCell.position] || [];
    const forceTombStem = getForcedTombStemForZhiFuKun(activePalaceCell);

    const pushEntry = (
      key: string,
      label: string,
      details: string[],
      statusTags: string[] = [],
      entryType: XiangYiEntry['entryType'] = 'normal',
    ) => {
      if (!label) return;
      const normalizedTags = Array.from(
        new Set(
          statusTags
            .filter((tag) => Boolean(tag) && tag !== '空亡'),
        ),
      );
      const normalized = sortXiangYiDetails(details);
      if (normalized.length === 0) return;
      const existingIndex = indexByLabel.get(label);
      if (typeof existingIndex === 'number') {
        const existing = items[existingIndex];
        existing.statusTags = Array.from(new Set([...existing.statusTags, ...normalizedTags]));
        existing.details = sortXiangYiDetails([...existing.details, ...normalized]);
        if (entryType === 'combo') existing.entryType = 'combo';
        return;
      }
      if (seen.has(key)) return;
      seen.add(key);
      indexByLabel.set(label, items.length);
      items.push({
        key,
        label,
        statusTags: normalizedTags,
        details: normalized,
        entryType,
      });
    };

    const xiangyiMode: 'full' | 'simple' = xiangyiCategoryMode === 'simple' ? 'simple' : 'full';
    if (activePalaceCell.gateSymbol && activePalaceCell.gateSymbol !== '-') {
      const gateChar = activePalaceCell.gateSymbol;
      const gateName = DOOR_SYMBOL_TO_NAME[gateChar] || `${gateChar}门`;
      const gateDetails = [...buildSymbolDetailLines(SYMBOL_DATA_BY_KEY.get(`door:${gateChar}`), xiangyiMode)];
      const gateStatus: string[] = [];
      if (activePalaceCell.hasMenPo) gateStatus.push('门迫');
      pushEntry(`gate:${gateChar}`, gateName, gateDetails, gateStatus);
    }

    if (activePalaceCell.deitySymbol && activePalaceCell.deitySymbol !== '-') {
      const deitySymbol = activePalaceCell.deitySymbol;
      const deityName = DEITY_SYMBOL_TO_NAME[deitySymbol] || deitySymbol;
      const dataChar = DEITY_SYMBOL_TO_DATA_CHAR[deitySymbol] || deitySymbol;
      pushEntry(
        `deity:${deitySymbol}`,
        deityName,
        buildSymbolDetailLines(SYMBOL_DATA_BY_KEY.get(`god:${dataChar}`), xiangyiMode),
        [],
      );
    }

    if (activePalaceCell.starSymbol && activePalaceCell.starSymbol !== '-') {
      const starSymbol = activePalaceCell.starSymbol;
      const starName = STAR_SYMBOL_TO_NAME[starSymbol] || `天${starSymbol}`;
      pushEntry(
        `star:${starSymbol}`,
        starName,
        buildSymbolDetailLines(SYMBOL_DATA_BY_KEY.get(`star:${starSymbol}`), xiangyiMode),
        [],
      );
    }

    const addStemEntry = (stem: string, stemType: 'heaven' | 'earth' | 'lead') => {
      const details = [...buildSymbolDetailLines(SYMBOL_DATA_BY_KEY.get(`stem:${stem}`), xiangyiMode)];
      const tags: string[] = [];
      const hasStemXing = hasJiXingByStem(activePalaceCell.position, stem);
      const hasStemTomb = isStemInTomb(stem, palaceBranches) || forceTombStem === stem;
      if (stemType === 'earth' && activePalaceCell.earthlyHasXingByFuYin) {
        tags.push('击刑');
      } else if (hasStemXing) {
        tags.push('击刑');
      }
      if (hasStemTomb) tags.push('入墓');
      pushEntry(`stem:${stemType}:${stem}`, stem, details, tags);
    };

    const heavenStems = splitStems(activePalaceCell.heavenlyStem);
    const earthStems = splitStems(activePalaceCell.earthlyStem);
    const leadStems = splitStems(activePalaceCell.leadStem);

    heavenStems.forEach((stem) => addStemEntry(stem, 'heaven'));
    earthStems.forEach((stem) => addStemEntry(stem, 'earth'));
    leadStems.forEach((stem) => addStemEntry(stem, 'lead'));

    const activePalace = POSITION_TO_TRIGRAM[activePalaceCell.position] || activePalaceCell.trigram || '';
    heavenStems.forEach((h) => {
      earthStems.forEach((e) => {
        const combo = normalizeComboKey(h, e);
        const reverse = normalizeComboKey(e, h);
        const diseaseLine = getPalaceDiseaseComboLine(activePalace, combo) || getPalaceDiseaseComboLine(activePalace, reverse);
        const genericDetail = dedupeComboDetailLines((STEM_COMBO_DETAILS.get(combo) || STEM_COMBO_DETAILS.get(reverse) || [])
          .map((line) => normalizeXiangyiBrackets(line.trim()))
          .filter((line) => line && !CORE_ORIGIN_REGEX.test(line)));
        // 组合象意必须保留原有内容；疾病象意作为追加信息，不得覆盖原条目。
        const detail = Array.from(new Set([
          ...genericDetail,
          ...(diseaseLine ? [diseaseLine] : []),
        ]));
        pushEntry(`combo:${combo}`, combo, detail, [], 'combo');
      });
    });

    return items;
  }, [activePalaceCell, xiangyiCategoryMode]);

  useEffect(() => {
    if (!activePalaceCell || xiangYiEntries.length === 0) return;
    setExpandedXiangYiKeys((prev) => prev.filter((key) => xiangYiEntries.some((entry) => entry.key === key)));
  }, [activePalaceCell, xiangYiEntries]);

  const showMobileXiangYiPage = Boolean(isMobileViewport && activePalaceCell);
  const hasAiReport = Boolean(aiReport.trim()) || aiFollowupHistory.length > 0;
  const aiModalQuestionRequired = !String(pan?.question || question || '').trim();
  const aiCanStartInterpretation = !aiLoading && (!aiModalQuestionRequired || Boolean(aiExtraQuestion.trim()));
  const isHistoryReplay = resultSource === 'history';
  const showHistoryParseCta = isHistoryReplay && !hasAiReport;
  const useDesktopSplitLayout = !isMobileViewport && hasAiReport;
  const shouldShowMobileAiPanel = Boolean(isMobileViewport && pan && !showMobileXiangYiPage && showAiPanel);
  const persistedCurrentRecordId = currentRecordIdRef.current || currentRecordId;
  const hasPersistedCurrentRecord = Boolean(
    persistedCurrentRecordId
    && records.some((item) => item.id === persistedCurrentRecordId),
  );
  const saveButtonLabel = hasPersistedCurrentRecord ? '已保存' : '保存';
  const saveButtonDisabled = !pan;
  const currentRecordNotes = useMemo(() => {
    if (!currentRecordId) return [];
    const current = records.find((item) => item.id === currentRecordId) || null;
    return sortPanNoteItemsByRecent(getPanRecordNotes(current));
  }, [currentRecordId, records]);
  const desktopActionShellClass = 'mx-auto w-full max-w-4xl';
  const desktopActionBaseButtonClass = 'inline-flex min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-[18px] border px-4 py-3 text-[13px] font-semibold transition-all duration-200 sm:flex-none sm:min-w-[112px] sm:text-sm';
  const desktopActionSoftButtonClass = `${desktopActionBaseButtonClass} border-[#e7dcc8] bg-white/92 text-[#5c4735] shadow-[0_8px_18px_rgba(113,82,43,0.08)] hover:-translate-y-0.5 hover:border-[#d7c5a7] hover:bg-[#fffaf1] hover:shadow-[0_14px_28px_rgba(113,82,43,0.12)]`;
  const desktopActionPrimaryButtonClass = `${desktopActionBaseButtonClass} border-[#8f6330] bg-gradient-to-r from-[#8c5a27] via-[#a36a2f] to-[#b67a34] text-[#fff9ef] shadow-[0_14px_30px_rgba(120,78,28,0.24)] hover:-translate-y-0.5 hover:shadow-[0_18px_34px_rgba(120,78,28,0.3)] disabled:translate-y-0`;
  const panMobileDockButtonClass = 'mobile-tabbar-btn group flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1.5 py-1 text-[11px] font-semibold transition-all duration-200 active:scale-[0.98]';
  const panMobileDockSoftButtonClass = `${panMobileDockButtonClass} text-slate-700 hover:text-slate-900`;
  const panMobileDockAiButtonClass = `${panMobileDockButtonClass} text-[#8b5a2b]`;
  const renderDesktopActionButtons = (options?: { forceQuestionModal?: boolean; containerClassName?: string }): React.ReactNode => {
    const forceQuestionModal = options?.forceQuestionModal === true;
    const containerClassName = options?.containerClassName || 'mt-3 flex justify-center';
    return (
      <div className={containerClassName}>
        <div className={desktopActionShellClass}>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <button
              onClick={() => setNotice('功能正在开发中')}
              type="button"
              className={desktopActionSoftButtonClass}
            >
              <MessageCircle className="w-4 h-4" />
              客服
            </button>
            <button
              onClick={() => { void copyFullPanText(); }}
              disabled={!pan}
              className={`${desktopActionSoftButtonClass} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <Share2 className="w-4 h-4" />
              分享
            </button>
            <button
              onClick={() => {
                if (forceQuestionModal || !hasAiReport) {
                  openAiPanel({ openQuestionModal: true });
                  return;
                }
                openAiPanel();
              }}
              disabled={aiLoading}
              className={`${desktopActionPrimaryButtonClass} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              <Sparkles className="w-4 h-4" />
              {aiLoading ? 'AI 解析中...' : 'AI 解析'}
            </button>
            <button
              onClick={openNotesPanel}
              type="button"
              className={desktopActionSoftButtonClass}
            >
              <BookOpen className="w-4 h-4" />
              笔记
            </button>
            <button
              onClick={handleSaveCurrentPan}
              disabled={saveButtonDisabled}
              className={`${desktopActionSoftButtonClass} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <Save className="w-4 h-4" />
              {saveButtonLabel}
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!shouldShowMobileAiPanel) return;
    const timer = window.setTimeout(() => {
      aiPanelContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, hasAiReport ? 80 : 20);
    return () => window.clearTimeout(timer);
  }, [shouldShowMobileAiPanel, hasAiReport]);

  const upsertCurrentPanRecord = (options?: { allowUntitled?: boolean }): { id: string | null; usedUntitled: boolean } => {
    const saved = upsertPanRecord(pan, {
      question: String(pan?.question || question || '').trim(),
      allowUntitled: options?.allowUntitled,
      aiReport,
      aiFollowupHistory,
    });
    return { id: saved.record?.id || null, usedUntitled: saved.usedUntitled };
  };

  const handleSaveCurrentPan = () => {
    const saved = upsertCurrentPanRecord({ allowUntitled: true });
    if (saved.id) {
      setCurrentRecordId(saved.id);
      currentRecordIdRef.current = saved.id;
      setNotice(saved.usedUntitled ? '未填写事项，已按“未命名事项”保存' : '已保存');
      return;
    }
    setNotice('请先起局后再保存');
  };

  const openNotesPanel = () => {
    const saved = upsertCurrentPanRecord({ allowUntitled: true });
    const id = saved.id || currentRecordIdRef.current || currentRecordId;
    if (!id) {
      setNotice('请先起局后再记笔记');
      return;
    }
    setCurrentRecordId(id);
    currentRecordIdRef.current = id;
    setEditingNoteId(null);
    setNoteDraft('');
    setShowNotesPanel(true);
    if (saved.usedUntitled) {
      setNotice('未填写事项，已自动保存为“未命名事项”');
    }
  };

  const handleSaveNoteItem = () => {
    const targetId = String(currentRecordId || '').trim();
    const content = String(noteDraft || '').trim();
    if (!targetId || !content) return;
    const now = Date.now();
    const activeEditingId = editingNoteId;
    patchRecordNotes(targetId, (prev) => {
      if (activeEditingId) {
        return prev.map((item) => (
          item.id !== activeEditingId
            ? item
            : { ...item, content, updatedAt: now }
        ));
      }
      return [
        {
          id: `note-${now.toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
          content,
          createdAt: now,
          updatedAt: now,
        },
        ...prev,
      ];
    });
    setEditingNoteId(null);
    setNoteDraft('');
    setNotice(activeEditingId ? '笔记已修改' : '笔记已新增');
  };

  const handleEditNoteItem = (note: PanNoteItem) => {
    setEditingNoteId(note.id);
    setNoteDraft(note.content);
  };

  const handleDeleteNoteItem = (noteId: string) => {
    const targetId = String(currentRecordId || '').trim();
    if (!targetId) return;
    patchRecordNotes(targetId, (prev) => prev.filter((item) => item.id !== noteId));
    if (editingNoteId === noteId) {
      setEditingNoteId(null);
      setNoteDraft('');
    }
    setNotice('笔记已删除');
  };

  const cancelNoteEditing = () => {
    setEditingNoteId(null);
    setNoteDraft('');
  };

  const buildEntryLabel = (entry: XiangYiEntry): string => {
    const tags = Array.from(
      new Set(entry.statusTags.filter((tag) => tag && !entry.label.includes(tag))),
    );
    return tags.length > 0 ? `${entry.label}（${tags.join('、')}）` : entry.label;
  };
  const renderXiangYiDetailLine = (line: string, key: string): React.ReactNode => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const coreMatch = trimmed.match(CORE_ORIGIN_REGEX);
    if (coreMatch) {
      return (
        <div key={key} className="grid grid-cols-[6.6em_1fr] gap-1.5 items-start">
          <p className="font-semibold text-slate-900 text-sm sm:text-base leading-5">核心原点 {coreMatch[1]}</p>
          <p className="text-slate-700 text-sm sm:text-base leading-5">{coreMatch[2]}</p>
        </div>
      );
    }
    const pairMatch = trimmed.match(/^([^：:]{1,14})[：:]\s*(.+)$/);
    if (pairMatch) {
      const label = pairMatch[1];
      return (
        <p key={key} className="text-slate-700 text-sm sm:text-base leading-5">
          <span className="font-semibold text-slate-800">{label}：</span>
          <span className="text-slate-700">{pairMatch[2]}</span>
        </p>
      );
    }
    return <p key={key} className="text-slate-700 text-sm sm:text-base leading-5">{trimmed}</p>;
  };

  const renderGroupedOriginDetails = (details: string[], keyPrefix: string): React.ReactNode => {
    const groups: Array<{ title: string; lines: string[]; id: string }> = [];
    let currentIndex = -1;
    details.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const coreMatch = trimmed.match(CORE_ORIGIN_REGEX);
      if (coreMatch) {
        groups.push({
          id: `core-${coreMatch[1]}-${idx}`,
          title: `核心原点 ${coreMatch[1]}：${coreMatch[2]}`,
          lines: [],
        });
        currentIndex = groups.length - 1;
        return;
      }
      if (currentIndex < 0) return;
      groups[currentIndex].lines.push(normalizeXiangyiBrackets(trimmed));
    });

    if (groups.length === 0) {
      const plain = details
        .map((line) => normalizeXiangyiBrackets(line.trim()))
        .filter(Boolean);
      if (plain.length === 0) return null;
      return (
        <div className="space-y-0 text-sm sm:text-base leading-5 py-1">
          {plain.map((line, idx) => renderXiangYiDetailLine(line, `${keyPrefix}-plain-${idx}`))}
        </div>
      );
    }

    const nonEmptyGroups = groups.filter((group) => group.lines.length > 0);
    if (nonEmptyGroups.length === 0) return null;

    return (
      <div className="space-y-1">
        {nonEmptyGroups.map((group, groupIdx) => (
          <div key={`${keyPrefix}-${group.id}-${groupIdx}`} className="py-1">
            <p className="text-sm sm:text-base font-semibold text-slate-900 mb-0.5 leading-5">{group.title}</p>
            <div className="space-y-0 text-sm sm:text-base leading-5">
              {group.lines.map((line, idx) => renderXiangYiDetailLine(line, `${keyPrefix}-${group.id}-${idx}`))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderFourHarmPanel = (): React.ReactNode => {
    if (!canShowFourXiangyi || !showFourHarmResult) return null;
    return (
      <div className="mt-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60 px-2.5 py-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-emerald-800">四害化解建议</p>
          {!canUseFourXiangyi && <span className="text-[11px] text-emerald-700">仅管理员会员可调用后台</span>}
        </div>
        {fourHarmLoading && (
          <p className="mt-1 text-sm text-emerald-700">正在生成化解建议…</p>
        )}
        {!fourHarmLoading && fourHarmError && (
          <p className="mt-1 text-sm text-rose-600">{fourHarmError}</p>
        )}
        {!fourHarmLoading && !fourHarmError && fourHarmLines.length > 0 && (
          fourHarmLines.length >= 2
            ? (
                <div className="mt-1.5 space-y-1 text-sm text-slate-700">
                  {fourHarmLines.map((line, index) => (
                    <div key={`four-harm-${index}`} className="flex items-start gap-2">
                      <span className="mt-[0.5em] h-1 w-1 rounded-full bg-slate-400 shrink-0" />
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              )
            : (
                <p className="mt-1.5 text-sm text-slate-700">{fourHarmLines[0]}</p>
              )
        )}
      </div>
    );
  };

  const normalizedAiReport = useMemo(
    () => normalizeAiReportLayoutText(aiReport),
    [aiReport],
  );
  const activeAiProgressSteps = aiLoadingMode === 'followup' ? AI_FOLLOWUP_PROGRESS_STEPS : AI_PROGRESS_STEPS;
  const aiLoadingEstimatedSeconds = aiLoadingMode === 'followup' ? aiFollowupEstimateSeconds : aiInitialEstimateSeconds;
  const aiLoadingTitle = '正在推演中，请诚心思考你的问题……';
  const aiLoadingEstimateHint = `预计约${aiLoadingEstimatedSeconds}秒`;
  const aiElapsedHint = `已思考${aiElapsedSeconds.toFixed(1)}秒`;

  const displayedAiReport = useMemo(() => {
    if (aiLoading && aiResponseMode === 'stream') {
      return normalizedAiReport;
    }
    if (aiExplainMode !== 'direct') return normalizedAiReport;
    return stripBracketExplanationsForDirect(normalizeAiReportLayoutText(stripInternalThinkingForDirect(normalizedAiReport)));
  }, [normalizedAiReport, aiExplainMode, aiLoading, aiResponseMode]);

  const fullReportForExport = useMemo(() => {
    const parts: string[] = [];
    if (displayedAiReport.trim()) parts.push(displayedAiReport.trim());
    aiFollowupHistory.forEach((item, idx) => {
      const ansBase = normalizeAiReportLayoutText(item.a);
      const ansDisplay = aiExplainMode === 'direct'
        ? stripBracketExplanationsForDirect(normalizeAiReportLayoutText(stripInternalThinkingForDirect(ansBase)))
        : ansBase;
      parts.push(`\n\n追问 ${idx + 1}：${item.q}\n\n${ansDisplay.trim()}`);
    });
    return parts.join('');
  }, [displayedAiReport, aiFollowupHistory, aiExplainMode]);

  const displayedFollowupStreaming = useMemo(() => {
    const base = normalizeAiReportLayoutText(aiFollowupStreaming);
    if (aiLoading && aiResponseMode === 'stream') return base;
    if (aiExplainMode !== 'direct') return base;
    return stripBracketExplanationsForDirect(normalizeAiReportLayoutText(stripInternalThinkingForDirect(base)));
  }, [aiFollowupStreaming, aiExplainMode, aiLoading, aiResponseMode]);

  const aiPlainText = useMemo(() => (
    fullReportForExport
      .replace(/```[\s\S]*?```/g, '')
      .replace(/^>\s?/gm, '')
      .replace(/[*_~`#]/g, '')
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  ), [fullReportForExport]);

  const copyToClipboard = async (text: string): Promise<boolean> => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fallback to textarea method
      }
    }
    // Fallback for HTTP/localhost
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const copyAiReportPlainText = async () => {
    if (!aiPlainText) {
      setAiError('请先生成 AI 解读后再复制。');
      return;
    }
    const ok = await copyToClipboard(aiPlainText);
    if (ok) {
      setAiError('');
      setError('已复制纯文字版解析。');
    } else {
      setAiError('复制失败，请手动复制。');
    }
  };

  const copyFullPanText = async () => {
    if (!pan) {
      setError('请先起卦后再复制。');
      return;
    }
    const fullText = buildPanSummaryForAi(pan, consultMode, {
      remoteMonthAsSubject: loadPanSettings().remoteMonthAsSubject === true,
    });
    if (!fullText) {
      setError('暂无盘面内容可复制。');
      return;
    }
    const ok = await copyToClipboard(fullText);
    if (ok) {
      setNotice('已复制完整盘面信息（含四柱）。');
      setTimeout(() => setNotice(''), 3000);
    } else {
      setError('复制失败，请手动复制。');
    }
  };

  const shareAiReportText = async () => {
    if (!aiPlainText) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: '轩辕国学 AI 解析', text: aiPlainText });
        return;
      }
      await navigator.clipboard.writeText(aiPlainText);
      setError('已复制解析，可直接分享。');
    } catch {
      setAiError('分享失败，请重试。');
    }
  };

  const handleRegenerateAiReport = async () => {
    if (!ensureAiMemberAccess()) return;
    setAiReport('');
    setAiFollowupHistory([]);
    setAiFollowupStreaming('');
    setAiExtraQuestion('');
    setShowFollowupComposer(false);
    await runAiInterpretation();
  };

  const downloadAiReportImage = async () => {
    if (!aiPlainText || !pan) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setAiError('当前浏览器不支持图片导出。');
      return;
    }
    const exportText = fullReportForExport
      .replace(/```[\s\S]*?```/g, '')
      .replace(/^>\s?/gm, '')
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
      .replace(/[*_~`]/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    const sourceLines = exportText
      .split('\n')
      .map((line) => line.trim())
      .filter((line, idx, arr) => line || (idx > 0 && arr[idx - 1] !== ''));

    const reportWidth = 1600;
    const outerPadding = 40;
    const frameX = outerPadding;
    const frameY = outerPadding;
    const frameW = reportWidth - outerPadding * 2;
    const contentX = frameX + 96;
    const contentW = frameW - 192;
    const headerH = 160;
    const infoH = 486;
    const boardH = 430;
    const footerH = 250;

    const bodyFont = "500 34px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    const headTitleFont = "800 58px 'FZXiaoBiaoSong-B05S','FZYaSong-B-GBK','Noto Serif SC','STSong','Songti SC','SimSun',serif";
    const smallLabelFont = "700 18px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    const valueFont = "700 26px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    const lineHeight = 50;
    const paragraphGap = 18;

    const tokenizeForWrap = (text: string): string[] => {
      const source = String(text || '').trim();
      if (!source) return [];
      const tokens: string[] = [];
      const re = /[\u4e00-\u9fff]|[A-Za-z0-9]+|\s+|[^\s]/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(source)) !== null) {
        tokens.push(m[0]);
      }
      return tokens.length > 0 ? tokens : source.split('');
    };

    const wrapText = (text: string, font: string, maxWidth: number): string[] => {
      const src = String(text || '').trim();
      if (!src) return [''];
      ctx.font = font;
      const lines: string[] = [];
      let current = '';
      const tokens = tokenizeForWrap(src);
      tokens.forEach((token) => {
        const next = `${current}${token}`;
        if (ctx.measureText(next).width > maxWidth && current) {
          lines.push(current.trimEnd());
          current = token.trimStart();
        } else {
          current = next;
        }
      });
      if (current.trim().length > 0) lines.push(current.trimEnd());
      return lines.length > 0 ? lines : [''];
    };

    type DrawBlock =
      | { type: 'space' }
      | { type: 'heading'; level: 1 | 2 | 3 | 4; text: string }
      | { type: 'lead'; text: string; subtitle?: string }
      | { type: 'paragraph'; text: string };
    const toHeadingLevel = (text: string): 3 | 4 => (
      /^([0-9一二三四五六七八九十]+[)）.、]|[一二三四五六七八九十]+、)/.test(String(text || '').trim()) ? 3 : 4
    );
    const blocks: DrawBlock[] = sourceLines.flatMap((line) => {
      const t = line.trim();
      if (!t) return [{ type: 'space' }];
      const lead = parseReportLeadHeading(t);
      if (lead) return [{ type: 'lead', text: lead.title, subtitle: lead.subtitle }];
      const markdownHeading = parseMarkdownHeadingLine(t);
      if (markdownHeading) {
        return [{ type: 'heading', level: Math.min(4, Math.max(1, markdownHeading.level)) as 1 | 2 | 3 | 4, text: markdownHeading.text }];
      }
      const decorHeading = parseDecorSectionHeading(t);
      if (decorHeading) {
        const heading: DrawBlock = { type: 'heading', level: toHeadingLevel(decorHeading.title), text: decorHeading.title };
        if (decorHeading.rest) return [heading, { type: 'paragraph', text: decorHeading.rest }];
        return [heading];
      }
      if (t.startsWith('- ') || t.startsWith('* ')) return [{ type: 'paragraph', text: t.slice(2).trim() }];
      return [{ type: 'paragraph', text: t.replace(/^\d+[.)、]\s+/, '').trim() }];
    });

    const headingConfigs: Record<1 | 2 | 3 | 4, { font: string; lineHeight: number; top: number; bottom: number; maxWidth: number }> = {
      1: { font: "800 48px 'FZXiaoBiaoSong-B05S','FZYaSong-B-GBK','Noto Serif SC','STSong','Songti SC','SimSun',serif", lineHeight: 60, top: 26, bottom: 28, maxWidth: contentW - 130 },
      2: { font: "800 42px 'FZXiaoBiaoSong-B05S','FZYaSong-B-GBK','Noto Serif SC','STSong','Songti SC','SimSun',serif", lineHeight: 54, top: 22, bottom: 22, maxWidth: contentW - 110 },
      3: { font: "800 40px 'Noto Serif SC','STSong','Songti SC','SimSun',serif", lineHeight: 50, top: 20, bottom: 16, maxWidth: contentW - 100 },
      4: { font: "700 33px 'Noto Serif SC','STSong','Songti SC','SimSun',serif", lineHeight: 44, top: 12, bottom: 10, maxWidth: contentW - 90 },
    };

    let bodyHeight = 0;
    blocks.forEach((block) => {
      if (block.type === 'space') {
        bodyHeight += 16;
        return;
      }
      if (block.type === 'paragraph') {
        const lines = wrapText(block.text, bodyFont, contentW - 18);
        bodyHeight += lines.length * lineHeight + paragraphGap;
        return;
      }
      if (block.type === 'lead') {
        const leadTitle = `❖ ${String(block.text || '').trim()} ❖`;
        const titleLines = wrapText(leadTitle, headingConfigs[3].font, contentW - 110);
        bodyHeight += headingConfigs[3].top + headingConfigs[3].bottom + titleLines.length * headingConfigs[3].lineHeight + 18;
        if (block.subtitle) {
          const subLines = wrapText(String(block.subtitle || '').trim(), bodyFont, contentW - 16);
          bodyHeight += subLines.length * lineHeight + paragraphGap;
        }
        return;
      }
      const cfg = headingConfigs[block.level];
      const lines = wrapText(block.text, cfg.font, cfg.maxWidth);
      bodyHeight += cfg.top + cfg.bottom + lines.length * cfg.lineHeight;
    });

    const reportHeight = Math.max(2600, Math.ceil(frameY + headerH + infoH + boardH + bodyHeight + footerH + 80));
    canvas.width = reportWidth;
    canvas.height = reportHeight;

    const bgGrad = ctx.createLinearGradient(0, 0, 0, reportHeight);
    bgGrad.addColorStop(0, '#f3f0e6');
    bgGrad.addColorStop(1, '#ece6d6');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, reportWidth, reportHeight);

    for (let i = 0; i < 2400; i += 1) {
      const nx = Math.random() * reportWidth;
      const ny = Math.random() * reportHeight;
      const alpha = 0.018 + Math.random() * 0.05;
      ctx.fillStyle = `rgba(115,88,58,${alpha.toFixed(3)})`;
      ctx.fillRect(nx, ny, 1, 1);
    }

    ctx.shadowColor = 'rgba(92,63,35,0.15)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle = '#faf9f6';
    ctx.fillRect(frameX, frameY, frameW, reportHeight - frameY * 2);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    const borderGrad = ctx.createLinearGradient(frameX, frameY, frameX + frameW, frameY + reportHeight * 0.45);
    borderGrad.addColorStop(0, '#d4c5a9');
    borderGrad.addColorStop(0.35, '#8B4513');
    borderGrad.addColorStop(0.7, '#d4c5a9');
    borderGrad.addColorStop(1, '#8B4513');
    ctx.strokeStyle = borderGrad;
    ctx.lineWidth = 4;
    ctx.strokeRect(frameX + 1, frameY + 1, frameW - 2, reportHeight - frameY * 2 - 2);

    let y = frameY + 90;
    ctx.font = headTitleFont;
    ctx.fillStyle = '#5c3a21';
    ctx.textAlign = 'center';
    ctx.fillText('轩辕国学 · AI解盘', reportWidth / 2, y);
    ctx.textAlign = 'left';

    const infoX = contentX;
    const infoY = y + 30;
    const infoW = contentW;
    const infoPadding = 28;
    ctx.fillStyle = '#f0ece1';
    ctx.fillRect(infoX, infoY, infoW, infoH - 22);
    ctx.strokeStyle = '#e6dec5';
    ctx.lineWidth = 2;
    ctx.strokeRect(infoX, infoY, infoW, infoH - 22);
    ctx.strokeStyle = '#8B4513';
    ctx.lineWidth = 2;
    ctx.strokeRect(infoX - 1, infoY - 1, infoW + 2, infoH - 20);

    const questionText = (pan.question || question || '未填写事项').trim() || '未填写事项';
    const situationText = (aiExtraQuestion || '').trim() || '未补充';
    const dateText = pan.timeNodeText || dateTimeValue.replace('T', ' ');
    const typeText = `${pan.mode === 'ke' ? '刻盘' : '时盘'} · ${consultMode === 'remote' ? '远程测' : '当面测'} · ${pan.juText || '-'}`;
    const termText = pan.solarTermText || '-';

    ctx.font = smallLabelFont;
    ctx.fillStyle = '#8B4513';
    ctx.fillText('预测事项', infoX + infoPadding, infoY + 38);
    ctx.font = "700 38px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    ctx.fillStyle = '#0f172a';
    wrapText(questionText, ctx.font, infoW - infoPadding * 2 - 350).slice(0, 2).forEach((line, idx) => {
      ctx.fillText(line, infoX + infoPadding, infoY + 84 + idx * 42);
    });

    ctx.font = "700 22px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    ctx.fillStyle = '#334155';
    ctx.textAlign = 'right';
    ctx.fillText(dateText, infoX + infoW - infoPadding, infoY + 70);
    ctx.font = "500 18px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    ctx.fillStyle = '#64748b';
    ctx.fillText(`${termText} · ${typeText}`, infoX + infoW - infoPadding, infoY + 101);
    ctx.textAlign = 'left';

    ctx.font = smallLabelFont;
    ctx.fillStyle = '#8B4513';
    ctx.fillText('情况', infoX + infoPadding, infoY + 140);
    ctx.font = "600 24px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    ctx.fillStyle = '#334155';
    wrapText(situationText, ctx.font, infoW - infoPadding * 2 - 10).slice(0, 2).forEach((line, idx) => {
      ctx.fillText(line, infoX + infoPadding, infoY + 176 + idx * 34);
    });

    ctx.strokeStyle = '#dcd3c1';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(infoX + infoPadding, infoY + 218);
    ctx.lineTo(infoX + infoW - infoPadding, infoY + 218);
    ctx.stroke();

    const pillarLabels = ['年柱', '月柱', '日柱', pan.mode === 'ke' ? '刻柱' : '时柱'];
    const pillarValues = [pan.yearPillar || '-', pan.monthPillar || '-', pan.dayPillar || '-', pan.mode === 'ke' ? (pan.kePillar || '-') : (pan.hourPillar || '-')];
    const pillarY = infoY + 242;
    const pillarGap = 14;
    const pillarW = Math.floor((infoW - infoPadding * 2 - pillarGap * 3) / 4);
    for (let i = 0; i < 4; i += 1) {
      const px = infoX + infoPadding + i * (pillarW + pillarGap);
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillRect(px, pillarY, pillarW, 82);
      ctx.font = "500 16px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
      ctx.fillStyle = '#8c7b64';
      ctx.textAlign = 'center';
      ctx.fillText(pillarLabels[i], px + pillarW / 2, pillarY + 24);
      ctx.font = valueFont;
      ctx.fillStyle = i === 0 ? '#7f1d1d' : (i === 2 ? '#8B4513' : '#111827');
      ctx.fillText(pillarValues[i], px + pillarW / 2, pillarY + 60);
    }
    ctx.textAlign = 'left';

    const summaryY = pillarY + 112;
    ctx.strokeStyle = '#dcd3c1';
    ctx.beginPath();
    ctx.moveTo(infoX + infoPadding, summaryY - 16);
    ctx.lineTo(infoX + infoW - infoPadding, summaryY - 16);
    ctx.stroke();
    const summaryItems = [
      { label: '旬首', value: pan.xunShou || '-' },
      { label: '值符', value: pan.zhiFu || '-' },
      { label: '值使', value: pan.zhiShi || '-' },
      { label: '马星', value: (pan.maText || '-').replace(/（.*?）/g, '').trim() || '-' },
      { label: '空亡', value: pan.kongText || '-' },
    ];
    const summaryW = Math.floor((infoW - infoPadding * 2) / 5);
    summaryItems.forEach((item, idx) => {
      const sx = infoX + infoPadding + idx * summaryW;
      ctx.font = "500 15px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
      ctx.fillStyle = '#8c7b64';
      ctx.textAlign = 'center';
      ctx.fillText(item.label, sx + summaryW / 2, summaryY + 12);
      ctx.font = "700 24px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
      ctx.fillStyle = idx === 1 ? '#8B4513' : '#1f2937';
      ctx.fillText(item.value, sx + summaryW / 2, summaryY + 48);
    });
    ctx.textAlign = 'left';

    y = infoY + infoH + 20;
    ctx.fillStyle = '#f7efe1';
    ctx.fillRect(contentX, y, contentW, boardH - 24);
    ctx.strokeStyle = '#decfb5';
    ctx.lineWidth = 2;
    ctx.strokeRect(contentX, y, contentW, boardH - 24);

    const boardSize = 396;
    const cellSize = boardSize / 3;
    const boardX = contentX + (contentW - boardSize) / 2;
    const boardY = y + 14;
    const hideBoardTextInExport = true;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(boardX, boardY, boardSize, boardSize);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.strokeRect(boardX, boardY, boardSize, boardSize);
    for (let i = 1; i < 3; i += 1) {
      ctx.beginPath();
      ctx.moveTo(boardX + i * cellSize, boardY);
      ctx.lineTo(boardX + i * cellSize, boardY + boardSize);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(boardX, boardY + i * cellSize);
      ctx.lineTo(boardX + boardSize, boardY + i * cellSize);
      ctx.stroke();
    }
    if (hideBoardTextInExport) {
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(boardX + cellSize + 1, boardY + cellSize + 1, cellSize - 2, cellSize - 2);
    }

    const drawStemLine = (
      position: number,
      stemText: string,
      startX: number,
      baselineY: number,
      options?: { forceXing?: boolean; forceTombStem?: string | null },
    ) => {
      const stems = splitStems(stemText);
      const drawChars = stems.length > 0 ? stems : (stemText ? [stemText] : ['-']);
      let xCursor = startX;
      drawChars.forEach((stem) => {
        const hasXing = Boolean(options?.forceXing) || hasJiXingByStem(position, stem);
        const inTomb = options?.forceTombStem === stem || isStemInTomb(stem, PALACE_BRANCHES[position] || []);
        ctx.fillStyle = hasXing && inTomb
          ? '#0284c7'
          : hasXing
            ? '#2563eb'
            : inTomb
              ? '#a16207'
              : '#0f172a';
        ctx.fillText(stem, xCursor, baselineY);
        xCursor += 24;
      });
    };

    if (!hideBoardTextInExport) {
      GRID_POSITIONS.forEach((position, index) => {
        const row = Math.floor(index / 3);
        const col = index % 3;
        const cellX = boardX + col * cellSize;
        const cellY = boardY + row * cellSize;
        const isCenter = position === 5;
        const cell = pan.palaces.get(position) || emptyPalace(position);
        const forceTombStem = getForcedTombStemForZhiFuKun(cell);
        if (isCenter) {
          ctx.fillStyle = '#f8fafc';
          ctx.fillRect(cellX + 1, cellY + 1, cellSize - 2, cellSize - 2);
          return;
        }

        if (cell.hasVoid) {
          ctx.font = "500 24px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
          ctx.fillStyle = '#64748b';
          ctx.fillText('○', cellX + cellSize - 28, cellY + 26);
        }

        ctx.font = "500 32px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
        ctx.fillStyle = '#0f172a';
        ctx.fillText(cell.deitySymbol || '-', cellX + 12, cellY + 34);

        ctx.font = "500 32px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
        drawStemLine(position, cell.heavenlyStem, cellX + 12, cellY + cellSize * 0.52, { forceTombStem });
        ctx.fillStyle = '#0f172a';
        ctx.fillText(cell.starSymbol || '-', cellX + cellSize - 34, cellY + cellSize * 0.52);

        ctx.font = "500 32px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
        drawStemLine(position, cell.earthlyStem, cellX + 12, cellY + cellSize - 12, {
          forceXing: cell.earthlyHasXingByFuYin,
          forceTombStem,
        });
        ctx.fillStyle = cell.hasMenPo ? '#dc2626' : '#0f172a';
        ctx.fillText(cell.gateSymbol || '-', cellX + cellSize - 34, cellY + cellSize - 12);
      });

      const outerLeadPos: Record<number, { x: number; y: number; align: CanvasTextAlign }> = {
        9: { x: boardX + boardSize / 2, y: boardY - 20, align: 'center' },
        4: { x: boardX - 30, y: boardY + cellSize * 0.5, align: 'right' },
        3: { x: boardX - 30, y: boardY + cellSize * 1.5, align: 'right' },
        8: { x: boardX - 30, y: boardY + cellSize * 2.5, align: 'right' },
        2: { x: boardX + boardSize + 30, y: boardY + cellSize * 0.5, align: 'left' },
        7: { x: boardX + boardSize + 30, y: boardY + cellSize * 1.5, align: 'left' },
        6: { x: boardX + boardSize + 30, y: boardY + cellSize * 2.5, align: 'left' },
        1: { x: boardX + boardSize / 2, y: boardY + boardSize + 34, align: 'center' },
      };
      ctx.font = "500 32px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
      Object.entries(outerLeadPos).forEach(([posText, item]) => {
        const pos = Number(posText);
        const leadStem = pan.palaces.get(pos)?.leadStem || '';
        if (!leadStem) return;
        ctx.textAlign = item.align;
        ctx.fillStyle = '#0f172a';
        ctx.fillText(leadStem, item.x, item.y);
      });
      ctx.textAlign = 'left';

      const horsePosMap: Record<number, { x: number; y: number }> = {
        4: { x: boardX - 36, y: boardY - 16 },
        2: { x: boardX + boardSize + 24, y: boardY - 16 },
        8: { x: boardX - 36, y: boardY + boardSize + 34 },
        6: { x: boardX + boardSize + 24, y: boardY + boardSize + 34 },
      };
      if (horsePosMap[pan.horsePosition]) {
        ctx.font = "700 30px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
        ctx.fillStyle = '#dc2626';
        ctx.fillText('马', horsePosMap[pan.horsePosition].x, horsePosMap[pan.horsePosition].y);
      }
    }

    y += boardH + 12;
    const drawHeading = (level: 1 | 2 | 3 | 4, text: string) => {
      const cfg = headingConfigs[level];
      const title = simplifyDisplayHeadingText(text);
      const lines = wrapText(title, cfg.font, cfg.maxWidth);
      y += cfg.top;
      if (level === 1) {
        ctx.fillStyle = '#f7efe1';
        ctx.fillRect(contentX + 10, y - 14, contentW - 20, lines.length * cfg.lineHeight + 28);
        ctx.strokeStyle = '#d7c7aa';
        ctx.lineWidth = 2;
        ctx.strokeRect(contentX + 10, y - 14, contentW - 20, lines.length * cfg.lineHeight + 28);
        ctx.font = cfg.font;
        ctx.fillStyle = '#5c3a21';
        ctx.textAlign = 'center';
        lines.forEach((line, idx) => {
          ctx.fillText(line, contentX + contentW / 2, y + idx * cfg.lineHeight + 38);
        });
        ctx.textAlign = 'left';
      } else if (level === 2) {
        ctx.fillStyle = '#f8f0e0';
        ctx.fillRect(contentX + 10, y - 10, contentW - 20, lines.length * cfg.lineHeight + 20);
        ctx.strokeStyle = '#dbc9a9';
        ctx.lineWidth = 2;
        ctx.strokeRect(contentX + 10, y - 10, contentW - 20, lines.length * cfg.lineHeight + 20);
        ctx.font = cfg.font;
        ctx.fillStyle = '#603818';
        lines.forEach((line, idx) => {
          ctx.fillText(line, contentX + 30, y + idx * cfg.lineHeight + 32);
        });
      } else if (level === 3) {
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(contentX + 10, y + 4, 7, lines.length * cfg.lineHeight - 6);
        ctx.font = cfg.font;
        ctx.fillStyle = '#6a3f1f';
        lines.forEach((line, idx) => {
          ctx.fillText(line, contentX + 30, y + idx * cfg.lineHeight + 36);
        });
      } else {
        ctx.fillStyle = '#f8f1e5';
        ctx.fillRect(contentX + 10, y - 6, contentW - 20, lines.length * cfg.lineHeight + 10);
        ctx.strokeStyle = '#c79b69';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(contentX + 10, y - 6, contentW - 20, lines.length * cfg.lineHeight + 10);
        ctx.font = cfg.font;
        ctx.fillStyle = '#87603e';
        lines.forEach((line, idx) => {
          ctx.fillText(line, contentX + 26, y + idx * cfg.lineHeight + 28);
        });
      }
      y += lines.length * cfg.lineHeight + cfg.bottom;
    };

    blocks.forEach((block) => {
      if (block.type === 'space') {
        y += 16;
        return;
      }
      if (block.type === 'heading') {
        drawHeading(block.level, block.text);
        return;
      }
      if (block.type === 'lead') {
        drawHeading(3, `❖ ${block.text} ❖`);
        if (block.subtitle) {
          ctx.font = bodyFont;
          ctx.fillStyle = '#1f2937';
          wrapText(block.subtitle, bodyFont, contentW - 16).forEach((line) => {
            ctx.fillText(line, contentX, y);
            y += lineHeight;
          });
          y += paragraphGap;
        }
        return;
      }
      ctx.font = bodyFont;
      ctx.fillStyle = '#1f2937';
      wrapText(block.text, bodyFont, contentW - 16).forEach((line) => {
        ctx.fillText(line, contentX, y);
        y += lineHeight;
      });
      y += paragraphGap;
    });

    const footerTop = reportHeight - footerH;
    ctx.strokeStyle = '#d4c5a9';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(contentX, footerTop);
    ctx.lineTo(contentX + contentW, footerTop);
    ctx.stroke();

    const qrText = 'http://qimen.aifashou.cn';
    let qrDrawn = false;
    const qrSize = 126;
    try {
      const qrApi = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&color=5c3a21&data=${encodeURIComponent(qrText)}`;
      const qrResp = await fetch(qrApi);
      if (qrResp.ok) {
        const qrBlob = await qrResp.blob();
        const qrObjUrl = URL.createObjectURL(qrBlob);
        const qrImage = new Image();
        await new Promise<void>((resolve, reject) => {
          qrImage.onload = () => resolve();
          qrImage.onerror = () => reject(new Error('qr-load-failed'));
          qrImage.src = qrObjUrl;
        });
        const qrX = contentX + contentW - qrSize - 8;
        const qrY = footerTop + 34;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
        ctx.strokeStyle = '#d4c5a9';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
        ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
        URL.revokeObjectURL(qrObjUrl);
        qrDrawn = true;
      }
    } catch {
      qrDrawn = false;
    }

    ctx.font = "500 20px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    ctx.fillStyle = '#334155';
    ctx.fillText('本结果由轩辕国学AI奇门解读，仅供参考，请相信科学。', contentX, footerTop + 68);
    ctx.font = "600 16px 'Noto Serif SC','STSong','Songti SC','SimSun',serif";
    ctx.fillStyle = '#8B4513';
    ctx.fillText('qimen.aifashou.cn', contentX, footerTop + 108);
    if (!qrDrawn) {
      ctx.fillStyle = '#8B4513';
      ctx.fillText('二维码加载失败，请直接访问网站。', contentX, footerTop + 142);
    }

    const link = document.createElement('a');
    link.download = `轩辕国学AI解析-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const renderPanExtensionButtons = (): React.ReactNode => {
    const buttonClass = 'inline-flex min-h-[36px] items-center justify-center rounded-[11px] border border-[#d6be97] bg-[linear-gradient(180deg,#bf9554_0%,#ae7f3d_100%)] px-1 py-1 text-center text-[11px] font-medium tracking-[0.01em] text-[#fffaf0] transition duration-200 hover:brightness-[1.03] active:scale-[0.98] sm:min-h-[38px] sm:text-[12px]';
    const splitButtonClass = 'overflow-hidden rounded-[11px] border border-[#d6be97] bg-[linear-gradient(180deg,#bf9554_0%,#ae7f3d_100%)]';
    return (
      <section className="mx-auto w-full max-w-[360px] sm:max-w-[420px]">
        <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
          {PAN_EXTENSION_PRIMARY_BUTTONS.map((label) => (
            <button
              key={label}
              type="button"
              onClick={showFeatureDevelopingNotice}
              className={buttonClass}
            >
              <span className="whitespace-nowrap">{label}</span>
            </button>
          ))}
          <div className={splitButtonClass}>
            <div className="grid grid-cols-2">
              <button
                type="button"
                onClick={() => { void handleSwitchJu('prev'); }}
                disabled={loading || aiLoading}
                className="inline-flex min-h-[36px] items-center justify-center px-1 py-1 text-[11px] font-medium text-[#fffaf0] transition hover:bg-white/8 disabled:cursor-not-allowed disabled:opacity-55 sm:min-h-[38px] sm:text-[12px]"
              >
                上局
              </button>
              <button
                type="button"
                onClick={() => { void handleSwitchJu('next'); }}
                disabled={loading || aiLoading}
                className="inline-flex min-h-[36px] items-center justify-center border-l border-white/30 px-1 py-1 text-[11px] font-medium text-[#fffaf0] transition hover:bg-white/8 disabled:cursor-not-allowed disabled:opacity-55 sm:min-h-[38px] sm:text-[12px]"
              >
                下局
              </button>
            </div>
          </div>
          {PAN_EXTENSION_SECONDARY_BUTTONS.map((label) => (
            <button
              key={label}
              type="button"
              onClick={showFeatureDevelopingNotice}
              className={buttonClass}
            >
              <span className="whitespace-nowrap">{label}</span>
            </button>
          ))}
        </div>
      </section>
    );
  };

  const renderAiPanelSection = (placement: 'desktop' | 'mobile'): React.ReactNode => {
    if (!showAiPanel) return null;
    const isMobilePlacement = placement === 'mobile';
    const sectionClass = isMobilePlacement ? 'mt-4 w-full max-w-3xl mx-auto pb-24' : 'mt-3';
    const contentClass = hasAiReport
      ? (isMobilePlacement
        ? 'flex-1 overflow-y-auto py-3 sm:px-4 sm:py-4'
        : 'flex-1 overflow-y-auto -mx-3 py-3 sm:mx-0 sm:px-4 sm:py-4')
      : 'px-3 py-3 sm:px-4 sm:py-4';

    return (
      <section ref={aiPanelContainerRef} className={sectionClass}>
        <div className="no-edge-back w-full">
          <div className={contentClass}>
            <div className="space-y-3">
              {!hasAiReport && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                  <p className="text-sm text-slate-600">
                    点击下方按钮填写事项后开始解析。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (!ensureAiMemberAccess()) return;
                      setShowAiQuestionModal(true);
                    }}
                    disabled={aiLoading}
                    className="mt-3 inline-flex min-w-[160px] items-center justify-center rounded-xl bg-[#8b6b3d] px-5 py-2.5 text-sm text-white hover:bg-[#785a31] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {aiLoading ? 'AI 解析中...' : '填写事项并开始解析'}
                  </button>
                </div>
              )}
              {aiError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                  <p className="text-sm text-rose-700">{aiError}</p>
                  {!aiLoading && (
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={() => { void handleRegenerateAiReport(); }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50 sm:text-sm"
                      >
                        重新生成解读
                      </button>
                    </div>
                  )}
                </div>
              )}
              {hasAiReport && (
                <div className="space-y-3">
                  <div
                    ref={aiReportRef}
                    className="relative overflow-hidden bg-[#faf9f6]"
                    style={reportPaperStyle}
                  >
                    <div className="pointer-events-none absolute inset-0" style={reportTextureStyle} />
                    <div className="relative z-[1] px-4 pt-4 sm:px-7 sm:pt-5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          {aiLoading && aiResponseMode === 'stream' && (
                            <div className="inline-flex items-center gap-1 rounded-full border border-[#d1b889] bg-[#f6ead3] px-2 py-[3px] text-[11px] text-[#8b6b3d]">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#8b6b3d] animate-pulse" />
                              流式输出中…
                              {aiFirstSentenceSeconds != null && (
                                <span className="ml-1 text-[10px] text-[#7b5a2f]">首句 {aiFirstSentenceSeconds.toFixed(1)}s</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="inline-flex items-center rounded-full border border-[#d1b889] bg-[#f6ead3] p-[2px]">
                          <button
                            onClick={() => setAiExplainMode('direct')}
                            className={`rounded-full px-2 py-[3px] text-[11px] leading-none transition-colors ${
                              aiExplainMode === 'direct'
                                ? 'bg-[#8b6b3d] text-white'
                                : 'text-[#8b6b3d] hover:bg-[#ead7b3]'
                            }`}
                          >
                            直断
                          </button>
                          <button
                            onClick={() => setAiExplainMode('detail')}
                            className={`rounded-full px-2 py-[3px] text-[11px] leading-none transition-colors ${
                              aiExplainMode === 'detail'
                                ? 'bg-[#8b6b3d] text-white'
                                : 'text-[#8b6b3d] hover:bg-[#ead7b3]'
                            }`}
                          >
                            详解
                          </button>
                        </div>
                      </div>

                      <div className="relative mt-2 border border-[#e6dec5] bg-[#f0ece1] p-3 sm:p-5">
                        <div className="pointer-events-none absolute -left-px -top-px h-3.5 w-3.5 border-l-2 border-t-2 border-[#8B4513]" />
                        <div className="pointer-events-none absolute -right-px -top-px h-3.5 w-3.5 border-r-2 border-t-2 border-[#8B4513]" />
                        <div className="pointer-events-none absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2 border-[#8B4513]" />
                        <div className="pointer-events-none absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2 border-[#8B4513]" />

                        <div className="border-b border-[#dcd3c1] pb-3 sm:flex sm:items-end sm:justify-between sm:pb-4">
                          <div className="min-w-0">
                            <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-[#8B4513]">预测事项</div>
                            <div className="text-lg font-bold text-slate-900 sm:text-[22px]">{reportQuestionText}</div>
                          </div>
                          <div className="mt-2 text-left sm:mt-0 sm:text-right">
                            <div className="text-sm font-bold text-slate-700">{reportDateText}</div>
                            <div className="text-xs text-slate-500">{reportTermText} · {reportTypeText}</div>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
                          {pillarItems.map((item, index) => (
                            <div key={`report-pillar-${item.label}`} className="rounded bg-white/55 p-2">
                              <div className="mb-0.5 text-[11px] text-[#8c7b64]">{item.label}</div>
                              <div className={`text-base font-bold sm:text-lg ${index === 0 ? 'text-red-900' : index === 2 ? 'text-[#8B4513]' : 'text-[#2c2c2c]'}`}>{item.value}</div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-3 border-t border-[#dcd3c1] pt-3">
                          <div className="grid grid-cols-5 gap-1 text-center">
                            {[
                              { label: '旬首', value: pan?.xunShou || '-' },
                              { label: '值符', value: pan?.zhiFu || '-' },
                              { label: '值使', value: pan?.zhiShi || '-' },
                              { label: '马星', value: (pan?.maText || '-').replace(/（.*?）/g, '').trim() || '-' },
                              { label: '空亡', value: pan?.kongText || '-' },
                            ].map((item, idx) => (
                              <div key={`report-summary-${item.label}`}>
                                <div className="text-[11px] text-[#8c7b64]">{item.label}</div>
                                <div className={`text-sm font-bold sm:text-base ${idx === 1 ? 'text-[#8B4513]' : 'text-[#2c2c2c]'}`}>{item.value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="relative z-[1] space-y-6 px-4 py-6 sm:px-7 sm:py-7">
                      {aiReport.trim() && (
                        <div className="text-[16px] sm:text-[18px]" style={{ fontFamily: "'Noto Serif SC','STSong','Songti SC','SimSun',serif" }}>
                          <PanMarkdownRenderer content={displayedAiReport} />
                        </div>
                      )}
                      {aiFollowupHistory.map((item, idx) => {
                        const ansBase = normalizeAiReportLayoutText(item.a);
                        const ansDisplay = aiExplainMode === 'direct'
                          ? stripBracketExplanationsForDirect(normalizeAiReportLayoutText(stripInternalThinkingForDirect(ansBase)))
                          : ansBase;
                        return (
                          <div key={`followup-${idx}`} className="border-t border-[#e6dec5] pt-4">
                            <p className="mb-2 text-base font-semibold text-[#8B4513] sm:text-[18px]">追问 {idx + 1}：{item.q}</p>
                            <div className="text-[16px] sm:text-[18px]" style={{ fontFamily: "'Noto Serif SC','STSong','Songti SC','SimSun',serif" }}>
                              <PanMarkdownRenderer content={ansDisplay} />
                            </div>
                          </div>
                        );
                      })}
                      {aiFollowupStreaming && (
                        <div className="border-t border-[#e6dec5] pt-4">
                          <p className="mb-2 text-base font-semibold text-[#8B4513] sm:text-[18px]">追问 {aiFollowupHistory.length + 1}：{aiExtraQuestion || '...'}</p>
                          <div className="text-[16px] sm:text-[18px]" style={{ fontFamily: "'Noto Serif SC','STSong','Songti SC','SimSun',serif" }}>
                            <PanMarkdownRenderer content={displayedFollowupStreaming} />
                          </div>
                        </div>
                      )}
                      <div className="space-y-2 text-center">
                        <p className="mx-auto max-w-3xl text-xs leading-6 text-[#7c6140] sm:text-sm">
                          要继续细化时间点、人物或环境点位，可以直接追问。
                        </p>
                        <button
                          onClick={() => setShowSolutionModal(true)}
                          className="inline-flex min-w-[180px] items-center justify-center rounded-xl border border-[#b68545] bg-gradient-to-r from-[#8b5a2b] to-[#aa7330] px-7 py-2.5 text-base font-bold tracking-[0.02em] text-[#fff8eb] shadow-[0_8px_20px_rgba(91,55,18,0.28)] transition hover:from-[#7a4d23] hover:to-[#9a672b]"
                        >
                          解决方案
                        </button>
                      </div>
                    </div>

                    <div className="relative z-[1] border-t border-[#d4c5a9] px-4 py-3 text-center sm:px-7 sm:py-4">
                      <p className="text-xs text-[#8B4513]/75">本结果由轩辕AI奇门解读，仅供参考，请相信科学。</p>
                    </div>
                  </div>

                  <div className="mt-1 grid w-full grid-cols-4 gap-2">
                    <button
                      onClick={() => setShowFollowupComposer((prev) => !prev)}
                      disabled={aiLoading}
                      className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-lg border border-[#7b4b1f] bg-[#8b5a2b] px-2 py-1.5 text-[11px] font-semibold text-white hover:bg-[#734721] sm:text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {showFollowupComposer ? '收起追问' : '继续追问'}
                    </button>
                    <button
                      onClick={() => { void copyAiReportPlainText(); }}
                      disabled={!aiPlainText}
                      className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-lg border border-[#c9b086] px-2 py-1.5 text-[11px] text-[#6f5532] hover:bg-[#f3e7cf] sm:text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      复制
                    </button>
                    <button
                      onClick={() => { void shareAiReportText(); }}
                      className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-lg border border-[#c9b086] px-2 py-1.5 text-[11px] text-[#6f5532] hover:bg-[#f3e7cf] sm:text-sm"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      分享
                    </button>
                    <button
                      onClick={downloadAiReportImage}
                      className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-lg border border-[#c9b086] px-2 py-1.5 text-[11px] text-[#6f5532] hover:bg-[#f3e7cf] sm:text-sm"
                    >
                      <Download className="h-3.5 w-3.5" />
                      下载
                    </button>
                  </div>

                  {showFollowupComposer && (
                    <div className="rounded-xl border border-[#d7c7aa] bg-[#fbf6ea] p-3 sm:p-4">
                      <div className="space-y-2">
                        <textarea
                          value={aiExtraQuestion}
                          onChange={(e) => setAiExtraQuestion(e.target.value)}
                          placeholder="继续补充你的问题"
                          className="min-h-[84px] w-full rounded-lg border border-[#cdb78f] bg-[#fbf6ea] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#8b6b3d]"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { void runAiInterpretation(); }}
                            disabled={aiLoading}
                            className="rounded-lg bg-[#8b6b3d] px-4 py-1.5 text-sm text-white hover:bg-[#785a31] disabled:opacity-60"
                          >
                            {aiLoading ? '解析中...' : '发送追问'}
                          </button>
                          <button
                            onClick={() => setShowFollowupComposer(false)}
                            className="rounded-lg border border-[#cdb78f] px-4 py-1.5 text-sm text-[#7d6641]"
                          >
                            收起
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {isAdminMember && (
                    <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 p-3 sm:p-4">
                      <div className="mb-2 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-indigo-600" />
                        <span className="font-semibold text-indigo-900">提示词优化建议（管理员）</span>
                      </div>
                      <p className="mb-3 text-xs text-indigo-700">
                        可提交优化建议并查看具体改动点，确认后可直接保存到后端 AI 提示词配置。
                      </p>
                      <textarea
                        value={promptOptimization}
                        onChange={(e) => setPromptOptimization(e.target.value)}
                        placeholder="请输入优化建议，例如：重点分析事业合作、侧重财务运势、关注时间节点等"
                        className="mb-3 min-h-[80px] w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {optimizationError && (
                        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                          {optimizationError}
                        </div>
                      )}
                      {optimizationSaveNotice && (
                        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                          {optimizationSaveNotice}
                        </div>
                      )}
                      {optimizedPrompt && (
                        <div className="mb-3 rounded-lg border border-indigo-200 bg-white p-3">
                          <div className="mb-1 text-xs font-semibold text-indigo-800">优化后的提示词：</div>
                          <div className="max-h-[140px] overflow-y-auto whitespace-pre-wrap text-xs text-slate-700">
                            {optimizedPrompt}
                          </div>
                        </div>
                      )}
                      {optimizationChangeNotes.length > 0 && (
                        <div className="mb-3 rounded-lg border border-indigo-200 bg-white p-3">
                          <div className="mb-1 text-xs font-semibold text-indigo-800">本次改动点：</div>
                          <ol className="list-decimal space-y-1 pl-4 text-xs text-slate-700">
                            {optimizationChangeNotes.map((note, idx) => (
                              <li key={`opt-note-${idx}`}>{note}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => { void handlePromptOptimization(); }}
                          disabled={optimizationLoading || optimizationSaving || !promptOptimization.trim()}
                          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {optimizationLoading ? '优化中...' : '提交优化'}
                        </button>
                        <button
                          onClick={() => { void handleSaveOptimizedPrompt(); }}
                          disabled={optimizationLoading || optimizationSaving || !optimizedPrompt.trim()}
                          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {optimizationSaving ? '保存中...' : '保存到后端提示词'}
                        </button>
                        <button
                          onClick={() => { void handleRepeatTestWithOptimizedPrompt(); }}
                          disabled={aiLoading || !optimizedPrompt.trim()}
                          className="rounded-lg border border-indigo-300 px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {aiLoading ? '测试中...' : '重复测试'}
                        </button>
                        {(optimizedPrompt || optimizationError || optimizationSaveNotice || optimizationChangeNotes.length > 0) && (
                          <button
                            onClick={() => {
                              setPromptOptimization('');
                              setOptimizedPrompt('');
                              setOptimizationChangeNotes([]);
                              setOptimizationError('');
                              setOptimizationSaveNotice('');
                            }}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-100"
                          >
                            清空
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const showResultHeaderShare = Boolean(pan && !showConfig);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="bg-gradient-to-b from-[#a27a45] via-[#b38750] to-[#c79a61] px-4 pt-4 pb-20 border-b border-[#d9ccb7]">
        <div className="max-w-5xl mx-auto text-white">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
            <button
              onClick={handleHeaderBack}
              className="p-2 rounded-full hover:bg-white/15 transition-colors"
              data-qimen-internal-back="pan"
              aria-label="返回"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <h1 className="flex-1 min-w-0 px-1 text-center text-[clamp(16px,4.8vw,22px)] font-extrabold leading-tight tracking-[0.02em] break-keep whitespace-normal">轩辕国学 · 奇门排盘</h1>
            {showResultHeaderShare ? (
              <button
                onClick={() => { void handleShare(); }}
                className="inline-flex h-8 items-center gap-1 rounded-full bg-white/90 text-slate-700 px-2.5 text-xs sm:text-sm font-medium"
              >
                <Share2 className="w-3.5 h-3.5" />
                分享
              </button>
            ) : (
              <div className="w-10 sm:w-11" />
            )}
          </div>
          {(!pan || showConfig) && (
            <div className="mt-2 flex justify-end gap-2">
              <div className="inline-flex h-8 items-center gap-1 rounded-full bg-white/90 px-2 text-slate-700">
                <button
                  onClick={() => setUiMode('simple')}
                  className={`text-xs px-1 ${uiMode === 'simple' ? 'text-slate-900' : 'text-slate-500'}`}
                >
                  普通
                </button>
                <button
                  onClick={() => setUiMode((prev) => (prev === 'simple' ? 'pro' : 'simple'))}
                  className={`relative h-6 w-11 rounded-full transition-colors ${uiMode === 'pro' ? 'bg-[#8b6b3d]' : 'bg-slate-300'}`}
                  aria-label="切换普通/专业模式"
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                      uiMode === 'pro' ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <button
                  onClick={() => setUiMode('pro')}
                  className={`text-xs px-1 ${uiMode === 'pro' ? 'text-slate-900' : 'text-slate-500'}`}
                >
                  专业
                </button>
              </div>
              <button
                onClick={() => { void handleShare(); }}
                className="inline-flex h-8 items-center gap-1 rounded-full bg-white/90 text-slate-700 px-2.5 text-xs sm:text-sm font-medium"
              >
                <Share2 className="w-3.5 h-3.5" />
                分享
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="-mt-16 pb-24">
        <div className="mx-auto max-w-[1480px] px-3 sm:px-4 space-y-4">
          {(!pan || showConfig) && (
            uiMode === 'simple' ? (
              <section className="bg-white rounded-2xl border border-slate-200 shadow-none overflow-hidden">
                <div className="p-3 sm:p-4">
                  <p className="text-sm sm:text-base leading-7 text-slate-700">
                    请先在心里诚心想着你的问题，然后把问题写在下方，再点击“AI解盘”。
                  </p>

                  <div className="mt-4 space-y-3">
                    <textarea
                      ref={questionTextareaRef}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder="请输入你要问的事项（建议一句话）"
                      className="w-full min-h-[102px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm sm:text-base outline-none focus:ring-2 focus:ring-[#8b6b3d] text-slate-700"
                    />

                    <button
                      onClick={() => { void startSimpleAiPan(); }}
                      disabled={loading || aiLoading}
                      className="w-full py-2.5 rounded-xl bg-[#8b6b3d] text-white text-sm sm:text-base font-normal hover:bg-[#785a31] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? '排盘中...' : aiLoading ? '解读中...' : 'AI 解盘'}
                    </button>

                    <div className="flex items-center justify-between text-xs sm:text-sm lg:text-base">
                      <button
                        onClick={openHistory}
                        className="text-[#8b6b3d] underline underline-offset-2"
                      >
                        排盘记录
                      </button>
                      <span className="text-slate-600">当前时间：{dateTimeValue.replace('T', ' ')}</span>
                    </div>
                  </div>

                  {notice && (
                    <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                      {notice}
                    </div>
                  )}
                  {error && (
                    <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                      {error}
                    </div>
                  )}
                </div>
              </section>
            ) : (
              <section className="bg-white rounded-2xl border border-slate-200 shadow-none overflow-hidden w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto">
                <div className="p-3 sm:p-4">
                  <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden">
                    <div className="flex flex-row items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className={rowLabelClass}>事项内容</div>
                      <input
                        ref={questionInputRef}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="请输入事项（仅填写后自动保存记录）"
                        className="min-w-0 w-full sm:flex-1 text-sm sm:text-base bg-transparent outline-none text-slate-700 placeholder:text-slate-400"
                      />
                    </div>

                    <div className="flex flex-row items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className={rowLabelClass}>排盘时间</div>
                      <div className="min-w-0 w-full sm:flex-1 flex items-center justify-between sm:justify-end gap-2">
                        <input
                          type="datetime-local"
                          value={dateTimeValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDateTimeValue(value);
                            const parsed = parseDateTimeLocal(value);
                            if (parsed) {
                              setKeIndex(Math.floor(parsed.getMinutes() / 10));
                            }
                          }}
                          className="w-full sm:max-w-[300px] text-left sm:text-right text-sm sm:text-base bg-transparent outline-none"
                        />
                        <button
                          onClick={() => {
                            const now = new Date();
                            setDateTimeValue(toDateTimeLocalValue(now));
                            setKeIndex(Math.floor(now.getMinutes() / 10));
                          }}
                          className="text-slate-400 text-base sm:text-lg"
                          aria-label="设置为当前时间"
                        >
                          ⟳
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-row items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className={rowLabelClass}>排盘类型</div>
                      <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm sm:text-base">
                        <button
                          onClick={() => setError('年盘暂未开放，请先使用时盘或刻盘。')}
                          className="inline-flex items-center gap-1 sm:gap-1.5 text-slate-400 whitespace-nowrap shrink-0 py-1"
                        >
                          <span className={optionCircleClass(false)}>✓</span>
                          <span className="whitespace-nowrap">年盘</span>
                        </button>
                        <button
                          onClick={() => setPanMode('hour')}
                          className="inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0 py-1"
                        >
                          <span className={optionCircleClass(panMode === 'hour')}>✓</span>
                          <span className="whitespace-nowrap">时盘</span>
                        </button>
                        <button
                          onClick={() => setPanMode('ke')}
                          className="inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0 py-1"
                        >
                          <span className={optionCircleClass(panMode === 'ke')}>✓</span>
                          <span className="whitespace-nowrap">刻盘</span>
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-row items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className={rowLabelClass}>问测方式</div>
                      <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm sm:text-base">
                        <button
                          onClick={() => setConsultMode('face')}
                          className="inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0 py-1"
                        >
                          <span className={optionCircleClass(consultMode === 'face')}>✓</span>
                          <span className="whitespace-nowrap">当面测</span>
                        </button>
                        <button
                          onClick={() => setConsultMode('remote')}
                          className="inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0 py-1"
                        >
                          <span className={optionCircleClass(consultMode === 'remote')}>✓</span>
                          <span className="whitespace-nowrap">远程测</span>
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-row items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className={rowLabelClass}>自选局数</div>
                      <div className="min-w-0 flex-1 flex items-center">
                        <select
                          value={manualJu}
                          onChange={(e) => setManualJu(e.target.value)}
                          className="w-auto min-w-[140px] text-sm sm:text-base rounded-lg border border-slate-300 bg-white px-3 py-1.5 sm:py-2 outline-none"
                        >
                          <option value="auto">自动定局</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((ju) => (
                            <option key={ju} value={String(ju)}>自选 {ju} 局</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex flex-row items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className={rowLabelClass}>时间类型</div>
                      <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm sm:text-base">
                        <button
                          onClick={() => { void toggleTrueSolar(); }}
                          className="inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0 py-1"
                        >
                          <span className={optionCircleClass(useTrueSolarTime)}>✓</span>
                          <span className="whitespace-nowrap">真太阳时</span>
                        </button>
                        <button
                          onClick={() => { void requestGeoLocation(); }}
                          disabled={geoLoading}
                          className="inline-flex items-center justify-center whitespace-nowrap shrink-0 px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 text-xs sm:text-sm hover:bg-slate-50 disabled:opacity-60 ml-2"
                        >
                          <span className="whitespace-nowrap">{geoLoading ? '定位中...' : '获取定位'}</span>
                        </button>
                        {(geoLocation || (useTrueSolarTime && trueSolarInfo)) && (
                          <span className="text-xs text-slate-500 w-full whitespace-nowrap truncate">
                            {geoLocation && `纬${geoLocation.lat.toFixed(2)} 经${geoLocation.lng.toFixed(2)}`}
                            {geoLocation && useTrueSolarTime && trueSolarInfo && ' · '}
                            {useTrueSolarTime && trueSolarInfo && `真太阳时 +${trueSolarInfo.deltaMinutes.toFixed(0)}分`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 mt-4">
                    <button
                      onClick={() => { void submitPan(); }}
                      disabled={loading}
                      className="w-full py-2.5 rounded-xl bg-[#8b6b3d] text-white text-sm sm:text-base font-normal hover:bg-[#785a31] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? '排盘中...' : '开始排盘'}
                    </button>
                    <button
                      onClick={openHistory}
                      className="w-full py-2.5 rounded-xl bg-[#8b6b3d] text-white text-sm sm:text-base font-normal hover:bg-[#785a31]"
                    >
                      排盘记录
                    </button>
                  </div>

                  {panMode === 'ke' && (
                    <p className="mt-3 text-sm sm:text-base text-slate-500">当前刻位：{KE_OPTIONS[Math.max(0, Math.min(5, Number(keIndex) || 0))]?.label || '-'}</p>
                  )}

                  {notice && (
                    <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                      {notice}
                    </div>
                  )}
                  {error && (
                    <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                      {error}
                    </div>
                  )}
                </div>
              </section>
            )
          )}

          {pan && (
            <div className={`${uiMode === 'simple' ? 'flex flex-col gap-4' : 'space-y-4'} ${isMobileViewport && !showMobileXiangYiPage ? 'pb-20' : ''}`}>
              {launchParams.fromWence && !showConfig && (
                <section className="w-full max-w-3xl mx-auto">
                  <button
                    onClick={onReturnToWence}
                    disabled={!onReturnToWence}
                    className="inline-flex items-center gap-2 rounded-full border border-[#c59b64] bg-[#fff7ea] px-4 py-2 text-sm font-medium text-[#7b5525] shadow-sm hover:bg-[#fff2de] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" />
                    返回问测应用继续解读
                  </button>
                </section>
              )}
              {!showMobileXiangYiPage && (
              <section ref={resultRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden w-full max-w-[1180px] mx-auto">
                <div className="grid grid-cols-[78px_1fr] sm:grid-cols-[88px_1fr_88px_1fr]">
                  <div className={topLabelClass}>事项</div>
                  <div className={`${topValueClass} sm:col-span-3`}>{pan.question || '-'}</div>

                  <div className={topLabelClass}>日期</div>
                  <div className={`${topValueClass} sm:col-span-3`}>{pan.timeNodeText}</div>

                  <div className={topLabelClass}>节气</div>
                  <div className={`${topValueClass} sm:col-span-3`}>{pan.solarTermText || '-'}</div>

                  <div className={topLabelClass}>类型</div>
                  <div className={`${topValueClass} sm:col-span-3`}>
                    {pan.mode === 'ke' ? '刻盘' : '时盘'}·{consultMode === 'remote' ? '远程测' : '当面测'}·{pan.juText}
                  </div>

                  {trueSolarInfo && (
                    <>
                      <div className={topLabelClass}>真太阳时</div>
                      <div className={`${topValueClass} sm:col-span-3`}>{trueSolarInfo.adjustedDateText}</div>
                    </>
                  )}
                </div>

                <div>
                  <div className="grid grid-cols-[78px_1fr_1fr_1fr_1fr] sm:grid-cols-[88px_1fr_1fr_1fr_1fr]">
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center font-bold text-[#9c5b31] bg-[#fefbec] border-b border-r border-slate-200 ${summaryTextClass}`}>旬首</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center font-bold text-[#9c5b31] bg-[#fefbec] border-b border-r border-slate-200 ${summaryTextClass}`}>值符</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center font-bold text-[#9c5b31] bg-[#fefbec] border-b border-r border-slate-200 ${summaryTextClass}`}>值使</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center font-bold text-[#9c5b31] bg-[#fefbec] border-b border-r border-slate-200 ${summaryTextClass}`}>马星</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center font-bold text-[#9c5b31] bg-[#fefbec] border-b border-slate-200 ${summaryTextClass}`}>空亡</div>
                  </div>
                  <div className="grid grid-cols-[78px_1fr_1fr_1fr_1fr] sm:grid-cols-[88px_1fr_1fr_1fr_1fr] border-b border-slate-200">
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center leading-tight text-slate-900 border-r border-slate-200 ${summaryTextClass}`}>{pan.xunShou}</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center leading-tight text-slate-900 border-r border-slate-200 ${summaryTextClass}`}>{pan.zhiFu}</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center leading-tight text-slate-900 border-r border-slate-200 ${summaryTextClass}`}>{pan.zhiShi}</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center leading-tight text-slate-900 border-r border-slate-200 ${summaryTextClass}`}>{pan.maText.replace(/（.*?）/g, '').trim()}</div>
                    <div className={`px-1 sm:px-3 py-1 sm:py-2 text-center leading-tight text-slate-900 ${summaryTextClass}`}>{pan.kongText}</div>
                  </div>

                  <div className="grid grid-cols-[78px_1fr_1fr_1fr_1fr] sm:grid-cols-[88px_1fr_1fr_1fr_1fr]">
                    <div className={`px-1 py-1 sm:py-2 font-bold text-[#9c5b31] bg-[#fefbec] border-r border-slate-200 flex items-center justify-center ${summaryTextClass}`} style={{ gridRow: '1 / 4' }}>
                      <span className="sm:hidden [writing-mode:vertical-rl] [text-orientation:upright] tracking-[0.2em]">四柱</span>
                      <span className="hidden sm:inline">四柱</span>
                    </div>
                    {pillarItems.map((item, index) => (
                      <div
                        key={`${item.label}-label`}
                        className={`px-1 sm:px-2 py-1 sm:py-2 text-center bg-[#fefbec] flex items-center justify-center ${
                          index < pillarItems.length - 1 ? 'border-r border-slate-200' : ''
                        }`}
                      >
                        <p className={`font-bold text-[#9c5b31] ${summaryTextClass}`}>{item.label}</p>
                      </div>
                    ))}
                    <div className="col-start-2 col-span-4 border-b border-slate-200" />
                    {pillarItems.map((item, index) => (
                      <div
                        key={`${item.label}-val`}
                        className={`px-1 sm:px-2 py-1 sm:py-2 text-center ${index < pillarItems.length - 1 ? 'border-r border-slate-200' : ''}`}
                      >
                        <p className={`font-bold leading-tight text-slate-900 ${summaryTextClass}`}>
                          {isMobileViewport
                            ? item.value.split('').map((char, cIdx) => (
                              <span key={`${item.label}-${cIdx}`} className="block leading-[1.15]">{char}</span>
                            ))
                            : item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
              )}
              {!showMobileXiangYiPage && (
                <div className={`grid grid-cols-1 ${useDesktopSplitLayout ? 'lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.28fr)]' : 'lg:grid-cols-1'} gap-4 items-start`}>
                <section className={`${useDesktopSplitLayout ? 'order-2 lg:max-h-[calc(100vh-120px)] lg:overflow-y-auto lg:overscroll-contain lg:pr-1' : 'order-2'} rounded-2xl p-0 bg-transparent border-0 shadow-none`}>
                  {isMobileViewport && pan ? null : (
                    <>
                      {!isMobileViewport && (!showHistoryParseCta || showAiPanel) && renderAiPanelSection('desktop')}
                      {!hasAiReport && renderDesktopActionButtons({ forceQuestionModal: showHistoryParseCta && !hasAiReport && !showAiPanel })}
                    </>
                  )}
                </section>

                <section className={`order-1 bg-white rounded-2xl border border-slate-200 shadow-sm px-3 pb-4 pt-2.5 sm:px-4 sm:pb-5 sm:pt-3 lg:px-5 lg:pb-6 lg:pt-3.5 ${useDesktopSplitLayout ? 'w-full lg:max-w-[700px]' : 'mx-auto w-full max-w-[1180px]'} ${!useDesktopSplitLayout && !isMobileViewport && !hasAiReport && (hasAiReport || activePalaceCell) ? 'lg:sticky lg:top-4 lg:self-start' : ''}`}>
                    <div className={`flex flex-col ${activePalaceCell && !isMobileViewport && !hasAiReport ? 'lg:flex-row lg:items-start lg:gap-4' : ''}`}>
                      <div className={`${activePalaceCell && !isMobileViewport && !hasAiReport ? 'lg:flex-1' : ''}`}>
                        <div className="relative mx-auto mt-2 w-full max-w-[330px] sm:mt-3 sm:max-w-[420px] lg:max-w-[390px] min-h-[360px] sm:min-h-[450px] lg:min-h-[430px] px-7 sm:px-4 pt-8 pb-6 sm:pt-10 sm:pb-8 lg:pt-9 lg:pb-7">
                          <div className="relative">
                            <div className="grid grid-cols-3 border border-black w-full">
                        {GRID_POSITIONS.map((position) => {
                          const cell = pan.palaces.get(position) || emptyPalace(position);
                          const isCenter = position === 5;
                          const gateClass = getGateClass(cell);
                          const forceTombStem = getForcedTombStemForZhiFuKun(cell);

                          return (
                            <div
                              key={position}
                              onClick={() => {
                                if (isCenter) return;
                                setSelectedPalacePos(position);
                                setExpandedXiangYiKeys([]);
                              }}
                              className={`relative aspect-square border border-black ${
                                isCenter
                                  ? 'bg-white'
                                  : `cursor-pointer ${
                                    selectedPalacePos === position
                                      ? 'bg-[#fffaf0]'
                                      : 'bg-white hover:bg-[#fff8e8]'
                                  }`
                              }`}
                            >
                              {!isCenter && (
                                <>
                                  {cell.hasVoid && (
                                    <span className={`absolute right-1 sm:right-2 top-0.5 sm:top-1 font-normal text-slate-500 ${palaceVoidClass}`}>○</span>
                                  )}

                                  <span className={`absolute left-1.5 sm:left-2 top-1.5 sm:top-2 font-normal text-slate-900 ${palaceSymbolClass}`}>{cell.deitySymbol}</span>

                                  <div className="absolute left-1.5 sm:left-2 right-1.5 sm:right-2 top-1/2 -translate-y-[52%] sm:-translate-y-[56%] flex items-center justify-between pointer-events-none">
                                    <span className={`font-normal leading-none ${palaceSymbolClass}`}>
                                      {renderStemText(cell.position, cell.heavenlyStem, { forceTombStem })}
                                    </span>
                                    <span className={`font-normal leading-none text-slate-900 ${palaceSymbolClass}`}>
                                      {cell.starSymbol}
                                    </span>
                                  </div>

                                  <span className={`absolute left-1.5 sm:left-2 bottom-1.5 sm:bottom-2 font-normal ${palaceSymbolClass}`}>
                                    {renderStemText(cell.position, cell.earthlyStem, { forceXing: cell.earthlyHasXingByFuYin, forceTombStem })}
                                  </span>

                                  <span className={`absolute right-1.5 sm:right-2 bottom-1.5 sm:bottom-2 font-normal ${palaceSymbolClass} ${gateClass}`}>
                                    {cell.gateSymbol}
                                  </span>
                                </>
                              )}
                            </div>
                          );
                        })}
                            </div>

                            {OUTER_POSITIONS.map((item) => {
                              const leadStem = pan.palaces.get(item.position)?.leadStem || '';
                              if (!leadStem) return null;
                              const innerAlignClass = item.side === 'left'
                                ? 'inline-block min-w-[1.8em] text-right'
                                : item.side === 'right'
                                  ? 'inline-block min-w-[1.8em] text-left'
                                  : 'inline-block min-w-[1.8em] text-center';

                              return (
                                <div
                                  key={item.position}
                                  style={item.style}
                                  className={`absolute ${outerLeadClass} font-normal text-slate-900 pointer-events-none`}
                                >
                                  <span className={innerAlignClass}>{leadStem}</span>
                                </div>
                              );
                            })}

                            {(() => {
                              const horsePos = Number(pan.horsePosition || 0);
                              const horseCornerClass = HORSE_CORNER_POSITIONS[horsePos];
                              if (!horseCornerClass) return null;
                              return (
                                <div
                                  style={horseCornerClass}
                                  className={`absolute ${outerLeadClass} ${palaceSymbolClass} font-normal text-red-600 pointer-events-none`}
                                >
                                  <span className="inline-block min-w-[1.8em] text-center">马</span>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        <p className="mt-4 text-sm text-slate-600">
                          颜色说明：
                          <span className="text-amber-700 ml-1">入墓</span>
                          <span>、</span>
                          <span className="text-blue-600">击刑</span>
                          <span>、</span>
                          <span className="text-red-600">门迫</span>
                          <span>、</span>
                          <span className="text-sky-600">刑+墓</span>
                          <span>；○ 为空亡。</span>
                        </p>
                      </div>

                      {activePalaceCell && !isMobileViewport && (
                        <aside className={`${hasAiReport ? 'w-full mt-4 border-t border-slate-200 pt-4' : 'hidden lg:block w-[360px]'} py-1 ${hasAiReport ? '' : 'lg:max-h-[calc(100vh-176px)] lg:overflow-y-auto lg:overscroll-contain lg:pr-1'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-base font-semibold text-slate-900">{activePalaceCell.trigram}{activePalaceCell.position}宫象意</h3>
                            {xiangYiEntries.length > 0 && (
                              <button
                                onClick={() => {
                                  const allExpanded = expandedXiangYiKeys.length === xiangYiEntries.length;
                                  setExpandedXiangYiKeys(allExpanded ? [] : xiangYiEntries.map((e) => e.key));
                                }}
                                title={expandedXiangYiKeys.length === xiangYiEntries.length ? '全部折叠' : '全部展开'}
                                aria-label={expandedXiangYiKeys.length === xiangYiEntries.length ? '全部折叠' : '全部展开'}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 shadow-sm transition-colors hover:bg-amber-100 hover:text-amber-800"
                              >
                                {expandedXiangYiKeys.length === xiangYiEntries.length ? <ChevronsUp className="h-4 w-4" /> : <ChevronsDown className="h-4 w-4" />}
                              </button>
                            )}
                          </div>
                          <div className="mt-2 space-y-2">
                            {xiangYiEntries.map((entry) => {
                              const expanded = expandedXiangYiKeys.includes(entry.key);
                              return (
                                <div key={`desktop-side-${entry.key}`}>
                                  <button
                                    onClick={() => setExpandedXiangYiKeys((prev) => (
                                      prev.includes(entry.key)
                                        ? prev.filter((key) => key !== entry.key)
                                        : [...prev, entry.key]
                                    ))}
                                    className={`w-full px-2.5 py-2.5 text-left flex items-center justify-between rounded-lg border transition-all active:scale-[0.995] ${
                                      expanded
                                        ? 'bg-amber-50 border-amber-200 shadow-sm'
                                        : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                                    }`}
                                  >
                                    <span className={`text-sm sm:text-base font-medium ${expanded ? 'text-amber-900' : 'text-slate-800'}`}>{buildEntryLabel(entry)}</span>
                                    <span className={`text-sm ${expanded ? 'text-amber-700' : 'text-slate-400'}`}>{expanded ? '－' : '＋'}</span>
                                  </button>
                                  {expanded && (
                                    <div className="px-1 pb-1.5">
                                      {renderGroupedOriginDetails(entry.details, `desktop-${entry.key}`)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-3 flex w-full items-center gap-1.5 flex-nowrap overflow-x-auto pb-1">
                            <button
                              onClick={() => openPracticeTool('findObject', activePalaceCell)}
                              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                            >
                              <ScanEye className="w-3.5 h-3.5" />
                              查找象意
                            </button>
                            <button
                              onClick={() => openPracticeTool('decoder', activePalaceCell)}
                              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                            >
                              <Box className="w-3.5 h-3.5" />
                              象意解读
                            </button>
                            {canShowFourXiangyi && (
                              <button
                                onClick={() => { void runFourHarmRelief(activePalaceCell); }}
                                disabled={fourHarmLoading}
                                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 whitespace-nowrap"
                              >
                                <Shield className="w-3.5 h-3.5" />
                                四害化解
                              </button>
                            )}
                          </div>
                          {renderFourHarmPanel()}
                        </aside>
                      )}
                    </div>
                    {!isMobileViewport && hasAiReport && (
                      <div className="mt-4 border-t border-slate-200 pt-4">
                        {renderDesktopActionButtons({ containerClassName: 'flex justify-center' })}
                      </div>
                    )}
                    {!isMobileViewport && (
                      <div className="mt-4 border-t border-slate-200 pt-4">
                        {renderPanExtensionButtons()}
                      </div>
                    )}
	                </section>
	                </div>
	              )}

              {!showMobileXiangYiPage && isMobileViewport && (
                <div className="mt-1">
                  {renderPanExtensionButtons()}
                </div>
              )}

              {shouldShowMobileAiPanel && renderAiPanelSection('mobile')}

	              {/* 移动端底部 Dock：客服、分享、解析、笔记、保存 */}
	              {isMobileViewport && pan && !showMobileXiangYiPage && (
                <div className="mobile-tabbar fixed bottom-0 left-0 right-0 z-30 pb-safe">
                  <div
                    className="mobile-tabbar-container"
                    style={{ width: 'min(92vw, 430px)', maxWidth: '430px', borderRadius: '28px' }}
                  >
                    <div className="mobile-tabbar-inner" style={{ minHeight: 62, gap: '0.38rem' }}>
                      <button
                        type="button"
onClick={() => setNotice('激活成功')}
                        className={`${panMobileDockSoftButtonClass} text-slate-600`}
                      >
                        <MessageCircle className="h-5 w-5 shrink-0" />
                        <span className="text-[10px]">客服</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { void copyFullPanText(); }}
                        disabled={!pan}
                        className={`${panMobileDockSoftButtonClass} disabled:opacity-50`}
                      >
                        <Share2 className="h-5 w-5 shrink-0" />
                        <span className="text-[10px]">分享</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { openAiPanel({ openQuestionModal: !hasAiReport }); }}
                        disabled={aiLoading}
                        className={`${panMobileDockAiButtonClass} disabled:opacity-60`}
                      >
                        <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e4cfac] bg-[#b78443] text-[#fff9ef] shadow-[0_4px_12px_rgba(119,74,27,0.14)]">
                          <Sparkles className="h-5 w-5 shrink-0" />
                        </span>
                        <span className="text-[10px] font-bold tracking-[0.01em]">{aiLoading ? '解析中' : 'AI解析'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={openNotesPanel}
                        className={panMobileDockSoftButtonClass}
                      >
                        <BookOpen className="h-5 w-5 shrink-0" />
                        <span className="text-[10px]">笔记</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveCurrentPan}
                        disabled={saveButtonDisabled}
                        className={`${panMobileDockSoftButtonClass} disabled:opacity-50`}
                      >
                        <Save className="h-5 w-5 shrink-0" />
                        <span className="text-[10px]">{saveButtonLabel}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 笔记半屏浮层（移动/平板毛玻璃；桌面端同款浮层） */}
              {showNotesPanel && (
                <div className="no-edge-back fixed inset-0 z-40 flex flex-col" aria-modal="true">
                  <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNotesPanel(false)} aria-hidden="true" />
                  <div className="relative mt-auto h-[56vh] max-h-[520px] flex flex-col rounded-t-2xl bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-xl">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">笔记内容</h3>
                        <p className="mt-0.5 text-xs text-slate-500">当前共 {currentRecordNotes.length} 条</p>
                      </div>
                      <button type="button" onClick={() => setShowNotesPanel(false)} className="p-2 -m-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {currentRecordId ? (
                        <>
                          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                            {currentRecordNotes.length > 0 ? (
                              <div className="space-y-3">
                                {currentRecordNotes.map((note) => {
                                  const changed = Number(note.updatedAt || 0) > Number(note.createdAt || 0) + 1000;
                                  return (
                                    <article key={note.id} className="border-b border-slate-100 py-2.5 last:border-b-0">
                                      <div className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-900">{note.content}</div>
                                      <div className="mt-3 flex items-center justify-between gap-3">
                                        <p className="text-xs text-slate-400">
                                          {formatPanNoteTimestamp(note.updatedAt || note.createdAt)}
                                          {changed ? ' · 已修改' : ''}
                                        </p>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteNoteItem(note.id)}
                                            className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 hover:bg-slate-200"
                                          >
                                            删除
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleEditNoteItem(note)}
                                            className="rounded-full bg-[#f6efe3] px-3 py-1 text-xs text-[#8b6b3d] hover:bg-[#efe3d0]"
                                          >
                                            修改
                                          </button>
                                        </div>
                                      </div>
                                    </article>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 text-sm text-slate-500">
                                还没有笔记，先在下面写一条。
                              </div>
                            )}
                          </div>
                          <div className="border-t border-slate-200 px-4 py-3">
                            <div className="flex items-center gap-2">
                              {editingNoteId && (
                                <button
                                  type="button"
                                  onClick={cancelNoteEditing}
                                  className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap"
                                >
                                  取消
                                </button>
                              )}
                              <textarea
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="输入笔记…"
                                className="min-h-[40px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8b6b3d] resize-none"
                              />
                              <button
                                type="button"
                                onClick={handleSaveNoteItem}
                                disabled={!noteDraft.trim()}
                                className="rounded-lg bg-[#8b6b3d] px-4 py-2 text-sm text-white hover:bg-[#785a31] disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                              >
                                {editingNoteId ? '保存' : '新增'}
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-1 items-center justify-center px-4 text-sm text-slate-500">请先填写事项后再记笔记。</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activePalaceCell && showMobileXiangYiPage && (
                <section className="lg:hidden py-1">
                  <div className="flex items-center justify-between gap-2 px-1">
                    <h3 className="text-base font-semibold text-slate-900">{activePalaceCell.trigram}{activePalaceCell.position}宫象意</h3>
                    {xiangYiEntries.length > 0 && (
                      <button
                        onClick={() => {
                          const allExpanded = expandedXiangYiKeys.length === xiangYiEntries.length;
                          setExpandedXiangYiKeys(allExpanded ? [] : xiangYiEntries.map((e) => e.key));
                        }}
                        title={expandedXiangYiKeys.length === xiangYiEntries.length ? '全部折叠' : '全部展开'}
                        aria-label={expandedXiangYiKeys.length === xiangYiEntries.length ? '全部折叠' : '全部展开'}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 shadow-sm transition-colors hover:bg-amber-100 hover:text-amber-800"
                      >
                        {expandedXiangYiKeys.length === xiangYiEntries.length ? <ChevronsUp className="h-4 w-4" /> : <ChevronsDown className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {xiangYiEntries.map((entry) => {
                      const expanded = expandedXiangYiKeys.includes(entry.key);
                      return (
                        <div key={`mobile-${entry.key}`} className="overflow-hidden">
                          <button
                            onClick={() => setExpandedXiangYiKeys((prev) => (
                              prev.includes(entry.key)
                                ? prev.filter((key) => key !== entry.key)
                                : [...prev, entry.key]
                            ))}
                            className={`w-full text-left px-2.5 py-2.5 text-sm flex items-center justify-between rounded-lg border transition-all active:scale-[0.995] ${
                              expanded
                                ? 'bg-amber-50 text-amber-900 border-amber-200 shadow-sm'
                                : 'bg-white text-slate-700 border-slate-200'
                            }`}
                          >
                            <span className="font-medium">{buildEntryLabel(entry)}</span>
                            <span className={`text-sm ${expanded ? 'text-amber-700' : 'text-slate-400'}`}>{expanded ? '－' : '＋'}</span>
                          </button>
                          {expanded && (
                            <div className="px-1 pb-1.5">
                              {renderGroupedOriginDetails(entry.details, `${entry.key}-mobile`)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex w-full items-center gap-1.5 flex-nowrap overflow-x-auto pb-1">
                    <button
                      onClick={() => openPracticeTool('findObject', activePalaceCell)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                    >
                      <ScanEye className="w-3.5 h-3.5" />
                      查找象意
                    </button>
                    <button
                      onClick={() => openPracticeTool('decoder', activePalaceCell)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                    >
                      <Box className="w-3.5 h-3.5" />
                      象意解读
                    </button>
                    {canShowFourXiangyi && (
                      <button
                        onClick={() => { void runFourHarmRelief(activePalaceCell); }}
                        disabled={fourHarmLoading}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 whitespace-nowrap"
                      >
                        <Shield className="w-3.5 h-3.5" />
                        四害化解
                      </button>
                    )}
                  </div>
                  {renderFourHarmPanel()}
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {!(isMobileViewport && pan && !showMobileXiangYiPage) && (
        <div className={`mobile-tabbar lg:hidden fixed bottom-0 left-0 right-0 z-40 ${dockStyle === 'ios' ? 'pb-safe' : 'mobile-tabbar-default'}`}>
          {dockStyle === 'ios' ? (
            <div className="mobile-tabbar-container">
              <div className="mobile-tabbar-inner">
                <button
                  onClick={() => handleBottomTabSwitch('pan')}
                  className="mobile-tabbar-btn text-amber-700"
                >
                  <Compass className="mobile-tabbar-icon" />
                  <span className="mobile-tabbar-label">排盘</span>
                </button>
                <button
                  onClick={() => handleBottomTabSwitch('practice')}
                  className="mobile-tabbar-btn text-slate-400"
                >
                  <LayoutGrid className="mobile-tabbar-icon" />
                  <span className="mobile-tabbar-label">应用</span>
                </button>
                <button
                  onClick={() => handleBottomTabSwitch('study')}
                  className="mobile-tabbar-btn text-slate-400"
                >
                  <BookOpen className="mobile-tabbar-icon" />
                  <span className="mobile-tabbar-label">学习</span>
                </button>
                <button
                  onClick={() => handleBottomTabSwitch('mine')}
                  className="mobile-tabbar-btn text-slate-400"
                >
                  <User className="mobile-tabbar-icon" />
                  <span className="mobile-tabbar-label">我的</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="mobile-tabbar-inner">
              <button
                onClick={() => handleBottomTabSwitch('pan')}
                className="mobile-tabbar-btn text-amber-700"
              >
                <Compass className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">排盘</span>
              </button>
              <button
                onClick={() => handleBottomTabSwitch('practice')}
                className="mobile-tabbar-btn text-slate-400"
              >
                <LayoutGrid className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">应用</span>
              </button>
              <button
                onClick={() => handleBottomTabSwitch('study')}
                className="mobile-tabbar-btn text-slate-400"
              >
                <BookOpen className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">学习</span>
              </button>
              <button
                onClick={() => handleBottomTabSwitch('mine')}
                className="mobile-tabbar-btn text-slate-400"
              >
                <User className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">我的</span>
              </button>
            </div>
          )}
        </div>
      )}

      {showModeOnboarding && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-xl p-5 sm:p-6">
            <h3 className="text-xl font-semibold text-slate-900">选择启动模式</h3>
            <div className="mt-3 text-sm text-slate-700 leading-7 space-y-2">
              <p className="font-semibold text-slate-900">专业模式</p>
              <p>1. 适合专业学习奇门遁甲的人。</p>
              <p>2. 可以进行更详细的专业参数调节。</p>
              <p className="font-semibold text-slate-900 pt-1">普通模式</p>
              <p>1. 适合不懂奇门的普通用户。</p>
              <p>2. 如果你只是想用 AI 帮你测算，就选择普通模式。</p>
              <p className="pt-1 text-slate-500">
                说明：你可以通过右上角随时切换模式，也可以在“我的”页面设置默认模式。
              </p>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => selectModeOnboarding('simple')}
                className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 px-4 py-3 font-medium hover:bg-amber-100"
              >
                进入普通模式
              </button>
              <button
                onClick={() => selectModeOnboarding('pro')}
                className="rounded-xl border border-slate-300 bg-white text-slate-700 px-4 py-3 font-medium hover:bg-slate-50"
              >
                进入专业模式
              </button>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="no-edge-back fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-xl p-5 relative max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => setShowHistoryModal(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">排盘记录（自动保存）</h3>
            <p className="text-xs text-slate-500 mb-3">按时间倒序排列；已保存事项、AI 解析、追问和笔记，可随时回看。</p>
            <div className="space-y-2">
              {records.map((item) => (
                <div key={item.id} className="rounded-xl border border-slate-200 p-3">
                  {(() => {
                    const noteCount = getPanRecordNoteCount(item);
                    return (
                      <>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm text-slate-900 font-medium line-clamp-1">{item.question}</p>
                    {(item.aiReport || (Array.isArray(item.aiFollowupHistory) && item.aiFollowupHistory.length > 0)) && (
                      <span className="inline-flex items-center rounded-md bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[11px] font-semibold">AI</span>
                    )}
                    {Array.isArray(item.aiFollowupHistory) && item.aiFollowupHistory.length > 0 && (
                      <span className="inline-flex items-center rounded-md bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[11px] font-semibold">追问 {item.aiFollowupHistory.length}</span>
                    )}
                    <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-semibold ${noteCount > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                      笔记 {noteCount}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {item.sourceDateTime.replace('T', ' ')} · {item.panMode === 'ke' ? '刻盘' : '时盘'} · {item.consultMode === 'remote' ? '远程测' : '当面测'} · {item.pan.juText} · {noteCount} 条笔记 · 更新于 {new Date(item.updatedAt || item.createdAt).toLocaleString('zh-CN')}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => applyRecord(item)}
                      className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm"
                    >
                      打开
                    </button>
                    <button
                      onClick={() => deleteRecord(item.id)}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-sm"
                    >
                      删除
                    </button>
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAiQuestionModal && (
        <div className="no-edge-back fixed inset-0 z-[84] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-800">填写事项</h3>
              <button
                type="button"
                onClick={() => setShowAiQuestionModal(false)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100"
                aria-label="关闭填写事项弹窗"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-4 py-3 sm:px-5 sm:py-4">
              <textarea
                ref={aiExtraQuestionRef}
                value={aiExtraQuestion}
                onChange={(e) => setAiExtraQuestion(e.target.value)}
                required={aiModalQuestionRequired}
                placeholder={aiModalQuestionRequired
                  ? '事项未填写，请在此输入你的问题（必填）'
                  : '请补充你要重点解读的事项（可选）'}
                className="w-full min-h-[120px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-400"
              />
              <p className="mt-2 text-xs text-slate-500">
                {aiModalQuestionRequired
                  ? '当前规则：事项未填写时，本框必填。'
                  : '事项已填写，可直接解析，也可补充更具体的问题。'}
                {' '}详细说一说，会给您更精准的分析。
              </p>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAiQuestionModal(false)}
                  className="rounded-lg border border-[#cdb78f] px-4 py-2 text-sm text-[#7d6641]"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAiQuestionModal(false);
                    void runAiInterpretation();
                  }}
                  disabled={!aiCanStartInterpretation}
                  className="rounded-lg bg-[#8b6b3d] px-4 py-2 text-sm text-white hover:bg-[#785a31] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {aiLoading ? '解析中...' : '开始解析'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 z-50 bg-slate-950/45 backdrop-blur-[3px] flex items-center justify-center p-4">
          <div className="relative w-full max-w-[340px] overflow-hidden rounded-2xl border border-[#dcc49a] bg-gradient-to-br from-[#fffaf0] via-[#f8efdc] to-[#f0dfbe] px-5 py-[18px] shadow-[0_30px_80px_-45px_rgba(41,27,9,0.7)]">
            <div className="absolute -top-10 -right-9 h-28 w-28 rounded-full bg-[#f5d38c]/45 blur-2xl" />
            <div className="absolute -bottom-10 -left-9 h-28 w-28 rounded-full bg-[#ecd1a0]/45 blur-2xl" />

            <div className="relative flex items-center gap-3">
              <div className="relative h-14 w-14 shrink-0">
                <span className="absolute inset-0 rounded-full border-[2.5px] border-[#8b6b3d]/20" />
                <span className="absolute inset-[4px] rounded-full border-[2.5px] border-[#8b6b3d]/35 animate-spin [animation-duration:1.7s]" />
                <span className="absolute inset-[9px] rounded-full border-[2.5px] border-transparent border-t-[#8b6b3d] border-r-[#b2884a] animate-spin [animation-duration:1.05s]" />
                <span className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#8b6b3d] shadow-[0_0_12px_rgba(139,107,61,0.55)] animate-pulse" />
              </div>
              <div>
                <p className="text-[17px] font-bold leading-none text-[#4e3518] [font-family:'STSong','Songti_SC','SimSun',serif]">
                  正在排盘
                </p>
                <p className="mt-2 text-xs text-[#6b5432]">{PROGRESS_STEPS[progressIndex]}</p>
              </div>
            </div>

            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[#ead9b8]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#8b6b3d] via-[#bb944f] to-[#8b6b3d] transition-all duration-300"
                style={{ width: `${((progressIndex + 1) / PROGRESS_STEPS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {aiLoading && aiThinkingOverlayVisible && (
        <div className="fixed inset-0 z-[70] bg-slate-950/45 backdrop-blur-[3px] flex items-center justify-center p-4">
          <div className="relative w-full max-w-[340px] overflow-hidden rounded-2xl border border-[#dcc49a] bg-gradient-to-br from-[#fffaf0] via-[#f8efdc] to-[#f0dfbe] px-5 py-[18px] shadow-[0_30px_80px_-45px_rgba(41,27,9,0.7)]">
            <div className="absolute -top-10 -right-9 h-28 w-28 rounded-full bg-[#f5d38c]/45 blur-2xl" />
            <div className="absolute -bottom-10 -left-9 h-28 w-28 rounded-full bg-[#ecd1a0]/45 blur-2xl" />

            <div className="relative flex items-center gap-3">
              <div className="relative h-14 w-14 shrink-0">
                <span className="absolute inset-0 rounded-full border-[2.5px] border-[#8b6b3d]/20" />
                <span className="absolute inset-[4px] rounded-full border-[2.5px] border-[#8b6b3d]/35 animate-spin [animation-duration:1.7s]" />
                <span className="absolute inset-[9px] rounded-full border-[2.5px] border-transparent border-t-[#8b6b3d] border-r-[#b2884a] animate-spin [animation-duration:1.05s]" />
                <span className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#8b6b3d] shadow-[0_0_12px_rgba(139,107,61,0.55)] animate-pulse" />
              </div>
              <div>
                <p className="text-[17px] font-bold leading-none text-[#4e3518] [font-family:'STSong','Songti_SC','SimSun',serif]">
                  {aiLoadingTitle}
                </p>
                <p className="mt-2 text-[11px] text-[#806b46]">{aiLoadingEstimateHint}</p>
                <p className="mt-1 text-[11px] text-[#806b46]">{aiElapsedHint}</p>
              </div>
            </div>

            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[#ead9b8]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#8b6b3d] via-[#bb944f] to-[#8b6b3d] transition-all duration-500 ease-out"
                style={{ width: `${Math.max(8, aiProgressPercent)}%` }}
              />
            </div>
            <div className="mt-3 rounded-lg border border-[#d9c29c] bg-[#f7ecd9] px-3 py-2">
              <p className="text-[11px] text-[#7a623e]">当前任务</p>
              <div className="mt-1 flex items-center gap-2 text-[12px] text-[#5a3e1d]">
                <span className="h-2 w-2 rounded-full border border-[#8b6b3d] bg-[#d4b079] animate-pulse" />
                <span>{activeAiProgressSteps[aiProgressIndex] || activeAiProgressSteps[activeAiProgressSteps.length - 1]}</span>
                <span className="ml-auto text-[11px] text-[#8a7148]">{Math.round(aiProgressPercent)}%</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {aiErrorModal.open && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-4 shadow-2xl">
            <h3 className="text-base font-bold text-rose-700">网络错误</h3>
            <p className="mt-2 text-sm leading-6 text-slate-700">{aiErrorModal.message || '网络连接异常，请稍后重试。'}</p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setAiErrorModal({ open: false, message: '' })}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
      <TeacherContactModal
        open={showMasterConsultModal}
        onClose={() => setShowMasterConsultModal(false)}
        title="大师解盘（开发中）"
        description="激活成功。"
        wechatId={TEACHER_WECHAT_ID}
        mode="notice"
      />
      <TeacherContactModal
        open={showSolutionModal}
        onClose={() => setShowSolutionModal(false)}
        title="解决方案"
        description="激活成功。"
        wechatId={TEACHER_WECHAT_ID}
        mode="notice"
      />
    </div>
  );
};

export default PanTestPage;
